import { useState, useEffect, useCallback, useRef, useMemo, Component } from "react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, ReferenceLine,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell
} from "recharts";

const API = process.env.REACT_APP_API_URL || "";

// ── Authenticated fetch ────────────────────────────────────────────────────
// Every /api call carries a Bearer token: normally the long-lived (30-day) session
// JWT the server mints after Google sign-in, briefly the Google ID token during the
// initial handshake. On a 401 (session expired/revoked) we clear it and bounce to
// the login screen for a fresh sign-in.
const _origFetch = window.fetch.bind(window);
let authToken = (() => { try { return localStorage.getItem("tapgp:dash:token"); } catch { return null; } })();
function setAuthToken(t) {
  authToken = t || null;
  try { t ? localStorage.setItem("tapgp:dash:token", t) : localStorage.removeItem("tapgp:dash:token"); } catch {}
}
function authFetch(url, opts = {}) {
  const headers = { ...(opts.headers || {}) };
  if(authToken) headers["Authorization"] = "Bearer " + authToken;
  return _origFetch(url, { ...opts, headers }).then(res => {
    if(res.status === 401 && !window.__authReloading) {
      window.__authReloading = true;
      setAuthToken(null);
      try { localStorage.removeItem("tapgp:dash:user"); } catch {}
      window.location.reload();
    }
    return res;
  });
}

const fmt = {
  gbp: (v) => `£${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
  pct: (v) => `${Number(v || 0).toFixed(1)}%`,
  num: (v) => Number(v || 0).toLocaleString("en-GB"),
  x: (v) => `${Number(v || 0).toFixed(1)}x`,
};

const COLORS = {
  blue: "#378ADD", green: "#1D9E75", orange: "#EF9F27", purple: "#534AB7",
  red: "#E24B4A", teal: "#0EA5A0", pink: "#D946A8",
  bg: "#F8F7F4", card: "#fff", metric: "#EFEFEB",
  border: "rgba(0,0,0,0.08)", text: "#1a1a1a", muted: "#888780",
};

const planColors = [COLORS.blue, COLORS.green, COLORS.purple, COLORS.teal, COLORS.orange, COLORS.pink];

// Debounce a fast-changing value (e.g. a filter input) so expensive
// filter/sort work over thousands of rows runs at most once per `delay`ms.
function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// Pure sort helper — hoisted to module scope so it's a stable reference and
// can be used inside useMemo without tripping exhaustive-deps.
function sortData(data, col, dir) {
  return [...data].sort((a, b) => {
    let av = a[col], bv = b[col];
    if(typeof av === 'string') av = av.toLowerCase();
    if(typeof bv === 'string') bv = bv.toLowerCase();
    if(av === null || av === undefined) av = dir === 'asc' ? Infinity : -Infinity;
    if(bv === null || bv === undefined) bv = dir === 'asc' ? Infinity : -Infinity;
    return dir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });
}

// Client-side CSV export — quotes fields and escapes embedded quotes.
function downloadCSV(filename, columns, rows) {
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const header = columns.map(c => esc(c.label)).join(',');
  const body = rows.map(r => columns.map(c => esc(typeof c.get === 'function' ? c.get(r) : r[c.key])).join(',')).join('\n');
  const csv = header + '\n' + body;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Small components ──────────────────────────────────────────────────────────

// Lightweight Markdown renderer for assistant chat messages — headers, bold, inline code, bullet &
// numbered lists, --- rules, and (the reason this exists) pipe TABLES as real bordered HTML tables.
// No new dependency; mirrors the Action Plan report's inline-bold / header / bullet patterns and adds
// tables. Display-only — it never changes the message text, just how it's shown.
function mdInline(text) {
  return String(text).split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((p, j) => {
    if (p.startsWith('**') && p.endsWith('**')) return <strong key={j} style={{ fontWeight: 700 }}>{p.slice(2, -2)}</strong>;
    if (p.startsWith('`') && p.endsWith('`')) return <code key={j} style={{ background: 'rgba(0,0,0,0.06)', borderRadius: 4, padding: '1px 5px', fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{p.slice(1, -1)}</code>;
    return <span key={j}>{p}</span>;
  });
}
function MarkdownMessage({ text }) {
  const lines = String(text || '').split('\n');
  const isRow = l => /^\s*\|.*\|\s*$/.test(l);
  const isSep = l => /^\s*\|?[\s:|-]+\|?\s*$/.test(l) && l.includes('-') && l.includes('|');
  const cells = l => l.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim());
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // TABLE — a pipe row immediately followed by a |---|---| separator row
    if (isRow(line) && i + 1 < lines.length && isSep(lines[i + 1])) {
      const header = cells(line); i += 2;
      const body = [];
      while (i < lines.length && isRow(lines[i])) { body.push(cells(lines[i])); i++; }
      out.push(
        <div key={`t${i}`} style={{ overflowX: 'auto', margin: '8px 0' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12.5, width: '100%' }}>
            <thead><tr>{header.map((h, c) => <th key={c} style={{ border: '1px solid #D8DBE0', padding: '5px 9px', background: '#E4E5EC', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{mdInline(h)}</th>)}</tr></thead>
            <tbody>{body.map((r, ri) => <tr key={ri}>{header.map((_, c) => <td key={c} style={{ border: '1px solid #E5E7EB', padding: '5px 9px', verticalAlign: 'top', background: ri % 2 ? '#FAFAFB' : '#fff' }}>{mdInline(r[c] ?? '')}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      continue;
    }
    const key = `l${i}`;
    if (line.trim() === '') out.push(<div key={key} style={{ height: 6 }} />);
    else if (line.trim() === '---') out.push(<hr key={key} style={{ border: 'none', borderTop: '1px solid #E5E7EB', margin: '8px 0' }} />);
    else if (line.startsWith('### ')) out.push(<div key={key} style={{ fontWeight: 700, fontSize: 13, marginTop: 10, marginBottom: 3 }}>{mdInline(line.slice(4))}</div>);
    else if (line.startsWith('## ')) out.push(<div key={key} style={{ fontWeight: 700, fontSize: 14, marginTop: 12, marginBottom: 4 }}>{mdInline(line.slice(3))}</div>);
    else if (line.startsWith('# ')) out.push(<div key={key} style={{ fontWeight: 700, fontSize: 15, marginTop: 12, marginBottom: 4 }}>{mdInline(line.slice(2))}</div>);
    else if (/^\s*[-*•]\s+/.test(line)) out.push(<div key={key} style={{ display: 'flex', gap: 8, margin: '2px 0' }}><span style={{ color: COLORS.purple, flexShrink: 0 }}>•</span><div style={{ flex: 1 }}>{mdInline(line.replace(/^\s*[-*•]\s+/, ''))}</div></div>);
    else if (/^\s*\d+\.\s+/.test(line)) out.push(<div key={key} style={{ display: 'flex', gap: 8, margin: '2px 0' }}><span style={{ color: COLORS.purple, fontWeight: 700, flexShrink: 0 }}>{line.match(/^\s*(\d+)\./)[1]}.</span><div style={{ flex: 1 }}>{mdInline(line.replace(/^\s*\d+\.\s+/, ''))}</div></div>);
    else out.push(<p key={key} style={{ margin: '3px 0' }}>{mdInline(line)}</p>);
    i++;
  }
  return <div style={{ lineHeight: 1.55 }}>{out}</div>;
}

function ChangeIndicator({ change, inverse = false }) {
  if(change === null || change === undefined) return null;
  const num = parseFloat(change);
  const isGood = inverse ? num < 0 : num > 0;
  const color = isGood ? COLORS.green : num === 0 ? COLORS.muted : COLORS.red;
  const arrow = num > 0 ? "▲" : num < 0 ? "▼" : "–";
  return <span style={{ fontSize: 10, color, marginLeft: 6, fontWeight: 600 }}>{arrow} {Math.abs(num).toFixed(1)}%</span>;
}

function MetricCard({ label, value, sub, color, change, inverseChange }) {
  return (
    <div style={{ background: COLORS.metric, borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ fontSize: 11, color: COLORS.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", flexWrap: "wrap" }}>
        <div style={{ fontSize: 22, fontWeight: 600, color: color || COLORS.text }}>{value}</div>
        {change !== undefined && <ChangeIndicator change={change} inverse={inverseChange} />}
      </div>
      {sub && <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function SectionCard({ title, badge, children }) {
  return (
    <div style={{ background: COLORS.card, border: `0.5px solid ${COLORS.border}`, borderRadius: 12, padding: "1rem 1.25rem", marginBottom: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: COLORS.text }}>{title}</div>
        {badge && <span style={{ fontSize: 10, background: COLORS.metric, color: COLORS.muted, borderRadius: 6, padding: "2px 7px" }}>{badge}</span>}
      </div>
      {children}
    </div>
  );
}

// Reusable confirmation gate for any action that writes to Google Ads. Shows the
// exact impact, runs onConfirm(setProgress) on confirm, disables the confirm button
// while executing (double-submit guard), and reports success/error.
function ConfirmActionModal({ title, description, confirmLabel = "Confirm", tone = "amber", onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const confirmBg = tone === "red" ? "#DC2626" : "#D97706"; // red for pauses/negatives, amber for changes
  const run = async () => {
    if (busy) return; // double-submit guard
    setBusy(true); setResult(null);
    try {
      const r = await onConfirm(setProgress);
      if (r && r.ok === false) setResult({ ok: false, message: r.error || r.message || "Failed" });
      else setResult({ ok: true, message: (r && r.message) || "Done" });
    } catch (e) { setResult({ ok: false, message: e.message }); }
    setBusy(false); setProgress(null);
  };
  return (
    <div onClick={() => { if (!busy) onClose(); }} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, padding: 22, maxWidth: 470, width: "100%", boxShadow: "0 12px 48px rgba(0,0,0,0.2)" }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 10 }}>{title}</div>
        <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, marginBottom: 16 }}>{description}</div>
        {progress && <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 12 }}>{progress}</div>}
        {result ? (
          <div>
            <div style={{ fontSize: 13, color: result.ok ? COLORS.green : COLORS.red, marginBottom: 14, whiteSpace: "pre-wrap" }}>{result.ok ? "✓ " : "✕ "}{result.message}</div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", cursor: "pointer", fontSize: 13 }}>Close</button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={onClose} disabled={busy} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", cursor: busy ? "default" : "pointer", fontSize: 13, opacity: busy ? 0.5 : 1 }}>Cancel</button>
            <button onClick={run} disabled={busy} style={{ padding: "8px 18px", borderRadius: 6, border: "none", background: confirmBg, color: "#fff", cursor: busy ? "default" : "pointer", fontSize: 13, fontWeight: 600, opacity: busy ? 0.7 : 1 }}>{busy ? "Working…" : confirmLabel}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function PlanBar({ name, count, total, color }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: COLORS.text, maxWidth: "70%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
        <span style={{ fontSize: 12, color: COLORS.muted }}>{typeof count === 'number' ? count.toLocaleString() : count}</span>
      </div>
      <div style={{ height: 5, background: COLORS.metric, borderRadius: 3 }}>
        <div style={{ height: "100%", width: `${Math.min(pct, 100)}%`, background: color || COLORS.blue, borderRadius: 3, transition: "width 0.6s ease" }} />
      </div>
    </div>
  );
}

function ROASGroupCard({ label, revenue, spend, roas, color }) {
  return (
    <div style={{ background: COLORS.metric, borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
        <div style={{ fontSize: 12, fontWeight: 500, color: COLORS.text }}>{label}</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        <div><div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 2 }}>Revenue</div><div style={{ fontSize: 15, fontWeight: 600, color: COLORS.green }}>{fmt.gbp(revenue)}</div></div>
        <div><div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 2 }}>Spend</div><div style={{ fontSize: 15, fontWeight: 600, color: COLORS.orange }}>{fmt.gbp(spend)}</div></div>
        <div><div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 2 }}>ROAS</div><div style={{ fontSize: 15, fontWeight: 600, color: parseFloat(roas) >= 3 ? COLORS.green : parseFloat(roas) >= 1.5 ? COLORS.orange : COLORS.red }}>{fmt.x(roas)}</div></div>
      </div>
    </div>
  );
}

function LTVGroupCard({ label, ltv, payments, customers, ltvCac, payback, avgFirst, color }) {
  return (
    <div style={{ background: COLORS.metric, borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
          <div style={{ fontSize: 12, fontWeight: 500, color: COLORS.text }}>{label}</div>
        </div>
        <span style={{ fontSize: 10, color: COLORS.muted }}>{fmt.num(customers)} customers</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
        <div><div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 2 }}>Avg LTV</div><div style={{ fontSize: 15, fontWeight: 600, color: COLORS.green }}>{fmt.gbp(ltv)}</div></div>
        <div><div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 2 }}>Avg payments</div><div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text }}>{payments}</div></div>
        <div><div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 2 }}>LTV:CAC</div><div style={{ fontSize: 15, fontWeight: 600, color: parseFloat(ltvCac) >= 3 ? COLORS.green : parseFloat(ltvCac) >= 1.5 ? COLORS.orange : COLORS.red }}>{ltvCac}x</div></div>
        <div><div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 2 }}>Payback</div><div style={{ fontSize: 15, fontWeight: 600, color: COLORS.blue }}>{payback ? `${payback}mo` : '—'}</div></div>
      </div>
      {avgFirst && <div style={{ marginTop: 8, fontSize: 11, color: COLORS.muted }}>Avg first payment: {fmt.gbp(avgFirst)}</div>}
    </div>
  );
}

function HealthDot({ ok, label }) {
  return (
    <span style={{ fontSize: 11, color: ok ? COLORS.green : COLORS.red, display: 'flex', alignItems: 'center', gap: 3 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: ok ? COLORS.green : COLORS.red, display: 'inline-block' }} />
      {label}
    </span>
  );
}

function objToChartData(obj, key1 = "value") {
  return Object.entries(obj || {}).sort(([a], [b]) => a.localeCompare(b)).map(([date, value]) => ({
    date: date.slice(5), [key1]: Math.round(value * 100) / 100,
  }));
}

function mergeChartData(obj1, key1, obj2, key2) {
  const keys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);
  return [...keys].sort().map(date => ({
    date: date.slice(5),
    [key1]: Math.round((obj1[date] || 0) * 100) / 100,
    [key2]: Math.round((obj2[date] || 0) * 100) / 100,
  }));
}

// ── Date picker ───────────────────────────────────────────────────────────────

// Narrow-viewport detection for overlays. An absolutely-positioned dropdown anchored `right: 0`
// hangs its fixed width off the LEFT of the trigger, so on a ~390px phone a 360px panel renders
// almost entirely off-screen. Anything using this switches to the app's existing modal pattern
// (fixed backdrop + centred panel, maxWidth with a % width) already used by tcpaModal/kwModal.
function useIsNarrowViewport(breakpoint = 768) {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth <= breakpoint);
  useEffect(() => {
    if(typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = e => setNarrow(e.matches);
    setNarrow(mq.matches);
    // addListener is the deprecated form — kept as a fallback for older iOS Safari.
    if(mq.addEventListener) mq.addEventListener('change', onChange); else mq.addListener(onChange);
    return () => { if(mq.removeEventListener) mq.removeEventListener('change', onChange); else mq.removeListener(onChange); };
  }, [breakpoint]);
  return narrow;
}

// A 'YYYY-MM-DD' calendar day (UTC, as the backend sends them) as "14 Sep 2026", or "14 Sep" without the year.
const fmtDay = (day, withYear = true) => new Date(day + 'T00:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC' });
// The calendar day n days after (or before) a 'YYYY-MM-DD' day.
const addDays = (day, n) => new Date(Date.parse(day + 'T00:00:00Z') + n * 86400000).toISOString().slice(0, 10);

// MH £59 website funnel (GA4): landing page to purchase for the selected date range. The data is
// charts.mh59Funnel from the backend (getGA4Data), already scoped to the £59 pages and purchase event;
// this only draws it. Steps are counted separately rather than following the same people through, so a
// step can show more people than the one before; the backend marks that step with a note.
// siteChanges (/api/mh59/site-changes, same range) are website changes logged in the Change Log on the £59
// pages: each shows as a ◆ on the steps of the page it changed. onShowDays(fromDay, toDay) switches the
// dashboard to that custom range, so either side of a change can be read on its own.
function Mh59WebFunnel({ steps, rangeLabel, siteChanges, onShowDays }) {
  const narrow = useIsNarrowViewport();
  if(!Array.isArray(steps) || steps.length === 0) {
    return <div style={{ fontSize: 12, color: COLORS.muted }}>No GA4 funnel data for {rangeLabel}.</div>;
  }
  const max = Math.max(1, ...steps.map(s => s.users || 0));
  const rows = steps.map((s, i) => {
    const next = steps[i + 1];
    return { ...s, next, moved: next && s.users > 0 ? (next.users / s.users) * 100 : null };
  });
  const warnings = [...new Set(steps.filter(s => s.warning).map(s => s.warning))];
  const cell = { border: '1px solid #E5E7EB', padding: '4px 8px', whiteSpace: 'nowrap' };
  const noteMark = (s) => s.warning ? <span title={s.warning} style={{ color: '#9A3412', marginLeft: 4, cursor: 'help' }}>⚠</span>
    : s.note ? <span title={s.note} style={{ color: COLORS.orange, marginLeft: 4, cursor: 'help' }}>ⓘ</span> : null;
  const changes = siteChanges?.changes || [];
  const changeMark = (s) => {
    const hits = changes.filter(c => c.steps.includes(s.step));
    return hits.length ? <span title={`${hits.map(c => `Changed ${fmtDay(c.day)}: ${c.notes || c.label}`).join('\n')}\nEvery later step can move too.`} style={{ color: COLORS.purple, marginLeft: 4, cursor: 'help' }}>◆</span> : null;
  };
  const today = new Date().toISOString().slice(0, 10);
  const sideBtn = { fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid #C4B5FD', background: '#fff', color: COLORS.purple, cursor: 'pointer' };
  return (
    <div>
      <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 10 }}>Landing page to purchase, {rangeLabel}. Follows the date range above; the weekly trend and the cohort figures further down this tab don't.</div>
      {warnings.map(w => (
        <div key={w} style={{ background: '#FFF7ED', border: '1px solid #FED7AA', color: '#9A3412', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{w}</div>
      ))}
      {changes.length > 0 && (
        <div style={{ background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12, color: COLORS.text }}>
          <div style={{ fontWeight: 600 }}>◆ Website changes in this date range</div>
          {changes.map(c => (
            <div key={c.id} style={{ marginTop: 6 }}>
              <span style={{ color: COLORS.purple, fontWeight: 600 }}>{fmtDay(c.day)}</span> · {c.label}{c.notes ? `: ${c.notes}` : ''}
              {/* a change after the range's first day splits it: offer each side on its own */}
              {c.day > siteChanges.start && onShowDays && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
                  <span style={{ color: COLORS.muted }}>This range mixes before and after. Show one side:</span>
                  <button onClick={() => onShowDays(addDays(c.day, -42), addDays(c.day, -1))} style={sideBtn}>6 weeks before</button>
                  {addDays(c.day, 1) <= today && (
                    <button onClick={() => onShowDays(addDays(c.day, 1), addDays(c.day, 42) < today ? addDays(c.day, 42) : today)} style={sideBtn}>
                      {addDays(c.day, 42) < today ? '6 weeks after' : `Since ${fmtDay(addDays(c.day, 1), false)}`}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
          <div style={{ color: COLORS.muted, marginTop: 6 }}>From Ads Intelligence → Change Log. ◆ marks the steps on the changed page; every later step can move too. The buttons leave out the go-live day itself.</div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {rows.map((s, i) => (
          <div key={s.step}>
            <div style={{ display: 'flex', flexDirection: narrow ? 'column' : 'row', alignItems: narrow ? 'stretch' : 'center', gap: narrow ? 4 : 10 }}>
              <div style={{ width: narrow ? 'auto' : 170, flexShrink: 0, fontSize: 12, color: COLORS.text }}>{s.step}{noteMark(s)}{changeMark(s)}</div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, background: COLORS.metric, borderRadius: 6, height: 24 }}>
                  <div style={{ width: `${Math.max(1.5, (s.users / max) * 100)}%`, height: '100%', margin: '0 auto', borderRadius: 6, background: s.warning ? COLORS.muted : s.note ? COLORS.orange : i === rows.length - 1 ? COLORS.green : COLORS.blue }} />
                </div>
                <div style={{ width: 56, flexShrink: 0, fontSize: 13, fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt.num(s.users)}</div>
              </div>
            </div>
            {s.moved !== null && (
              <div style={{ fontSize: 11, padding: narrow ? '2px 0 6px' : '2px 0 6px 180px', color: s.moved > 100 ? COLORS.orange : COLORS.red }}>
                {s.moved > 100
                  ? `↑ ${fmt.num(s.next.users - s.users)} more at the next step: ${s.warning || s.next.warning ? 'undercounted for this date range (see warning above)' : 'steps are counted separately (see note)'}`
                  : `↓ ${fmt.pct(100 - s.moved)} dropped (${fmt.num(s.users - s.next.users)} people)`}
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ overflowX: 'auto', marginTop: 12 }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
          <thead><tr>{['Stage', 'People', 'Share of landing', 'Went on to next step', 'Dropped before next step'].map(h => (
            <th key={h} style={{ ...cell, background: '#F3F4F6', textAlign: 'left', fontWeight: 600 }}>{h}</th>))}</tr></thead>
          <tbody>{rows.map(s => (
            <tr key={s.step}>
              <td style={cell}>{s.step}{noteMark(s)}{changeMark(s)}</td>
              <td style={{ ...cell, fontWeight: 600 }}>{fmt.num(s.users)}</td>
              <td style={cell}>{s.pct === null || s.pct === undefined ? '—' : `${s.pct}%`}</td>
              <td style={cell}>{s.moved === null ? '—' : fmt.pct(s.moved)}</td>
              <td style={{ ...cell, color: s.moved !== null && s.moved <= 100 ? COLORS.red : COLORS.muted }}>
                {s.moved === null ? '—' : s.moved > 100 ? 'none (more at next step)' : `${fmt.pct(100 - s.moved)} · ${fmt.num(s.users - s.next.users)} people`}
              </td>
            </tr>))}</tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 8, lineHeight: 1.5 }}>
        Steps are counted separately, not by following the same people through, so a step can show more people than the one before. "Checkout page" can exceed "Checkout started" because some people reach checkout another way, for example from an email link. Source: GA4, £59 pages and the £59 purchase event only.
      </div>
    </div>
  );
}

// Weekly trend of the two £59 funnel rates the website fixes target (/api/mh59/weekly-trend): four-week rolling
// rates over the last 26 complete weeks, with a ◆ line at each website change logged on a £59 page. A fixed
// window, so it doesn't follow the date range.
function Mh59WeeklyTrend({ trend }) {
  const narrow = useIsNarrowViewport();
  if(!trend) return <div style={{ fontSize: 12, color: COLORS.muted }}>Loading the weekly trend…</div>;
  if(!trend.ok) return <div style={{ fontSize: 12, color: COLORS.red }}>Weekly trend unavailable: {trend.error}</div>;
  const weeks = trend.weeks.map(w => ({ ...w, label: fmtDay(w.week, false) }));
  const changes = trend.changes || [];
  const onChart = changes.filter(c => weeks.some(w => w.week === c.week));
  const notYet = changes.filter(c => c.week > trend.weeks[trend.weeks.length - 1].week);
  const median = (xs) => { const v = xs.filter(x => x != null).sort((a, b) => a - b); return v.length ? v[Math.floor(v.length / 2)] : 0; };
  const firstRecorded = (key) => weeks.find(w => w[key])?.week;
  // y axis in 10-point steps, up to the next 10 above the highest rate plus a margin
  const yMax = Math.min(100, Math.ceil((Math.max(0, ...weeks.flatMap(w => [w.proceedRate4w, w.purchaseRate4w]).filter(v => v != null)) + 5) / 10) * 10);
  const yTicks = Array.from({ length: yMax / 10 + 1 }, (_, i) => i * 10);
  // found by label: weeks with no rate on either line come with an empty payload
  const tip = ({ active, label }) => {
    const w = active && weeks.find(x => x.label === label);
    if(!w) return null;
    const line = (recorded, rate, n, unit, num, den) => !recorded ? 'not recorded yet'
      : `${rate == null ? 'needs four recorded weeks' : `${rate}% over 4 weeks (${fmt.num(n)} ${unit})`}; this week ${num} of ${den}`;
    return (
      <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '8px 10px', fontSize: 11, lineHeight: 1.6, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', maxWidth: 320 }}>
        <div style={{ fontWeight: 600 }}>Week of {fmtDay(w.week)}</div>
        <div style={{ color: COLORS.blue }}>Result → checkout: {line(w.proceedRecorded, w.proceedRate4w, w.eligible4w, 'eligible results', w.proceeded, w.eligible)}</div>
        <div style={{ color: COLORS.green }}>Checkout → purchase: {line(w.purchaseRecorded, w.purchaseRate4w, w.checkout4w, 'checkout visitors', w.purchased, w.checkout)}</div>
        {changes.filter(c => c.week === w.week).map(c => <div key={c.id} style={{ color: COLORS.purple }}>◆ {fmtDay(c.day)}: {c.notes || c.label}</div>)}
      </div>
    );
  };
  return (
    <div>
      <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 8 }}>Four-week rolling rates for the weeks of {fmtDay(trend.startDate)} to {fmtDay(trend.endDate)} (Monday to Sunday). Doesn't follow the date range.</div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, marginBottom: 6 }}>
        <span><span style={{ color: COLORS.blue, fontWeight: 700 }}>━</span> Eligible result → clicked through to checkout</span>
        <span><span style={{ color: COLORS.green, fontWeight: 700 }}>━</span> Checkout page → purchase</span>
        <span style={{ color: COLORS.purple }}>┆ ◆ website change</span>
      </div>
      <ResponsiveContainer width="100%" height={narrow ? 220 : 260}>
        <LineChart data={weeks} margin={{ top: 18, right: 12, left: -8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: COLORS.muted }} interval={narrow ? 3 : 1} />
          <YAxis tick={{ fontSize: 10, fill: COLORS.muted }} tickFormatter={v => `${v}%`} domain={[0, yMax]} ticks={yTicks} />
          <Tooltip content={tip} />
          {onChart.map(c => (
            <ReferenceLine key={c.id} x={fmtDay(c.week, false)} stroke={COLORS.purple} strokeDasharray="4 3"
              label={{ value: narrow ? '◆' : `◆ ${fmtDay(c.day, false)}`, position: 'top', fontSize: 10, fill: COLORS.purple }} />
          ))}
          <Line type="monotone" dataKey="proceedRate4w" name="Result → checkout" stroke={COLORS.blue} strokeWidth={2} dot={{ r: 2 }} connectNulls={false} isAnimationActive={false} />
          <Line type="monotone" dataKey="purchaseRate4w" name="Checkout → purchase" stroke={COLORS.green} strokeWidth={2} dot={{ r: 2 }} connectNulls={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
      {notYet.length > 0 && (
        <div style={{ fontSize: 11, color: COLORS.purple, marginTop: 6 }}>
          ◆ {notYet.map(c => `${fmtDay(c.day)} (${c.label})`).join(', ')}: appears on the chart two days after the end of its week.
        </div>
      )}
      <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 8, lineHeight: 1.5 }}>
        Each point covers about {fmt.num(median(weeks.map(w => w.eligible4w)))} eligible results and {fmt.num(median(weeks.map(w => w.checkout4w)))} checkout visitors, so moves of a few points are noise: judge a change over five to six weeks either side of its ◆.
        A line starts four weeks after its events were first recorded in full ({firstRecorded('proceedRecorded') ? fmtDay(firstRecorded('proceedRecorded')) : '—'} and {firstRecorded('purchaseRecorded') ? fmtDay(firstRecorded('purchaseRecorded')) : '—'}), and a week is added two days after it ends.
        People are counted per week, so someone active in two weeks of a window counts twice. Source: GA4, £59 pages and the £59 purchase event only; ◆ from Ads Intelligence → Change Log (type Website or Landing Page, on a £59 page).
      </div>
    </div>
  );
}

function DateRangePicker({ onApply, onCancel, initialStart, initialEnd }) {
  const [startDate, setStartDate] = useState(initialStart || '');
  const [endDate, setEndDate] = useState(initialEnd || '');

  const presets = [
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 14 days', days: 14 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 60 days', days: 60 },
    { label: 'Last 90 days', days: 90 },
    { label: 'Last 6 months', days: 180 },
    { label: 'Last 12 months', days: 365 },
  ];

  function applyPreset(days) {
    const end = new Date();
    const start = new Date(Date.now() - days*86400*1000);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  }

  function handleApply() {
    if(!startDate || !endDate) return;
    const from = Math.floor(new Date(startDate).getTime()/1000);
    const to = Math.floor(new Date(endDate + 'T23:59:59').getTime()/1000);
    onApply(from, to, `${startDate} → ${endDate}`);
  }

  const isNarrow = useIsNarrowViewport();

  // One body, two shells: a right-anchored dropdown on desktop, the app's standard centred modal on
  // a phone. The date inputs stack vertically when narrow — iOS date inputs have a wide intrinsic
  // minimum and would otherwise overflow a two-column row.
  const body = (
    <>
      <div style={{ display: 'flex', flexDirection: isNarrow ? 'column' : 'row', gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 4 }}>Start date</div>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ width: '100%', padding: '6px 8px', border: `0.5px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, background: COLORS.metric, color: COLORS.text }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 4 }}>End date</div>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ width: '100%', padding: '6px 8px', border: `0.5px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, background: COLORS.metric, color: COLORS.text }} />
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {presets.map(p => (
          <button key={p.label} onClick={() => applyPreset(p.days)} style={{ padding: '4px 10px', border: `0.5px solid ${COLORS.border}`, borderRadius: 6, background: COLORS.metric, fontSize: 11, cursor: 'pointer', color: COLORS.muted }}>
            {p.label}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={handleApply} disabled={!startDate || !endDate} style={{ flex: 1, padding: isNarrow ? '12px 8px' : '8px', background: COLORS.blue, color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', opacity: (!startDate || !endDate) ? 0.5 : 1 }}>Apply</button>
        <button onClick={onCancel} style={{ flex: 1, padding: isNarrow ? '12px 8px' : '8px', background: COLORS.metric, color: COLORS.text, border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
      </div>
    </>
  );

  // Phone: same shell as the app's other modals — fixed full-screen backdrop, centred panel that is a
  // PERCENTAGE of the viewport (never a fixed px width), capped height so it can't run off a short
  // screen. Tapping the backdrop cancels, matching tcpaModal/kwModal.
  if(isNarrow) {
    return (
      <div onClick={onCancel} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
        <div onClick={e => e.stopPropagation()} style={{ background: COLORS.card, border: `0.5px solid ${COLORS.border}`, borderRadius: 12, padding: '1rem', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', width: '90%', maxWidth: 420, maxHeight: '90vh', overflowY: 'auto', textAlign: 'left' }}>
          {body}
        </div>
      </div>
    );
  }

  // Desktop: unchanged right-anchored dropdown.
  return (
    <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 100, background: COLORS.card, border: `0.5px solid ${COLORS.border}`, borderRadius: 12, padding: '1rem', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', width: 360, marginTop: 4 }}>
      {body}
    </div>
  );
}

// ── Auth ──────────────────────────────────────────────────────────────────────

const GOOGLE_CLIENT_ID = "873469113819-cv0etv1gqlshpqg8qdg81boqsn3s2g2i.apps.googleusercontent.com";
const ALLOWED_EMAILS = ["sunil@tapgp.co.uk"];

function LoginScreen({ authErr, gsiReady }) {
  const btnRef = useRef(null);
  useEffect(() => {
    // Wait for the GSI script to load AND initialize() to have run (signalled by
    // gsiReady) before rendering the button — otherwise window.google is undefined
    // on mount and the button silently never appears.
    if (gsiReady && window.google && btnRef.current) {
      btnRef.current.innerHTML = "";
      window.google.accounts.id.renderButton(btnRef.current, { theme: "outline", size: "large", text: "sign_in_with" });
    }
  }, [gsiReady]);
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100vh", fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif", background: COLORS.bg }}>
      <div style={{ background:"#fff", borderRadius:16, padding:"2.5rem 3rem", boxShadow:"0 4px 24px rgba(0,0,0,0.08)", textAlign:"center", maxWidth:360 }}>
        <div style={{ fontSize:32, marginBottom:8 }}>📊</div>
        <h1 style={{ fontSize:20, fontWeight:600, marginBottom:4 }}>TapGP Dashboard</h1>
        <p style={{ fontSize:13, color: COLORS.muted, marginBottom:24 }}>Sign in with your TapGP Google account</p>
        <div ref={btnRef} style={{ display:"flex", justifyContent:"center" }} />
        {authErr && <p style={{ fontSize:12, color: COLORS.red, marginTop:12 }}>{authErr}</p>}
      </div>
    </div>
  );
}

const RANGES = ["yd", "1d", "1w", "1m", "3m", "6m", "1y"];
const RANGE_LABELS = { yd: "YD", "1d": "1D", "1w": "1W", "1m": "1M", "3m": "3M", "6m": "6M", "1y": "1Y" };
const TABS = ["Overview", "ROAS", "LTV", "Subscriptions", "MH Funnel", "Traffic", "Ads Intelligence", "Assistant"];
const ADS_SUBTABS = ['Recommendations','Keywords','Search Terms','Pacing','Ad Quality','Page Ideas','Impression Share','Cannibalism','Change Log','Settings','Action Plan'];
const tabToSlug = (t) => t.toLowerCase().replace(/\s+/g, '-');
// Action-plan task categories. `off` = off-platform work (not in the Google Ads change log,
// so it can't be auto-detected and must be ticked manually) — shown visually distinct.
const TASK_CAT = {
  landing_page: { label: 'Landing page', color: '#7C3AED', off: true },
  ad_copy:      { label: 'Ad copy',      color: '#DB2777', off: true },
  keyword:      { label: 'Keyword',      color: '#2563EB', off: false },
  negative:     { label: 'Negative',     color: '#92400E', off: false },
  bid:          { label: 'Bid / tCPA',   color: '#059669', off: false },
  other:        { label: 'Other',        color: '#6B7280', off: false },
};

// Relative-time formatter for the freshness indicator.
function timeAgo(ms) {
  if(!ms) return '';
  const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if(s < 60) return 'just now';
  if(s < 3600) return Math.floor(s/60) + 'm ago';
  if(s < 86400) return Math.floor(s/3600) + 'h ago';
  return Math.floor(s/86400) + 'd ago';
}

// ── Main Dashboard ────────────────────────────────────────────────────────────

// Error boundary so one render error in any tab shows a friendly message instead
// of a blank white screen.
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('Dashboard render error:', error, info); }
  render() {
    if(this.state.hasError) {
      return (
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif", background:'#F8F7F4', textAlign:'center', padding:'2rem' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>⚠️</div>
          <h1 style={{ fontSize:20, fontWeight:600, marginBottom:8, color:'#1a1a1a' }}>Something went wrong</h1>
          <p style={{ fontSize:13, color:'#888780', marginBottom:20, maxWidth:420 }}>The dashboard hit an unexpected error. Refreshing usually fixes it.</p>
          <button onClick={() => window.location.reload()} style={{ padding:'10px 24px', borderRadius:8, border:'none', background:'#534AB7', color:'#fff', cursor:'pointer', fontSize:14, fontWeight:600 }}>Refresh</button>
          {this.state.error && <pre style={{ marginTop:20, fontSize:11, color:'#888780', maxWidth:560, overflow:'auto', whiteSpace:'pre-wrap' }}>{String(this.state.error?.message || this.state.error)}</pre>}
        </div>
      );
    }
    return this.props.children;
  }
}

export default function Dashboard() {
  return <ErrorBoundary><DashboardApp /></ErrorBoundary>;
}

function DashboardApp() {
  const [user, setUser] = useState(() => {
    try { const s = localStorage.getItem("tapgp:dash:user"); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [authErr, setAuthErr] = useState("");
  const [gsiReady, setGsiReady] = useState(false); // GSI script loaded AND initialize() run

  useEffect(() => {
    const init = () => {
      if (!window.google) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response) => {
          const payload = JSON.parse(atob(response.credential.split(".")[1]));
          const email = payload.email;
          if (!ALLOWED_EMAILS.includes(email.toLowerCase())) { setAuthErr(`Access denied for ${email}.`); return; }
          window.__authReloading = false;
          // Use the Google ID token transiently so the session-mint call authenticates,
          // then swap it for the long-lived session JWT the server returns.
          setAuthToken(response.credential);
          try {
            const r = await _origFetch('/api/auth/session', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + response.credential }, body: JSON.stringify({ idToken: response.credential }) });
            const j = await r.json();
            if (j.ok && j.session) setAuthToken(j.session); // 30-day session replaces the ~1h ID token
            // If minting fails (e.g. SESSION_SECRET unset) we keep the ID token — still works ~1h.
          } catch (e) { /* keep the ID token as a fallback */ }
          const u = { name: payload.name, email, picture: payload.picture };
          setUser(u); setAuthErr("");
          localStorage.setItem("tapgp:dash:user", JSON.stringify(u));
        },
      });
      setGsiReady(true); // LoginScreen can now render the button
      if (!user) window.google.accounts.id.prompt();
    };
    // If the script is already present/loaded (e.g. remount), init immediately;
    // otherwise append it and init on load.
    if (window.google?.accounts?.id) {
      init();
    } else {
      const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existing) {
        existing.addEventListener("load", init);
      } else {
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.onload = init;
        document.head.appendChild(script);
      }
    }
  }, []);

  function signOut() {
    localStorage.removeItem("tapgp:dash:user");
    setAuthToken(null);
    setUser(null);
    window.google?.accounts.id.disableAutoSelect();
  }

  // NOTE: the !user early-return lives BELOW all hook declarations — returning
  // here would change the hook count between renders and crash React on sign-in.

  const [range, setRange] = useState("1m");
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const [customLabel, setCustomLabel] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tab, setTab] = useState("Overview");
  const [data, setData] = useState(null);
  const [asstMessages, setAsstMessages] = useState([]);
  const [asstInput, setAsstInput] = useState('');
  const [asstLoading, setAsstLoading] = useState(false);
  const [asstLoaded, setAsstLoaded] = useState(false);
  const asstEndRef = useRef(null);
  const [adsIntel, setAdsIntel] = useState(null);
  const [adQuality, setAdQuality] = useState(null);
  const [adQualityLoading, setAdQualityLoading] = useState(false);
  const [pageSpeed, setPageSpeed] = useState({}); // url -> { loading } | { data } | { error }
  const pageSpeedStarted = useRef(new Set());     // urls we've already kicked off
  const [expandedAdGroups, setExpandedAdGroups] = useState({});
  const [showGuide, setShowGuide] = useState(false);
  const [expandedRecs, setExpandedRecs] = useState({budget:true, demand:true, waste:true, fix:true, overtarget:false, bidding:false, watch:false});
  const [adsSettingsDraft, setAdsSettingsDraft] = useState({});
  const [settingsResult, setSettingsResult] = useState(null);
  const [negSettings, setNegSettings] = useState({ neg_filter_click_threshold: 3, neg_filter_spend_threshold: 5 });
  const [negDraft, setNegDraft] = useState({});
  const [negSettingsResult, setNegSettingsResult] = useState(null);
  const [tcpaModal, setTcpaModal] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // approval-gated action config
  const [tcpaValue, setTcpaValue] = useState('');
  const [tcpaResult, setTcpaResult] = useState(null);
  const [recCopySuggestions, setRecCopySuggestions] = useState({});
  const [recCopyLoading, setRecCopyLoading] = useState({});
  const [copySuggestions, setCopySuggestions] = useState({});
  const [copyLoading, setCopyLoading] = useState({});
  const [adsIntelLoading, setAdsIntelLoading] = useState(false);
  const [changes, setChanges] = useState([]);
  const [showChangeForm, setShowChangeForm] = useState(false);
  const [syncingHistory, setSyncingHistory] = useState(false);
  const [syncHistoryResult, setSyncHistoryResult] = useState(null);
  const [changeForm, setChangeForm] = useState({ change_type:'Google Ads', campaign_or_page:'', notes:'', html_before:'', html_after:'', observation_days:14, changed_on:'' });
  const [changeWarning, setChangeWarning] = useState(null);
  // MH Funnel tab: the Change Log's website changes on the £59 pages for the selected range (◆ markers on the
  // funnel), and the £59 weekly trend.
  const [mh59SiteChanges, setMh59SiteChanges] = useState(null);
  const [mh59Trend, setMh59Trend] = useState(null);
  const [adsTab, setAdsTab] = useState('Keywords');
  const [pageOpps, setPageOpps] = useState(null);
  const [pageOppsLoading, setPageOppsLoading] = useState(false);
  const [hideZeroSpend, setHideZeroSpend] = useState(true);
  const [kwSort, setKwSort] = useState({ col: 'spend', dir: 'desc' });
  const [kwFilter, setKwFilter] = useState({ campaign: '', keyword: '', flag: '' });
  const [stSort, setStSort] = useState({ col: 'spend', dir: 'desc' });
  const [stFilter, setStFilter] = useState({ campaign: '', term: '', action: '' });
  const [pacingSort, setPacingSort] = useState({ col: 'totalSpend', dir: 'desc' });
  const [pacingFilter, setPacingFilter] = useState('');
  const [cannFilter, setCannFilter] = useState('');
  const [showBorderline, setShowBorderline] = useState(false);
  const [minConfidence, setMinConfidence] = useState(60);
  const [kwModal, setKwModal] = useState(null); // { term, campaign, mode: 'keyword'|'negative' }
  const [manualKw, setManualKw] = useState({ term: '', assessing: false, result: null });
  const [adGroups, setAdGroups] = useState([]);
  const [selectedAdGroup, setSelectedAdGroup] = useState('');
  const [matchType, setMatchType] = useState('EXACT');
  const [kwActionResult, setKwActionResult] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [aiClassifications, setAiClassifications] = useState({});
  const [aiClassifying, setAiClassifying] = useState(false);
  const [actionPlan, setActionPlan] = useState(null);
  const [reportTab, setReportTab] = useState('Summary');
  const [apFeedback, setApFeedback] = useState('');
  const [apFeedbackSaved, setApFeedbackSaved] = useState(false);
  const [actionPlanLoading, setActionPlanLoading] = useState(false);
  const [actionPlanError, setActionPlanError] = useState(null);
  const [actionPlanQuestion, setActionPlanQuestion] = useState(null);
  const [actionPlanAnswer, setActionPlanAnswer] = useState('');
  const [actionPlanAnswerSaved, setActionPlanAnswerSaved] = useState(false);
  const [actionPlanHistory, setActionPlanHistory] = useState([]); // previous saved reports
  const [actionPlanAnswers, setActionPlanAnswers] = useState([]); // saved answers (matched to a report's question)
  const [actionPlanTasks, setActionPlanTasks] = useState({ open: [], completed: [] }); // tracked tasks from prior reports
  const [actionPlanActions, setActionPlanActions] = useState([]); // structured one-click actions for the viewed report
  const [selectedReportId, setSelectedReportId] = useState(null); // null = view the latest/current report
  const [businessContext, setBusinessContext] = useState(null);
  const [editingContext, setEditingContext] = useState(false);
  const [contextDraft, setContextDraft] = useState({});
  const [contextSaved, setContextSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [serverUpdated, setServerUpdated] = useState(null); // snapshot lastUpdated (ISO) from server
  const [nowTick, setNowTick] = useState(Date.now());       // bumps every 60s to refresh "X ago"
  const [changeEvals, setChangeEvals] = useState({});       // change_id -> outcome evaluation
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  // Shared with DateRangePicker — see useIsNarrowViewport. The alerts panel uses the same
  // fixed-width right-anchored pattern, so it gets the same narrow-viewport treatment.
  const isNarrowViewport = useIsNarrowViewport();
  const [ngramResult, setNgramResult] = useState(null);     // result of a "negative this word" action

  const SortHeader = ({ label, col, sort, setSort, style }) => (
    <th onClick={() => setSort(s => ({ col, dir: s.col === col && s.dir === 'desc' ? 'asc' : 'desc' }))}
      style={{ textAlign:'left', padding:'6px 8px', color: sort.col === col ? COLORS.purple : COLORS.muted, fontWeight:600, whiteSpace:'nowrap', cursor:'pointer', userSelect:'none', ...style }}>
      {label} {sort.col === col ? (sort.dir === 'desc' ? '↓' : '↑') : '↕'}
    </th>
  );

  const openKwModal = async (term, campaign, mode) => {
    setKwModal({ term, campaign, mode });
    setKwActionResult(null);
    setSelectedAdGroup('');
    setReadiness(null);
    // Fetch readiness for keyword additions
    if(mode === 'keyword') {
      try {
        const r = await authFetch('/api/ads/readiness?campaign=' + encodeURIComponent(campaign));
        const rj = await r.json();
        setReadiness(rj.readiness?.[0] || null);
      } catch(e) { setReadiness(null); }
    }
    if(mode === 'keyword') {
      try {
        const res = await authFetch('/api/ads/ad-groups?campaign=' + encodeURIComponent(campaign));
        const json = await res.json();
        setAdGroups(json.adGroups || []);
        if(json.adGroups?.length > 0) setSelectedAdGroup(json.adGroups[0].adGroupId);
      } catch(e) { setAdGroups([]); }
    }
  };

  const assessManualKeyword = async () => {
    if(!manualKw.term.trim()) return;
    setManualKw(s=>({...s, assessing:true, result:null}));
    try {
      const res = await authFetch('/api/ads/assess-keyword', {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ term: manualKw.term.trim() })
      });
      const json = await res.json();
      setManualKw(s=>({...s, assessing:false, result:json}));
    } catch(e) {
      setManualKw(s=>({...s, assessing:false, result:{ok:false, error:'Request failed'}}));
    }
  };
  const confirmKwAction = async () => {

    if(!kwModal) return;
    try {
      let result;
      if(kwModal.mode === 'negative') {
        const res = await authFetch('/api/ads/add-negative', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ keyword: kwModal.term, campaignName: kwModal.campaign }) });
        result = await res.json();
      } else {
        const res = await authFetch('/api/ads/add-keyword', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ keyword: kwModal.term, adGroupId: selectedAdGroup, matchType }) });
        result = await res.json();
      }
      setKwActionResult(result);
      if(result.ok) {
        // Auto-log to change log
        await authFetch('/api/changes', {
          method: 'POST',
          headers: {'Content-Type':'application/json'},
          body: JSON.stringify({
            change_type: 'Google Ads',
            campaign_or_page: kwModal.campaign,
            notes: kwModal.mode === 'negative'
              ? `Added "${kwModal.term}" as negative exact match keyword`
              : `Added "${kwModal.term}" as ${matchType.toLowerCase()} match keyword`,
            observation_days: 14
          })
        });
        fetchChanges();
      }
    } catch(e) {
      setKwActionResult({ ok: false, error: e.message });
    }
  };

  const classifyNegatives = async (searchTerms) => {
    const negCandidates = searchTerms.filter(st => st.intentClass !== 'in_scope' && (st.flag === 'negative_candidate' || (st.conversions === 0 && st.clicks > 0 && !st.isAlreadyNegative && (st.confidence||0) >= 60)));
    if(!negCandidates.length) return;
    setAiClassifying(true);
    try {
      const res = await authFetch('/api/ads/classify-negatives', {
        method: 'POST',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ terms: negCandidates.map(st => ({ term: st.term, campaign: st.campaign, clicks: st.clicks, spend: st.spend })) })
      });
      const json = await res.json();
      if(json.ok) setAiClassifications(json.classifications || {});
    } catch(e) { console.error(e); }
    setAiClassifying(false);
  };

  useEffect(() => {
    if(!user) return;
    if(tab === 'Ads Intelligence') {
      fetchAdsIntel(range);
      fetchAdQuality();
      fetchBusinessContext();
    }
  }, [user, range, tab, customFrom, customTo]);

  // Assistant: load saved conversation once when the tab is first opened.
  useEffect(() => {
    if(!user || tab !== 'Assistant' || asstLoaded) return;
    setAsstLoaded(true);
    (async () => {
      try { const r = await authFetch('/api/assistant/conversation'); const j = await r.json(); if(j.ok && Array.isArray(j.messages)) setAsstMessages(j.messages.map(m => { let actions = []; try { const t = m.tool_calls ? JSON.parse(m.tool_calls) : null; if(Array.isArray(t)) actions = t.map(x => ({ name: x.name, write: !!x.write, label: x.label || null })); } catch(e) {} return { role: m.role, content: m.content, actions }; })); } catch(e) {}
    })();
  }, [user, tab, asstLoaded]);

  const sendAssistant = async (text) => {
    const msg = String(text != null ? text : asstInput).trim();
    if(!msg || asstLoading) return;
    setAsstInput('');
    setAsstMessages(m => [...m, { role: 'user', content: msg }]);
    setAsstLoading(true);
    try {
      const r = await authFetch('/api/assistant/chat', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: msg }) });
      const j = await r.json();
      setAsstMessages(m => [...m, { role: 'assistant', content: j.ok ? j.reply : ('⚠️ ' + (j.error || 'Something went wrong.')), actions: (j.ok && Array.isArray(j.actions)) ? j.actions : [] }]);
    } catch(e) {
      setAsstMessages(m => [...m, { role: 'assistant', content: '⚠️ Could not reach the assistant. Please try again.' }]);
    } finally { setAsstLoading(false); }
  };
  const clearAssistant = async () => {
    // Only wipe the local chat when the server clear succeeded — the server clear also cancels any
    // staged (confirmation-pending) action, and an apparently-fresh chat must never hide a live one.
    try {
      const r = await authFetch('/api/assistant/clear', { method: 'POST' });
      const j = await r.json();
      if(j.ok) setAsstMessages([]);
    } catch(e) {}
  };
  useEffect(() => { if(tab === 'Assistant') asstEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [asstMessages, asstLoading, tab]);

  // Load the global wrong-intent filter thresholds when the Settings tab opens.
  useEffect(() => {
    if(!user || !(tab === 'Ads Intelligence' && adsTab === 'Settings')) return;
    setNegSettingsResult(null); // clear any stale Saved/error banner from a previous visit
    (async () => {
      try { const r = await authFetch('/api/ads/global-settings'); const d = await r.json(); if(d.ok && d.settings) setNegSettings(d.settings); } catch(e) {}
    })();
  }, [user, tab, adsTab]);

  // Lazy-load mobile PageSpeed for each landing page when the Ad Quality tab opens.
  // Fired in the background per-URL (cached server-side) so it never blocks the tab.
  useEffect(() => {
    if(!user) return;
    if(!(tab === 'Ads Intelligence' && adsTab === 'Ad Quality' && adQuality?.adGroups)) return;
    const urls = [...new Set(adQuality.adGroups.map(ag => ag.url).filter(Boolean))];
    urls.forEach(url => {
      if(pageSpeedStarted.current.has(url)) return;
      pageSpeedStarted.current.add(url);
      setPageSpeed(p => ({ ...p, [url]: { loading: true } }));
      authFetch('/api/ads/pagespeed?url=' + encodeURIComponent(url))
        .then(r => r.json())
        .then(j => setPageSpeed(p => ({ ...p, [url]: j.ok ? { data: j } : { error: j.error || 'failed' } })))
        .catch(e => setPageSpeed(p => ({ ...p, [url]: { error: e.message } })));
    });
  }, [user, tab, adsTab, adQuality]);

  useEffect(() => {
    if(!user) return;
    if(adsTab === 'Action Plan' && adsIntel) {
      // Always refresh the history (for the Previous-reports selector); generate a new
      // report only if we don't already have one and the latest saved one is stale.
      fetchActionPlanTasks();
      fetchActionPlanActions();
      authFetch('/api/action-plan/history').then(r=>r.json()).then(json=>{
        const reports = json.reports || [];
        setActionPlanHistory(reports);
        setActionPlanAnswers(json.answers || []);
        if(!actionPlan && !actionPlanLoading) {
          const last = reports[0];
          const sevenDaysAgo = Math.floor(Date.now()/1000) - 7*24*60*60;
          if(!last || last.generated_at < sevenDaysAgo) {
            generateActionPlan();
          } else {
            setActionPlan(last.report);
            setActionPlanQuestion(last.question);
          }
        }
      }).catch(()=>{});
    }
  }, [user, adsTab, adsIntel]);

  // Reload one-click actions for whichever report is being viewed (latest, or a selected historical one).
  useEffect(() => {
    if(adsTab === 'Action Plan') fetchActionPlanActions(selectedReportId || undefined);
  }, [adsTab, selectedReportId]);

  const fetchPageOpps = async () => {
    if (pageOpps || pageOppsLoading) return;
    if (!adsIntel || !adsIntel.searchTerms) return;
    setPageOppsLoading(true);
    try {
      const aqKws = (adQuality && adQuality.keywords) ? adQuality.keywords : [];
      const pageScoreFor = (term, camp) => { const m = aqKws.find(q => q.keyword.toLowerCase() === term.toLowerCase() && q.campaign === camp); return m ? m.pageScore : null; };
      const shortlist = (adsIntel.searchTerms || []).filter(s => s.clicks >= 10 && s.spend >= 30 && (s.conversions === 0 || (s.conversions > 0 && (s.spend / Math.max(s.conversions, 0.01)) > 50))).map(s => ({ term: s.term, campaign: s.campaign, clicks: s.clicks, conversions: s.conversions, spend: s.spend, pageScore: pageScoreFor(s.term, s.campaign) }));
      const r = await authFetch('/api/ads/page-opportunities', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ terms: shortlist }) });
      const d = await r.json();
      if (d.ok) setPageOpps({ verdicts: d.results || {}, shortlist });
    } catch (e) { setPageOpps({ verdicts: {}, shortlist: [] }); }
    setPageOppsLoading(false);
  };
  const fetchAdQuality = async () => {
    if(adQuality) return; // cached for session
    setAdQualityLoading(true);
    try {
      const res = await authFetch('/api/ads/quality');
      const json = await res.json();
      if(json.ok) setAdQuality(json);
    } catch(e) { console.error(e); }
    setAdQualityLoading(false);
  };

  const fetchBusinessContext = async () => {
    try {
      const res = await authFetch('/api/action-plan/context');
      const json = await res.json();
      if(json.ok) setBusinessContext(json.context);
    } catch(e) {}
  };

  const fetchActionPlanHistory = async () => {
    try {
      const r = await authFetch('/api/action-plan/history');
      const json = await r.json();
      setActionPlanHistory(json.reports || []);
      setActionPlanAnswers(json.answers || []);
      return json.reports || [];
    } catch(e) { return []; }
  };

  const fetchActionPlanTasks = async () => {
    try {
      const r = await authFetch('/api/action-plan/tasks');
      const json = await r.json();
      if(json.ok) setActionPlanTasks({ open: json.open || [], completed: json.completed || [] });
    } catch(e) { /* non-fatal */ }
  };

  const toggleTaskStatus = async (id, status) => {
    // Optimistic: the panel re-fetches after the write so it reflects the server.
    try {
      await authFetch('/api/action-plan/tasks/' + id + '/status', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ status }) });
    } catch(e) { console.error(e); }
    fetchActionPlanTasks();
  };

  const fetchActionPlanActions = async (reportId) => {
    try {
      const r = await authFetch('/api/action-plan/actions' + (reportId ? ('?report_id=' + reportId) : ''));
      const json = await r.json();
      if(json.ok) setActionPlanActions(json.actions || []);
    } catch(e) { /* non-fatal */ }
  };

  const dismissAction = async (id) => {
    try { await authFetch('/api/action-plan/actions/' + id + '/status', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ status: 'dismissed' }) }); }
    catch(e) { console.error(e); }
    fetchActionPlanActions(selectedReportId || undefined);
  };

  // Apply a stored one-click action through the EXISTING ConfirmActionModal + existing write endpoints,
  // mirroring the askPauseKeyword/askRaiseTcpa builders. On success: log via logChangeQuiet (pause-keyword
  // auto-logs server-side, so it's omitted there to match), mark the action applied, and refresh actions +
  // tasks (the applied-status write triggers task auto-reconciliation server-side).
  const applyAction = (action) => {
    const p = action.params || {};
    const camp = p.campaign || '';
    const mt = (p.match_type || 'EXACT');
    const hasNums = action.spend != null && action.clicks != null && action.conv != null && action.cpa != null;
    const numsRow = hasNums ? <div style={{fontSize:12,color:COLORS.muted,marginTop:6}}>Spend £{Math.round(action.spend)} · {action.clicks} clicks · {action.conv} conv · £{Math.round(action.cpa)} CPA</div> : null;
    const onApplied = async (j) => {
      if(j && j.ok) {
        try { await authFetch('/api/action-plan/actions/' + action.id + '/status', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ status: 'applied' }) }); } catch(e) {}
        fetchActionPlanActions(selectedReportId || undefined);
        fetchActionPlanTasks();
        fetchChanges();
      }
    };
    if(action.action_type === 'pause_keyword') {
      // FIX 2A — the reason logged with the pause (so a future reactivation can explain why). Prefer the
      // model's own `reason`; otherwise derive one from the action's figures so the log is never empty.
      const pauseReason = (p.reason && String(p.reason).trim())
        || [`paused via Action Plan`, (action.conv != null ? `${action.conv} conv` : null), (action.spend != null ? `£${Math.round(action.spend)} spend` : null), (action.clicks != null ? `${action.clicks} clicks` : null)].filter(Boolean).join(', ');
      setConfirmModal({
        title: '🚫 Pause keyword', confirmLabel: 'Pause keyword', tone: 'red',
        description: (<div><div>Pause keyword <b>"{p.keyword}"</b> in <b>{shortenCampaign(camp)}</b>{p.ad_group ? ` / ${p.ad_group}` : ''}.</div>{numsRow}</div>),
        onConfirm: async () => {
          const r = await authFetch('/api/ads/pause-keyword', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ campaignName: camp, adGroupName: p.ad_group, keywordText: p.keyword, matchType: mt, reason: pauseReason }) });
          const j = await r.json();
          await onApplied(j); // pause-keyword auto-logs the change server-side
          return j;
        }
      });
    } else if(action.action_type === 'add_negative') {
      setConfirmModal({
        title: '🚫 Add negative keyword', confirmLabel: 'Add negative', tone: 'red',
        description: (<div><div>Add <b>"{p.keyword}"</b> as a {mt.toLowerCase()} negative in <b>{shortenCampaign(camp)}</b>.</div>{numsRow}</div>),
        onConfirm: async () => {
          const r = await authFetch('/api/ads/add-negative', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ keyword: p.keyword, campaignName: camp, matchType: mt }) });
          const j = await r.json();
          if(j.ok) await logChangeQuiet(camp, `Added "${p.keyword}" as ${mt.toLowerCase()} negative keyword`);
          await onApplied(j);
          return j;
        }
      });
    } else if(action.action_type === 'raise_tcpa') {
      setConfirmModal({
        title: '⚙️ Set target CPA', confirmLabel: `Set £${p.value}`, tone: 'amber',
        description: (<div><div>Set target CPA for <b>{shortenCampaign(camp)}</b> to <b>£{p.value}</b>.</div>{numsRow}</div>),
        onConfirm: async () => {
          const r = await authFetch('/api/ads/update-tcpa', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ campaignName: camp, newTcpa: p.value }) });
          const j = await r.json();
          if(j.ok) await logChangeQuiet(camp, `Set target CPA to £${p.value}`);
          await onApplied(j);
          return j;
        }
      });
    } else if(action.action_type === 'add_keyword') {
      setConfirmModal({
        title: '➕ Add keyword', confirmLabel: 'Add keyword', tone: 'amber',
        description: (<div><div>Add <b>"{p.keyword}"</b> as a {mt.toLowerCase()} keyword in <b>{shortenCampaign(camp)}</b>{p.ad_group ? ` / ${p.ad_group}` : ''}.</div>{numsRow}</div>),
        onConfirm: async () => {
          // The add-keyword endpoint needs the numeric adGroupId — resolve it from the ad-group name.
          const agR = await authFetch('/api/ads/ad-groups?campaign=' + encodeURIComponent(camp));
          const agJson = await agR.json();
          const ag = (agJson.adGroups || []).find(g => (g.adGroupName || '').toLowerCase() === (p.ad_group || '').toLowerCase());
          if(!ag) return { ok: false, error: `Could not find ad group "${p.ad_group}" in ${camp}` };
          const r = await authFetch('/api/ads/add-keyword', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ keyword: p.keyword, adGroupId: ag.adGroupId, matchType: mt }) });
          const j = await r.json();
          if(j.ok) await logChangeQuiet(camp, `Added "${p.keyword}" as ${mt.toLowerCase()} keyword`);
          await onApplied(j);
          return j;
        }
      });
    } else if(action.action_type === 'reactivate_keyword') {
      // FIX 2C — surface the original pause context (date + reason) when one was recorded.
      const pauseContext = (p.paused_on || p.pause_reason)
        ? (<div style={{marginTop:8,fontSize:12,color:COLORS.muted}}>This keyword was paused{p.paused_on ? ` on ${p.paused_on}` : ''}{p.pause_reason ? ` because ${p.pause_reason}` : ''}. The destination page is confirmed clean.</div>)
        : null;
      setConfirmModal({
        title: '♻️ Reactivate keyword', confirmLabel: 'Reactivate', tone: 'amber',
        description: (<div><div>Re-enable paused keyword <b>"{p.keyword}"</b> in <b>{shortenCampaign(camp)}</b>{p.ad_group ? ` / ${p.ad_group}` : ''}.</div>{pauseContext}{numsRow}</div>),
        onConfirm: async () => {
          const r = await authFetch('/api/ads/reactivate-keyword', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ campaignName: camp, adGroupName: p.ad_group, keywordText: p.keyword, matchType: mt }) });
          const j = await r.json();
          await onApplied(j); // reactivate-keyword auto-logs the change server-side
          return j;
        }
      });
    } else if(action.action_type === 'update_final_url') {
      setConfirmModal({
        title: '🔗 Repoint Final URL', confirmLabel: 'Repoint URL', tone: 'amber',
        description: (<div><div>Repoint <b>{shortenCampaign(camp)}{p.ad_group ? ` / ${p.ad_group}` : ''}</b> Final URL to:</div><div style={{fontSize:12,wordBreak:'break-all',marginTop:4,color:COLORS.text}}><b>{p.new_url}</b></div></div>),
        onConfirm: async () => {
          const r = await authFetch('/api/ads/update-final-url', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ campaignName: camp, adGroupName: p.ad_group, newUrl: p.new_url }) });
          const j = await r.json();
          await onApplied(j); // auto-logs server-side
          return j;
        }
      });
    } else if(action.action_type === 'update_budget') {
      const isCut = p.current_daily_budget != null && Number(p.new_daily_budget) < Number(p.current_daily_budget);
      setConfirmModal({
        title: isCut ? '✂️ Cut daily budget' : '💷 Raise daily budget', confirmLabel: `Set £${p.new_daily_budget}/day`, tone: isCut ? 'red' : 'amber',
        description: (<div><div>Set <b>{shortenCampaign(camp)}</b> daily budget to <b>£{p.new_daily_budget}</b>{p.current_daily_budget != null ? ` (from £${p.current_daily_budget})` : ''}.</div></div>),
        onConfirm: async () => {
          const r = await authFetch('/api/ads/update-budget', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ campaignName: camp, newDailyBudget: p.new_daily_budget }) });
          const j = await r.json();
          await onApplied(j); // auto-logs server-side
          return j;
        }
      });
    } else if(action.action_type === 'update_keyword_bid') {
      setConfirmModal({
        title: '📉 Set keyword bid', confirmLabel: `Set £${p.new_bid_gbp}`, tone: 'amber',
        description: (<div><div>Set CPC bid on <b>"{p.keyword}"</b> in <b>{shortenCampaign(camp)}</b>{p.ad_group ? ` / ${p.ad_group}` : ''} to <b>£{p.new_bid_gbp}</b>.</div>{numsRow}</div>),
        onConfirm: async () => {
          const r = await authFetch('/api/ads/update-keyword-bid', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ campaignName: camp, adGroupName: p.ad_group, keywordText: p.keyword, matchType: mt, newBidGbp: p.new_bid_gbp }) });
          const j = await r.json();
          await onApplied(j); // auto-logs server-side
          return j;
        }
      });
    }
  };

  const generateActionPlan = async () => {
    setActionPlanLoading(true);
    setActionPlan(null);
    setActionPlanError(null);
    try {
      const res = await authFetch('/api/action-plan/generate', {
        method: 'POST',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ range })
      });
      const json = await res.json();
      if(json.ok) {
        setActionPlan(json.report);
        setActionPlanQuestion(json.question);
        setActionPlanAnswer('');
        setActionPlanAnswerSaved(false);
        setSelectedReportId(null);   // snap back to the latest after generating
        fetchActionPlanHistory();     // refresh so the new report appears in the selector
        fetchActionPlanTasks();       // new report inserts new open tasks; auto-detect may have closed some
        fetchActionPlanActions(json.report_id); // load the new report's one-click actions
      } else {
        setActionPlanError(json.error || 'Generation failed — try again');
      }
    } catch(e) { console.error(e); setActionPlanError(e.message); }
    setActionPlanLoading(false);
  };

  const saveApFeedback = async () => {
    if(!apFeedback.trim()) return;
    await authFetch('/api/action-plan/answer', {
      method: 'POST',
      headers: {'Content-Type':'application/json'},
      body: JSON.stringify({ question: '(Sunil feedback)', answer: apFeedback })
    });
    setApFeedbackSaved(true);
    setApFeedback('');
    setTimeout(() => setApFeedbackSaved(false), 3000);
  };

  const saveActionPlanAnswer = async () => {
    if(!actionPlanAnswer.trim() || !actionPlanQuestion) return;
    await authFetch('/api/action-plan/answer', {
      method: 'POST',
      headers: {'Content-Type':'application/json'},
      body: JSON.stringify({ question: actionPlanQuestion, answer: actionPlanAnswer })
    });
    setActionPlanAnswerSaved(true);
    fetchActionPlanHistory(); // so the saved answer shows under the Question tab
  };

  const saveBusinessContext = async () => {
    await authFetch('/api/action-plan/context', {
      method: 'POST',
      headers: {'Content-Type':'application/json'},
      body: JSON.stringify(contextDraft)
    });
    setBusinessContext(prev => ({...prev, ...contextDraft}));
    setEditingContext(false);
    setContextSaved(true);
    setTimeout(() => setContextSaved(false), 3000);
  };

  const fetchAdsIntel = async (r, full = false) => {
    setAdsIntelLoading(true);
    try {
      let url = '/api/ads-intelligence?range=' + r;
      if(r === 'custom' && customFrom && customTo) {
        url += `&from=${new Date(customFrom*1000).toISOString().split('T')[0]}&to=${new Date(customTo*1000).toISOString().split('T')[0]}`;
      }
      if(full) url += '&full=1';
      const res = await authFetch(url);
      const json = await res.json();
      setAdsIntel(json);
      if(json.searchTerms) classifyNegatives(json.searchTerms);
    } catch(e) { console.error(e); }
    setAdsIntelLoading(false);
  };

  const fetchChanges = async () => {
    try {
      const res = await authFetch('/api/changes');
      const json = await res.json();
      setChanges(json.changes || []);
    } catch(e) { console.error(e); }
  };

  // ◆ markers for the £59 funnel: website changes logged on its pages within the selected range.
  const fetchMh59SiteChanges = async (r, from, to) => {
    try {
      const res = await authFetch(`/api/mh59/site-changes?range=${r}${r === 'custom' && from && to ? `&from=${from}&to=${to}` : ''}`);
      const json = await res.json();
      setMh59SiteChanges(json.ok ? json : null);
    } catch(e) { console.error(e); }
  };

  const fetchMh59Trend = async () => {
    try {
      const res = await authFetch('/api/mh59/weekly-trend');
      setMh59Trend(await res.json());
    } catch(e) { setMh59Trend({ ok: false, error: e.message }); }
  };

  // A logged or deleted website change moves the funnel's ◆ markers and the trend's lines.
  const refreshSiteChangeViews = () => {
    fetchMh59SiteChanges(range, customFrom, customTo);
    if(mh59Trend) fetchMh59Trend();
  };

  const submitChange = async () => {
    try {
      const res = await authFetch('/api/changes', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(changeForm) });
      const json = await res.json();
      // Keep the form open on a refused entry (e.g. a go-live date in the future) so it can be corrected.
      if(json.ok === false) { setChangeWarning(json.error || 'The change was not saved.'); return; }
      if(json.warning) setChangeWarning(json.warning);
      else setChangeWarning(null);
      setShowChangeForm(false);
      setChangeForm({ change_type:'Google Ads', campaign_or_page:'', notes:'', html_before:'', html_after:'', observation_days:14, changed_on:'' });
      fetchChanges();
      refreshSiteChangeViews();
    } catch(e) { console.error(e); }
  };

  const deleteChange = async (id) => {
    await authFetch('/api/changes/' + id, { method:'DELETE' });
    fetchChanges();
    refreshSiteChangeViews();
  };

  const syncAdsHistory = async () => {
    setSyncingHistory(true);
    setSyncHistoryResult(null);
    try {
      const res = await authFetch('/api/ads/sync-change-history', { method:'POST' });
      const json = await res.json();
      setSyncHistoryResult(json.ok ? { ok:true, imported: json.imported || 0 } : { ok:false, error: json.error || 'Sync failed' });
      if(json.ok) fetchChanges();
    } catch(e) { setSyncHistoryResult({ ok:false, error: e.message }); }
    setSyncingHistory(false);
  };

  const diffHtml = (before, after) => {
    if(!before || !after) return [];
    const bLines = before.split('\n');
    const aLines = after.split('\n');
    const result = [];
    const maxLen = Math.max(bLines.length, aLines.length);
    for(let i=0; i<maxLen; i++) {
      if(bLines[i] !== aLines[i]) {
        if(bLines[i]) result.push({ type:'removed', text: bLines[i] });
        if(aLines[i]) result.push({ type:'added', text: aLines[i] });
      }
    }
    return result;
  };

  const fetchData = useCallback(async (r, from, to) => {
    try {
      setLoading(true); setError(null);
      let url = `${API}/api/dashboard?range=${r}`;
      if(r === 'custom' && from && to) url += `&from=${from}&to=${to}`;
      const res = await authFetch(url);
      if (!res.ok) throw new Error(`API error ${res.status}`);
      const json = await res.json();
      setData(json);
      setLastUpdated(new Date().toLocaleTimeString("en-GB"));
      setServerUpdated(json.lastUpdated || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if(!user) return;
    fetchData(range, customFrom, customTo);
    fetchChanges();
    fetchMh59SiteChanges(range, customFrom, customTo);
    const interval = setInterval(() => fetchData(range, customFrom, customTo), 300000);
    return () => clearInterval(interval);
  }, [user, range, customFrom, customTo, fetchData]);

  // The weekly trend is a fixed window, so it loads once, the first time the MH Funnel tab opens.
  useEffect(() => {
    if(user && tab === 'MH Funnel' && !mh59Trend) fetchMh59Trend();
  }, [user, tab, mh59Trend]);

  // ── Debounced filters + memoized heavy-table rows ──────────────────────────
  // These run the 13k-row filter+sort at most once per 300ms of typing, and only
  // when an input they depend on actually changes (not on every render). MUST be
  // above the auth gate — they are hooks.
  const dStTerm = useDebounced(stFilter.term);
  const dKwCampaign = useDebounced(kwFilter.campaign);
  const dKwKeyword = useDebounced(kwFilter.keyword);

  const aqKwIndex = useMemo(() => {
    const idx = {};
    (adQuality?.keywords || []).forEach(q => { idx[q.keyword.toLowerCase() + '|||' + q.campaign] = q; });
    return idx;
  }, [adQuality]);

  const searchTermRows = useMemo(() => {
    const all = adsIntel?.searchTerms || [];
    const filtered = all.filter(st => {
      const matchesFilters = (!dStTerm || st.term.toLowerCase().includes(dStTerm.toLowerCase())) && (!stFilter.campaign || st.campaign.toLowerCase().includes(stFilter.campaign.toLowerCase()));
      if(stFilter.action) return matchesFilters && ((stFilter.action === 'add_as_keyword' && st.recClass === 'opportunity') || (stFilter.action === 'negative_candidate' && !st.isAlreadyNegative && st.intentClass !== 'in_scope' && (st.recClass === 'waste' || st.recClass === 'extreme_cpa')));
      if(showBorderline) return matchesFilters && st.conversions === 0 && !st.isAlreadyNegative && (st.confidence || 0) >= minConfidence && (st.recClass === 'waste' || (st.clicks > 0 && st.clicks < (st.clickThreshold || 50)));
      return matchesFilters && !st.isAlreadyNegative && (st.recClass === 'opportunity' || st.recClass === 'waste' || st.recClass === 'extreme_cpa');
    }).map(st => ({ ...st, cpa: st.conversions > 0 ? Math.round(st.spend / st.conversions) : null }));
    return sortData(filtered, stSort.col, stSort.dir);
  }, [adsIntel, dStTerm, stFilter.campaign, stFilter.action, showBorderline, minConfidence, stSort.col, stSort.dir]);

  const keywordRows = useMemo(() => {
    const all = adsIntel?.keywords || [];
    const filtered = all.filter(k => (!hideZeroSpend || k.spend > 0) && (!dKwCampaign || k.campaign.toLowerCase().includes(dKwCampaign.toLowerCase())) && (!dKwKeyword || k.keyword.toLowerCase().includes(dKwKeyword.toLowerCase())) && (!kwFilter.flag || k.flag === kwFilter.flag))
      .map(k => { const aq = aqKwIndex[k.keyword.toLowerCase() + '|||' + k.campaign]; return { ...k, adScore: aq?.adScore ?? null, pageScore: aq?.pageScore ?? null }; });
    return sortData(filtered, kwSort.col, kwSort.dir);
  }, [adsIntel, hideZeroSpend, dKwCampaign, dKwKeyword, kwFilter.flag, kwSort.col, kwSort.dir, aqKwIndex]);

  // N-gram aggregation over the full search-term set (word -> clicks/spend/conv).
  const ngramData = useMemo(() => {
    const all = adsIntel?.searchTerms || [];
    const STOP = new Set(['the','for','a','to','in','of','and','or','my','me','near','with','on','at','is','i','uk','an','do','can','how','what','your','you']);
    const words = {};
    all.forEach(st => {
      const seen = new Set();
      (st.term || '').toLowerCase().split(/\s+/).forEach(raw => {
        const w = raw.replace(/[^a-z0-9]/g, '');
        if(w.length < 3 || STOP.has(w) || seen.has(w)) return;
        seen.add(w);
        if(!words[w]) words[w] = { word: w, clicks: 0, spend: 0, conversions: 0, terms: 0, campaignSpend: {} };
        words[w].clicks += st.clicks || 0;
        words[w].spend += st.spend || 0;
        words[w].conversions += st.conversions || 0;
        words[w].terms += 1;
        words[w].campaignSpend[st.campaign] = (words[w].campaignSpend[st.campaign] || 0) + (st.spend || 0);
      });
    });
    return Object.values(words).map(w => {
      const domCampaign = Object.entries(w.campaignSpend).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
      return { word: w.word, clicks: w.clicks, terms: w.terms, spend: Math.round(w.spend * 100) / 100, conversions: Math.round(w.conversions * 100) / 100, domCampaign, systemic: w.spend >= 50 && w.conversions < 0.5 && w.terms >= 5 };
    }).sort((a, b) => b.spend - a.spend);
  }, [adsIntel]);

  // Relative-time tick: re-render every 60s so "Updated Xh ago" stays current.
  useEffect(() => {
    if(!user) return;
    const t = setInterval(() => setNowTick(Date.now()), 60000);
    return () => clearInterval(t);
  }, [user]);

  // Poll alerts every 5 min.
  useEffect(() => {
    if(!user) return;
    const load = () => authFetch('/api/alerts').then(r => r.json()).then(j => { if(j.ok) setAlerts(j.alerts || []); }).catch(() => {});
    load();
    const t = setInterval(load, 300000);
    return () => clearInterval(t);
  }, [user]);

  // Load change outcome evaluations when the Change Log tab opens.
  useEffect(() => {
    if(!user) return;
    if(tab === 'Ads Intelligence' && adsTab === 'Change Log') {
      authFetch('/api/changes/evaluations').then(r => r.json()).then(j => { if(j.ok) setChangeEvals(j.evaluations || {}); }).catch(() => {});
    }
  }, [user, tab, adsTab]);

  // ── Hash routing: #<tab>[/<adsSubtab>] survives refresh ────────────────────
  useEffect(() => {
    if(!user) return;
    const applyHash = () => {
      const raw = window.location.hash.replace(/^#/, '');
      if(!raw) return;
      const [tabSlug, subSlug] = raw.split('/');
      const matchedTab = TABS.find(t => tabToSlug(t) === tabSlug);
      if(matchedTab) setTab(matchedTab);
      if(matchedTab === 'Ads Intelligence' && subSlug) {
        const matchedSub = ADS_SUBTABS.find(s => tabToSlug(s) === subSlug);
        if(matchedSub) setAdsTab(matchedSub);
      }
    };
    applyHash();
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, [user]);

  // Write the current tab/subtab back to the hash. Skip the first run so we don't
  // clobber an incoming hash before the read effect above has applied it.
  const hashWriteInit = useRef(true);
  useEffect(() => {
    if(!user) return;
    if(hashWriteInit.current) { hashWriteInit.current = false; return; }
    const slug = tab === 'Ads Intelligence' ? `ads-intelligence/${tabToSlug(adsTab)}` : tabToSlug(tab);
    if(window.location.hash.replace(/^#/, '') !== slug) {
      window.history.replaceState(null, '', '#' + slug);
    }
  }, [user, tab, adsTab]);

  // Auto-imported Google Ads changes are records, not reviews the user owns — exclude them.
  const overdueReviews = (changes || []).filter(ch => ch.change_type !== 'Google Ads (auto)' && ch.review_date && ch.review_date * 1000 < nowTick).length;

  // Auth gate — placed AFTER every hook above so the hook count is identical
  // across logged-out and logged-in renders (Rules of Hooks).
  if (!user) return <LoginScreen authErr={authErr} gsiReady={gsiReady} />;

  function handleCustomApply(from, to, label) {
    setCustomFrom(from);
    setCustomTo(to);
    setCustomLabel(label);
    setRange('custom');
    setShowDatePicker(false);
    fetchData('custom', from, to);
  }

  function handleRangeClick(r) {
    setRange(r);
    setCustomFrom(null);
    setCustomTo(null);
    setCustomLabel('');
    setShowDatePicker(false);
  }

  // Add an n-gram word as a PHRASE negative to its dominant campaign.
  const negativeWord = async (word, campaign) => {
    if(!campaign) { setNgramResult({ word, ok: false, error: 'No campaign found for this word' }); return; }
    if(!window.confirm(`Add "${word}" as a PHRASE negative to ${campaign}?\n\nThis blocks every search term containing "${word}" in that campaign. Make sure no converting terms use this word.`)) return;
    try {
      const res = await authFetch('/api/ads/add-negative', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ keyword: word, campaignName: campaign, matchType: 'PHRASE' }) });
      const json = await res.json();
      setNgramResult({ word, ...json });
      if(json.ok) {
        await logChangeQuiet(campaign, `Added "${word}" as phrase negative (n-gram systemic waste)`);
        fetchChanges();
      }
    } catch(e) { setNgramResult({ word, ok: false, error: e.message }); }
  };

  // ── Approval-gated one-click actions (all open ConfirmActionModal first) ──────
  const shortenCampaign = (n) => (n||'').replace('Back-up fixed terms campaign','Back-up').replace('Mental health - £59','MH £59');

  // Best-effort change-log write. The Google Ads mutation is the source of truth; a
  // failed audit-log entry must not crash the action flow or be reported as a failed write.
  const logChangeQuiet = async (campaign_or_page, notes, observation_days=14) => {
    try { await authFetch('/api/changes', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ change_type:'Google Ads', campaign_or_page, notes, observation_days }) }); }
    catch(e) { console.warn('change log failed:', e.message); }
  };

  const askPauseKeyword = ({ keyword, campaignName, adGroupName, matchType, description }) => {
    setConfirmModal({
      title: '🚫 Pause keyword', description, confirmLabel: 'Pause keyword', tone: 'red',
      onConfirm: async () => {
        const r = await authFetch('/api/ads/pause-keyword', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ campaignName, adGroupName, keywordText: keyword, matchType }) });
        const j = await r.json();
        if(j.ok) fetchChanges(); // backend auto-logs the change
        return j;
      }
    });
  };

  const askRaiseTcpa = ({ campaignName, current, next, description }) => {
    setConfirmModal({
      title: '⚙️ Raise target CPA', description, confirmLabel: `Raise to £${next}`, tone: 'amber',
      onConfirm: async () => {
        const r = await authFetch('/api/ads/update-tcpa', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ campaignName, newTcpa: next }) });
        const j = await r.json();
        if(j.ok) {
          await logChangeQuiet(campaignName, `Raised target CPA from £${Math.round(current)} to £${next}`);
          fetchChanges();
        }
        return j;
      }
    });
  };

  const askNegativeAll = (terms) => {
    const total = terms.reduce((s,t)=>s+(t.spend||0),0);
    setConfirmModal({
      title: '🚫 Negative all wasted terms',
      description: (<div>
        <div style={{marginBottom:8}}>Add these <b>{terms.length}</b> terms as negative keywords? Total wasted spend: <b>£{Math.round(total)}</b>.</div>
        <div style={{maxHeight:160,overflowY:'auto',fontSize:12,border:'1px solid #E5E7EB',borderRadius:6,padding:'6px 8px'}}>
          {terms.map((t,i)=>(<div key={i} style={{padding:'2px 0',color:COLORS.muted}}>"{t.keyword}" · {shortenCampaign(t.campaign)} · £{t.spend}</div>))}
        </div>
      </div>),
      confirmLabel: `Add ${terms.length} negatives`, tone: 'red',
      onConfirm: async (setProgress) => {
        let added=0, failed=0;
        for(let i=0;i<terms.length;i++){
          const t=terms[i];
          setProgress(`Adding ${i+1}/${terms.length}: "${t.keyword}"…`);
          try {
            const r = await authFetch('/api/ads/add-negative', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ keyword: t.keyword, campaignName: t.campaign }) });
            const j = await r.json();
            if(j.ok){ added++; await logChangeQuiet(t.campaign, `Added "${t.keyword}" as negative exact match keyword`); }
            else failed++;
          } catch(e){ failed++; }
          await new Promise(res=>setTimeout(res, 400)); // small delay between writes
        }
        fetchChanges();
        return { ok: added>0, message: `Added ${added} negative${added===1?'':'s'}${failed?`, ${failed} failed`:''}.` };
      }
    });
  };

  const m = data?.metrics || {};
  const charts = data?.charts || {};
  const subs = data?.subscriptions || {};
  const rpActive = subs.rpActive || 0;
  const rpCancelled = subs.rpCancelled || 0;
  const rpChurnRate = subs.rpChurnRate || 0;
  const roasByGroup = data?.roasByGroup || {};
  const mhFunnel = data?.mhFunnel || {};
  const ltv = data?.ltv || {};
  const comparison = data?.comparison || {};
  const health = data?.health || {};

  const revVsSpend = mergeChartData(charts.revenueByDay, "Revenue", charts.adSpendByDay, "Ad spend");
  const mrrData = (charts.mrrTrend || []).map(r => ({ date: r.date?.slice(5), MRR: Math.round(r.revenue) }));
  const sessionData = objToChartData(charts.sessionsByDay, "Sessions");
  const channelData = Object.entries(charts.sessionsByChannel || {}).map(([channel, sessions]) => ({ channel: channel.length > 14 ? channel.slice(0,14)+'…' : channel, sessions })).sort((a,b)=>b.sessions-a.sessions).slice(0,6);

  // Cohort chart data
  const cohortData = [
    { label: '1 year', gp: ltv.cohorts?.yr1?.avgLtv || 0, mh: ltv.mhCohort1yr?.avgLtv || 0 },
    { label: '2 years', gp: ltv.cohorts?.yr2?.avgLtv || 0, mh: 0 },
    { label: '3 years', gp: ltv.cohorts?.yr3?.avgLtv || 0, mh: 0 },
  ];
  // £19 repeat-prescription retention (censoring-correct survival curve)
  const r19 = ltv.mh19Retention || {};
  const r19Curve = (r19.curve || []).map(p => ({ month: `${p.month}mo`, pct: p.pct }));

  const rangeLabel = range === 'yd' ? 'yesterday' : range === '1d' ? 'today' : range === 'custom' ? customLabel : `last ${range}`;
  const prevLabel = range === 'yd' ? 'day before' : range === '1d' ? 'yesterday' : range === 'custom' ? 'previous period' : `previous ${range}`;

  return (
    <div style={{ maxWidth: 1140, margin: "0 auto", padding: "1.5rem 1rem", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", background: COLORS.bg, minHeight: "100vh" }}>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .metrics-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px; margin-bottom: 1.25rem; }
        .charts-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 1.25rem; }
        .range-btn { padding: 5px 14px; border: 0.5px solid rgba(0,0,0,0.2); border-radius: 8px; background: transparent; font-size: 12px; cursor: pointer; color: #888780; transition: all 0.15s; }
        .range-btn:hover { background: #EFEFEB; }
        .range-btn.active { background: #EFEFEB; color: #1a1a1a; font-weight: 500; }
        .range-btn.yd.active { background: #534AB7; color: #fff; border-color: #534AB7; }
        .range-btn.custom-btn { border-color: #0EA5A0; color: #0EA5A0; }
        .range-btn.custom-btn.active { background: #0EA5A0; color: #fff; }
        .tab-btn { padding: 6px 14px; border: none; border-radius: 8px; background: transparent; font-size: 13px; cursor: pointer; color: #888780; transition: all 0.15s; }
        .tab-btn:hover { color: #1a1a1a; }
        .tab-btn.active { background: #fff; color: #1a1a1a; font-weight: 500; box-shadow: 0 1px 4px rgba(0,0,0,0.08); }
        @media (max-width: 640px) { .charts-2col { grid-template-columns: 1fr; } }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 600, color: COLORS.text }}>tapGP Dashboard</h1>
          {(() => {
            // Freshness from the SERVER snapshot timestamp (not the fetch time). nowTick
            // is referenced so this re-evaluates every 60s.
            void nowTick;
            const ts = serverUpdated ? new Date(serverUpdated).getTime() : null;
            if(!ts) return lastUpdated ? <p style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>Updated {lastUpdated}</p> : null;
            const ageH = (Date.now() - ts) / 3600000;
            const color = ageH > 4 ? COLORS.red : ageH > 2 ? COLORS.orange : COLORS.muted;
            return <p style={{ fontSize: 11, color, marginTop: 2 }}>Updated {timeAgo(ts)}{ageH > 2 ? ' · data may be stale' : ''}</p>;
          })()}
          {health.lastCheck && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4 }}>
              {[{label:'Ads',ok:health.googleAds},{label:'GA4',ok:health.ga4},{label:'Stripe',ok:health.stripe},{label:'WC',ok:health.woocommerce}].map(s => (
                <HealthDot key={s.label} ok={s.ok} label={s.label} />
              ))}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {RANGES.map(r => (
            <button key={r} className={`range-btn ${r} ${range === r ? "active" : ""}`} onClick={() => handleRangeClick(r)}>
              {RANGE_LABELS[r]}
            </button>
          ))}
          {/* Custom date range button */}
          <div style={{ position: 'relative' }}>
            <button className={`range-btn custom-btn ${range === 'custom' ? 'active' : ''}`} onClick={() => setShowDatePicker(!showDatePicker)}>
              {range === 'custom' && customLabel ? customLabel : '📅 Custom'}
            </button>
            {showDatePicker && (
              <DateRangePicker
                onApply={handleCustomApply}
                onCancel={() => setShowDatePicker(false)}
                initialStart={customFrom ? new Date(customFrom*1000).toISOString().split('T')[0] : ''}
                initialEnd={customTo ? new Date(customTo*1000).toISOString().split('T')[0] : ''}
              />
            )}
          </div>
          <button className="range-btn" onClick={() => fetchData(range, customFrom, customTo)} style={{ marginLeft: 4 }}>↻</button>
          {/* Alerts bell */}
          <div style={{ position: 'relative', marginLeft: 4 }}>
            <button className="range-btn" onClick={() => setShowAlerts(s => !s)} style={{ position: 'relative' }}>
              🔔
              {alerts.length > 0 && <span style={{ position:'absolute', top:-4, right:-4, background:COLORS.red, color:'#fff', borderRadius:10, fontSize:9, fontWeight:700, padding:'1px 5px', minWidth:14, textAlign:'center' }}>{alerts.length}</span>}
            </button>
            {showAlerts && (() => {
              // One body, two shells — identical treatment to DateRangePicker. A 320px panel anchored
              // right:0 only fits today because the bell is the RIGHTMOST control; it clips on a 320px
              // device, and would clip at any width the moment another control is added to its right.
              const alertsBody = (
                <>
                  <div style={{ fontSize:12, fontWeight:600, marginBottom:8, color:COLORS.text }}>Alerts {alerts.length>0?`(${alerts.length})`:''}</div>
                  {alerts.length === 0 ? <div style={{ fontSize:12, color:COLORS.muted, padding:'8px 0' }}>✓ Nothing needs attention.</div> : alerts.map((a, i) => {
                    const c = a.severity === 'alert' ? COLORS.red : a.severity === 'warning' ? COLORS.orange : COLORS.blue;
                    return <div key={i} style={{ display:'flex', gap:8, padding:'7px 0', borderBottom: i<alerts.length-1?'1px solid #F3F4F6':'none' }}>
                      <span style={{ width:6, height:6, borderRadius:'50%', background:c, marginTop:5, flexShrink:0 }} />
                      <span style={{ fontSize:12, color:COLORS.text, lineHeight:1.4 }}>{a.message}</span>
                    </div>;
                  })}
                </>
              );
              if(isNarrowViewport) return (
                <div onClick={() => setShowAlerts(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
                  <div onClick={e => e.stopPropagation()} style={{ background:COLORS.card, border:`0.5px solid ${COLORS.border}`, borderRadius:12, padding:'0.75rem', boxShadow:'0 8px 32px rgba(0,0,0,0.12)', width:'90%', maxWidth:420, maxHeight:'90vh', overflowY:'auto', textAlign:'left' }}>
                    {alertsBody}
                  </div>
                </div>
              );
              return (
                <div style={{ position:'absolute', top:'100%', right:0, zIndex:100, background:COLORS.card, border:`0.5px solid ${COLORS.border}`, borderRadius:12, padding:'0.75rem', boxShadow:'0 8px 32px rgba(0,0,0,0.12)', width:320, marginTop:6, textAlign:'left' }}>
                  {alertsBody}
                </div>
              );
            })()}
          </div>
          <button className="range-btn" onClick={signOut} style={{ marginLeft: 8, color: COLORS.red }}>Sign out</button>
          {user && <img src={user.picture} alt={user.name} style={{ width:28, height:28, borderRadius:"50%", marginLeft:8 }} />}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, background: COLORS.metric, borderRadius: 10, padding: 4, marginBottom: "1.25rem", width: "fit-content", flexWrap: "wrap" }}>
        {TABS.map(t => (
          <button key={t} className={`tab-btn ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>{t}{t === 'Ads Intelligence' && adsIntel?.anomalies?.length > 0 ? <span title="anomalies" style={{background:COLORS.red,color:'#fff',borderRadius:10,fontSize:10,padding:'1px 5px',marginLeft:4}}>{adsIntel.anomalies.length}</span> : null}{t === 'Ads Intelligence' && overdueReviews > 0 ? <span title="overdue change reviews" style={{background:COLORS.orange,color:'#fff',borderRadius:10,fontSize:10,padding:'1px 5px',marginLeft:4}}>⏰{overdueReviews}</span> : null}</button>
        ))}
      </div>

      {/* Comparison banner */}
      {comparison?.revenue && comparison.revenue.previous > 0 && (
        <div style={{ background: COLORS.card, border: `0.5px solid ${COLORS.border}`, borderRadius: 10, padding: "10px 16px", marginBottom: "1rem", fontSize: 12, color: COLORS.muted, display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center" }}>
          <span>vs {prevLabel}:</span>
          <span>Revenue {fmt.gbp(comparison.revenue.previous)} <ChangeIndicator change={comparison.revenue.change} /></span>
          <span>New customers {fmt.num(comparison.newCustomers?.previous)} <ChangeIndicator change={comparison.newCustomers?.change} /></span>
        </div>
      )}

      {error && <div style={{ background: "#FCEBEB", border: `0.5px solid #F09595`, borderRadius: 10, padding: "12px 16px", marginBottom: "1rem", fontSize: 13, color: "#A32D2D" }}>⚠ Could not fetch data: {error}</div>}
      {loading && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(248,247,244,0.8)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
          <div style={{ width: 32, height: 32, border: `3px solid ${COLORS.metric}`, borderTop: `3px solid ${COLORS.blue}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <div style={{ fontSize: 13, color: COLORS.muted }}>{range === 'custom' ? 'Building custom report…' : 'Loading…'}</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Assistant tab renders independently of dashboard `data` (read-only chat). */}
      {tab === "Assistant" && (
        <div style={{maxWidth:760,margin:'0 auto'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10,flexWrap:'wrap',gap:8}}>
            <div style={{fontSize:13,color:COLORS.muted}}>Ask about the account — campaigns, keywords, search terms, spend, CPA, pacing, the change log. It can also make safe changes: add keywords, supplement RSA headlines, and build new ad groups (those always ask you to confirm first). Every change is logged.</div>
            {asstMessages.length>0 && <button onClick={clearAssistant} style={{fontSize:12,padding:'4px 10px',borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer',whiteSpace:'nowrap'}}>Clear chat</button>}
          </div>
          <div style={{border:'1px solid #E5E7EB',borderRadius:10,background:'#fff',display:'flex',flexDirection:'column',height:'min(70vh,560px)'}}>
            <div style={{flex:1,overflowY:'auto',padding:14,display:'flex',flexDirection:'column',gap:10}}>
              {asstMessages.length===0 && !asstLoading && (
                <div style={{margin:'auto',textAlign:'center',color:COLORS.muted,fontSize:13,maxWidth:420}}>
                  <div style={{fontSize:28,marginBottom:8}}>💬</div>
                  <div style={{marginBottom:12}}>Hi — I'm the tapGP Ads Assistant. I can read and explain your account in depth.</div>
                  <button onClick={()=>sendAssistant('What can you do?')} style={{fontSize:13,padding:'8px 14px',borderRadius:8,border:'1px solid '+COLORS.purple,background:'#F5F3FF',color:COLORS.purple,cursor:'pointer',fontWeight:600}}>What can you do?</button>
                </div>
              )}
              {asstMessages.map((m,i)=>(
                <div key={i} style={{display:'flex',flexDirection:'column',alignItems:m.role==='user'?'flex-end':'flex-start'}}>
                  <div style={{maxWidth:'85%',padding:'8px 12px',borderRadius:12,fontSize:13,lineHeight:1.5,whiteSpace:m.role==='user'?'pre-wrap':'normal',wordBreak:'break-word',background:m.role==='user'?COLORS.purple:'#F3F4F6',color:m.role==='user'?'#fff':COLORS.text}}>{m.role==='user'?m.content:<MarkdownMessage text={m.content}/>}</div>
                  {m.role==='assistant' && Array.isArray(m.actions) && m.actions.length>0 && (
                    <div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:4,maxWidth:'85%'}}>
                      {m.actions.map((a,j)=>(
                        <span key={j} title={a.write?'Action taken':'Data read'} style={{fontSize:11,padding:'2px 7px',borderRadius:10,border:'1px solid '+(a.write?COLORS.purple:'#E5E7EB'),background:a.write?'#F5F3FF':'#fff',color:a.write?COLORS.purple:COLORS.muted,fontWeight:a.write?600:400}}>{a.write?'⚡ ':'🔍 '}{a.label||a.name}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {asstLoading && (
                <div style={{display:'flex',justifyContent:'flex-start'}}>
                  <div style={{padding:'8px 12px',borderRadius:12,fontSize:13,background:'#F3F4F6',color:COLORS.muted}}>Thinking…</div>
                </div>
              )}
              <div ref={asstEndRef} />
            </div>
            <div style={{borderTop:'1px solid #E5E7EB',padding:10,display:'flex',gap:8}}>
              <textarea value={asstInput} onChange={e=>setAsstInput(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendAssistant();}}} placeholder="Ask about your account…" rows={1} style={{flex:1,resize:'none',padding:'9px 12px',borderRadius:8,border:'1px solid #E5E7EB',fontSize:13,fontFamily:'inherit',maxHeight:120,boxSizing:'border-box'}} />
              <button onClick={()=>sendAssistant()} disabled={asstLoading||!asstInput.trim()} style={{padding:'0 16px',borderRadius:8,border:'none',background:(asstLoading||!asstInput.trim())?'#C4B5FD':COLORS.purple,color:'#fff',cursor:(asstLoading||!asstInput.trim())?'default':'pointer',fontWeight:600,fontSize:13}}>Send</button>
            </div>
          </div>
        </div>
      )}

      {data && (
        <>
          {/* ── OVERVIEW ── */}
          {tab === "Overview" && (
            <>
              <div className="metrics-grid">
                <MetricCard label="Total revenue" value={fmt.gbp(m.revenue)} change={comparison?.revenue?.change} />
                <MetricCard label="MRR" value={fmt.gbp(m.mrr)} />
                <MetricCard label="Ad spend" value={fmt.gbp(m.adSpend)} inverseChange />
                <MetricCard label="ROAS" value={fmt.x(m.roas)} />
                <MetricCard label="Active subs" value={fmt.num(m.activeSubs)} />
                <MetricCard label="CAC" value={fmt.gbp(m.cac)} sub={(m.cacGpNewCustomers != null || m.cacMhNewCustomers != null) ? `GP ${m.cacGpNewCustomers ? fmt.gbp(m.gpCac) : '—'} · MH ${m.cacMhNewCustomers ? fmt.gbp(m.mhCac) : '—'} · ${m.cacWindow || 'range'} · blended w/ organic${m.cacLowSample ? ' · ⚠ thin sample' : ''}` : undefined} inverseChange />
                <MetricCard label="Churn rate" value={fmt.pct(m.churnRate)} inverseChange />
                <MetricCard label="Net cash flow" value={fmt.gbp(m.netCashflow)} color={m.netCashflow > 0 ? COLORS.green : COLORS.red} />
                {m.mhTrueCpa != null && (
                  <MetricCard
                    label="MH CPA (Stripe)"
                    value={fmt.gbp(m.mhTrueCpa)}
                    color={m.mhTargetCpa ? (m.mhTrueCpa <= m.mhTargetCpa ? COLORS.green : COLORS.red) : COLORS.text}
                    sub={(m.mhReportedCpa ? 'vs £'+m.mhReportedCpa+' Google · ' : '') + (m.mhTargetCpa ? 'target £'+m.mhTargetCpa+' · ' : '') + (m.mhStripePurchases||0)+' purchases · blended'}
                  />
                )}
              </div>
              <div className="charts-2col">
                <SectionCard title="Revenue vs ad spend">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={revVsSpend} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: COLORS.muted }} />
                      <YAxis tick={{ fontSize: 10, fill: COLORS.muted }} tickFormatter={v => `£${v >= 1000 ? (v/1000).toFixed(0)+"k" : v}`} />
                      <Tooltip formatter={(v, n) => [fmt.gbp(v), n]} />
                      <Bar dataKey="Revenue" fill={COLORS.blue} radius={[3,3,0,0]} />
                      <Bar dataKey="Ad spend" fill={COLORS.orange} radius={[3,3,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </SectionCard>
                <SectionCard title="MRR trend">
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={mrrData} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: COLORS.muted }} />
                      <YAxis tick={{ fontSize: 10, fill: COLORS.muted }} tickFormatter={v => `£${v >= 1000 ? (v/1000).toFixed(0)+"k" : v}`} />
                      <Tooltip formatter={(v) => [fmt.gbp(v), "MRR"]} />
                      <Area type="monotone" dataKey="MRR" stroke={COLORS.green} fill="rgba(29,158,117,0.1)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </SectionCard>
              </div>
              <div className="charts-2col">
                <SectionCard title="Cash flow summary">
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {[
                      { label: "Stripe revenue", value: fmt.gbp(m.revenue), color: COLORS.green },
                      { label: "Ad spend", value: `-${fmt.gbp(m.adSpend)}`, color: COLORS.red },
                      { label: "Stripe fees", value: `-${fmt.gbp(m.stripeFees)}`, color: "#BA7517" },
                      { label: "Net cash flow", value: fmt.gbp(m.netCashflow), color: m.netCashflow > 0 ? COLORS.green : COLORS.red },
                    ].map(item => (
                      <div key={item.label} style={{ background: COLORS.metric, borderRadius: 8, padding: "12px 14px" }}>
                        <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 4 }}>{item.label}</div>
                        <div style={{ fontSize: 18, fontWeight: 500, color: item.color }}>{item.value}</div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
                <SectionCard title="MH funnel snapshot" badge="live">
                  {mhFunnel.total59 !== undefined && mhFunnel.total59 !== null ? (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                        {[
                          { label: "New intakes (30d)", value: fmt.num(mhFunnel.recentIntake || 0), color: COLORS.blue },
                          { label: "Active £19 plan", value: fmt.num(mhFunnel.totalActive19 || 0), color: COLORS.green },
                          { label: "£59 → £19 conversion", value: (mhFunnel.conversionRate59 || 0) + "%", color: COLORS.purple, sub: (() => { const k = (mhFunnel.km59 || []).find(x => x.weeks === 13); return k ? `KM est. @13wk · ${k.ciLow}–${k.ciHigh}% · ${k.events} conv` : `${mhFunnel.converted59 || 0} of ${mhFunnel.eligible59 || 0} eligible`; })() },
                          { label: "£189 → £19 conversion", value: (mhFunnel.conversionRate189 || 0) + "%", color: COLORS.teal, sub: `${mhFunnel.converted189 || 0} of ${mhFunnel.eligible189 || 0} eligible` },
                        ].map(item => (
                          <div key={item.label} style={{ background: COLORS.metric, borderRadius: 8, padding: "10px 12px" }}>
                            <div style={{ fontSize: 10, color: COLORS.muted }}>{item.label}</div>
                            <div style={{ fontSize: 18, fontWeight: 600, color: item.color }}>{item.value}</div>
                            {item.sub && <div style={{ fontSize: 10, color: COLORS.muted, marginTop: 2 }}>{item.sub}</div>}
                          </div>
                        ))}
                      </div>
                      <div style={{ fontSize: 11, color: COLORS.muted }}>{fmt.num(mhFunnel.totalPatients || 0)} MH patients total · £59 rate is a Kaplan-Meier estimate; £189 uses an 8-week window</div>
                    </>
                  ) : <div style={{ fontSize: 13, color: COLORS.muted, padding: "1rem 0" }}>MH tracker not connected</div>}
                </SectionCard>
              </div>
              {m.cacLowSample && m.cacSampleNote && (
                <div style={{ background: "rgba(239,68,68,0.08)", border: `0.5px solid ${COLORS.red}`, borderRadius: 8, padding: "8px 14px", marginBottom: "1rem", fontSize: 12, color: "#7A1010" }}>
                  ⚠ {m.cacSampleNote}
                </div>
              )}
            </>
          )}

          {/* ── ROAS ── */}
          {tab === "ROAS" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: "1.25rem" }}>
                <ROASGroupCard label="GP Campaigns" revenue={roasByGroup.gp?.revenue || 0} spend={roasByGroup.gp?.spend || 0} roas={roasByGroup.gp?.roas || 0} color={COLORS.blue} />
                <ROASGroupCard label="Mental Health Campaigns" revenue={roasByGroup.mh?.revenue || 0} spend={roasByGroup.mh?.spend || 0} roas={roasByGroup.mh?.roas || 0} color={COLORS.purple} />
              </div>
              <div className="charts-2col">
                <SectionCard title="Spend by campaign">
                  {Object.entries(charts.spendByCampaign || {}).length > 0 ? (
                    <div>{Object.entries(charts.spendByCampaign || {}).sort(([,a],[,b]) => (b?.spend||b)-(a?.spend||a)).slice(0,8).map(([campaign, spend], i) => (
                      <PlanBar key={campaign} name={spend?.status && spend.status !== 'ENABLED' ? `${campaign} (${String(spend.status).toLowerCase()})` : campaign} count={Math.round(spend?.spend||spend)} total={Object.values(charts.spendByCampaign).reduce((s,v)=>s+(v?.spend||v),0)} color={planColors[i % planColors.length]} />
                    ))}</div>
                  ) : <div style={{ fontSize: 13, color: COLORS.muted }}>No campaign data</div>}
                </SectionCard>
                <SectionCard title="Overall metrics">
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <MetricCard label="Total ROAS" value={fmt.x(m.roas)} />
                    <MetricCard label="CAC" value={fmt.gbp(m.cac)} sub={`${m.cacWindow || 'selected range'} · blended${m.cacLowSample ? ' · ⚠ thin sample' : ''}`} />
                    <MetricCard label="New customers" value={fmt.num(m.newCustomers)} change={comparison?.newCustomers?.change} />
                    <MetricCard label="Total ad spend" value={fmt.gbp(m.adSpend)} />
                  </div>
                </SectionCard>
              </div>
              {m.cacLowSample && m.cacSampleNote && (
                <div style={{ background: "rgba(239,68,68,0.08)", border: `0.5px solid ${COLORS.red}`, borderRadius: 8, padding: "8px 14px", marginBottom: "1rem", fontSize: 12, color: "#7A1010" }}>
                  ⚠ {m.cacSampleNote}
                </div>
              )}
            </>
          )}

          {/* ── LTV ── */}
          {tab === "LTV" && (
            <>
              {/* CAC caveat */}
              <div style={{ background: "rgba(239,159,39,0.1)", border: `0.5px solid ${COLORS.orange}`, borderRadius: 8, padding: "8px 14px", marginBottom: "1rem", fontSize: 12, color: "#7A5010" }}>
                ⚠ CAC (£{m.cac}, {m.cacWindow || 'selected range'}) follows the selected time range and includes organic customers — true paid CAC is higher. LTV:CAC is per-service (each service's LTV ÷ its own CAC). Payback periods shown are therefore optimistic estimates.
              </div>

              {/* GP and MH LTV cards */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: "1.25rem" }}>
                <LTVGroupCard
                  label="GP Patients"
                  ltv={ltv.gpLtv || 0}
                  payments={ltv.gpPayments || 0}
                  customers={ltv.gpCustomers || 0}
                  ltvCac={ltv.gpLtvCac || '—'}
                  payback={ltv.gpPayback}
                  avgFirst={ltv.gpAvgFirstPayment}
                  color={COLORS.blue}
                />
                <LTVGroupCard
                  label="MH Patients"
                  ltv={ltv.mhLtv || 0}
                  payments={ltv.mhPayments || 0}
                  customers={ltv.mhCustomers || 0}
                  ltvCac={ltv.mhLtvCac || '—'}
                  payback={ltv.mhPayback}
                  avgFirst={ltv.mhAvgFirstPayment}
                  color={COLORS.purple}
                />
              </div>

              <div className="charts-2col">
                {/* Cohort LTV chart */}
                <SectionCard title="GP cohort LTV by tenure" badge="GP only — MH product <1yr old">
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={cohortData} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: COLORS.muted }} />
                      <YAxis tick={{ fontSize: 10, fill: COLORS.muted }} tickFormatter={v => `£${v}`} />
                      <Tooltip formatter={(v, n) => [fmt.gbp(v), n]} />
                      <Bar dataKey="gp" name="GP LTV" fill={COLORS.blue} radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 8 }}>
                    LTV is relatively flat across cohorts (£51-£60) due to historical lower pricing. Most value comes from pricing increases not repeat bookings.
                  </div>
                </SectionCard>

                {/* Key retention metrics */}
                <SectionCard title="Retention insights">
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                    <div style={{ background: COLORS.metric, borderRadius: 8, padding: "12px 14px" }}>
                      <div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 4 }}>Repeat rate</div>
                      <div style={{ fontSize: 22, fontWeight: 600, color: COLORS.text }}>{ltv.repeatRate}%</div>
                      <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>of patients ever return</div>
                    </div>
                    <div style={{ background: COLORS.metric, borderRadius: 8, padding: "12px 14px" }}>
                      <div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 4 }}>Avg days to return</div>
                      <div style={{ fontSize: 22, fontWeight: 600, color: COLORS.text }}>{ltv.avgDaysToReturn}</div>
                      <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>days after first appointment</div>
                    </div>
                    <div style={{ background: "rgba(29,158,117,0.08)", borderRadius: 8, padding: "12px 14px", border: `0.5px solid ${COLORS.green}` }}>
                      <div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 4 }}>Optimal follow-up</div>
                      <div style={{ fontSize: 22, fontWeight: 600, color: COLORS.green }}>{ltv.optimalFollowUpDays} days</div>
                      <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>send nudge before day 21</div>
                    </div>
                    <div style={{ background: COLORS.metric, borderRadius: 8, padding: "12px 14px" }}>
                      <div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 4 }}>Top 10% revenue share</div>
                      <div style={{ fontSize: 22, fontWeight: 600, color: COLORS.text }}>{ltv.top10Pct}%</div>
                      <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>relatively healthy spread</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.muted, lineHeight: 1.7, padding: "8px 12px", background: COLORS.metric, borderRadius: 8 }}>
                    <strong style={{ color: COLORS.text }}>Key insight:</strong> GP patients average 1.7 appointments regardless of tenure. MH patients average 3.6 payments. The biggest growth lever is converting one-time GP patients into repeat bookers via follow-up automation.
                  </div>
                </SectionCard>
              </div>

              {/* MH 1yr cohort */}
              {ltv.mhCohort1yr?.avgLtv > 0 && (
                <SectionCard title="MH 1-year cohort LTV">
                  <div style={{ display: "flex", gap: 10 }}>
                    <div style={{ background: COLORS.metric, borderRadius: 8, padding: "12px 14px", flex: 1 }}>
                      <div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 4 }}>1yr MH LTV</div>
                      <div style={{ fontSize: 22, fontWeight: 600, color: COLORS.purple }}>{fmt.gbp(ltv.mhCohort1yr.avgLtv)}</div>
                      <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>{fmt.num(ltv.mhCohort1yr.customers)} customers</div>
                    </div>
                    <div style={{ background: COLORS.metric, borderRadius: 8, padding: "12px 14px", flex: 1 }}>
                      <div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 4 }}>vs GP 1yr LTV</div>
                      <div style={{ fontSize: 22, fontWeight: 600, color: COLORS.blue }}>{fmt.gbp(ltv.cohorts?.yr1?.avgLtv || 0)}</div>
                      <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>{ltv.mhCohort1yr.avgLtv > 0 && ltv.cohorts?.yr1?.avgLtv > 0 ? `MH is ${((ltv.mhCohort1yr.avgLtv / ltv.cohorts.yr1.avgLtv)).toFixed(1)}x higher` : ''}</div>
                    </div>
                  </div>
                </SectionCard>
              )}

              {/* £19 Repeat Prescription Retention (censoring-correct survival curve) */}
              {Array.isArray(r19.kmSurvival) && r19.kmSurvival.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.muted, marginBottom: 4 }}>
                    £19 plan retention — Kaplan-Meier (censoring-correct), {fmt.num(r19.kmSubscribers)} subscribers · {fmt.num(r19.kmChurned)} churned · {fmt.num(r19.kmActive)} still active
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
                      <thead><tr>{['Horizon','Still subscribed','95% CI','At risk','Churn events','Confidence'].map(h => (
                        <th key={h} style={{ border: '1px solid #E5E7EB', padding: '4px 8px', background: '#F3F4F6', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>))}</tr></thead>
                      <tbody>{r19.kmSurvival.map(k => (
                        <tr key={k.months}>
                          <td style={{ border: '1px solid #E5E7EB', padding: '4px 8px' }}>{k.months} months</td>
                          <td style={{ border: '1px solid #E5E7EB', padding: '4px 8px', fontWeight: 600 }}>{k.pct}%</td>
                          <td style={{ border: '1px solid #E5E7EB', padding: '4px 8px', color: COLORS.muted }}>{k.ciLow}–{k.ciHigh}%</td>
                          <td style={{ border: '1px solid #E5E7EB', padding: '4px 8px' }}>{k.atRisk}</td>
                          <td style={{ border: '1px solid #E5E7EB', padding: '4px 8px' }}>{k.events}</td>
                          <td style={{ border: '1px solid #E5E7EB', padding: '4px 8px', color: k.reliability === 'good' ? COLORS.green : k.reliability === 'indicative' ? COLORS.orange : COLORS.red }}>
                            {k.reliability === 'good' ? 'good' : k.reliability === 'indicative' ? 'indicative (n<30)' : 'weak (n<10)'}
                          </td>
                        </tr>))}</tbody>
                    </table>
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 6 }}>{r19.kmNote}</div>
                </div>
              )}
              {r19.totalCustomers > 0 && (
                <SectionCard title="£19 Repeat Prescription Retention" badge="MH plan · charge-based">
                  <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                    <div style={{ background: "rgba(139,92,246,0.08)", borderRadius: 8, padding: "12px 14px", flex: 1, border: `0.5px solid ${COLORS.purple}` }}>
                      <div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 4 }}>Median retention</div>
                      <div style={{ fontSize: 22, fontWeight: 600, color: COLORS.purple }}>{r19.kmMedianMonths ?? r19.medianMonths} months</div>
                      <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>≥50% still subscribed</div>
                    </div>
                    <div style={{ background: COLORS.metric, borderRadius: 8, padding: "12px 14px", flex: 1 }}>
                      <div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 4 }}>Aged cohort avg</div>
                      <div style={{ fontSize: 22, fontWeight: 600, color: COLORS.text }}>{r19.agedCohortMean} mo</div>
                      <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>joined ≥12mo ago ({fmt.num(r19.agedCohortSize)})</div>
                    </div>
                    <div style={{ background: COLORS.metric, borderRadius: 8, padding: "12px 14px", flex: 1 }}>
                      <div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 4 }}>Naive population avg</div>
                      <div style={{ fontSize: 22, fontWeight: 600, color: COLORS.muted }}>{r19.naivePopulationMean} mo</div>
                      <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>understated by recency</div>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={180}>
                    <AreaChart data={r19Curve} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: COLORS.muted }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: COLORS.muted }} tickFormatter={v => `${v}%`} />
                      <Tooltip formatter={v => [`${v}%`, 'Still subscribed']} />
                      <Area type="monotone" dataKey="pct" name="Still subscribed" stroke={COLORS.purple} fill={COLORS.purple} fillOpacity={0.15} />
                    </AreaChart>
                  </ResponsiveContainer>
                  <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 8, lineHeight: 1.7 }}>
                    <strong style={{ color: COLORS.text }}>Median ~{r19.kmMedianMonths ?? r19.medianMonths} months (Kaplan-Meier).</strong> The aged cohort (joined ≥12mo ago) averages {r19.agedCohortMean} months — but a naive "average tenure of current subscribers" reads just {r19.naivePopulationMean} months, because recent joiners haven't had time to prove out. The curve is censoring-correct (each month counts only subscribers old enough to have reached it). Churn = charges stopped (charge-based plan — a deliberate cancel can't be told apart from a card failure). ⚠ The plan is only ~{r19.productAgeMonths} months old, so retention beyond 12 months isn't yet observable — the true long-run figure could be higher.
                  </div>
                </SectionCard>
              )}
            </>
          )}

          {/* ── SUBSCRIPTIONS ── */}
          {tab === "Subscriptions" && (
            <>
              <div className="metrics-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
                <MetricCard label="Active subs" value={fmt.num(m.activeSubs)} />
                <MetricCard label="MRR" value={fmt.gbp(m.mrr)} />
                <MetricCard label="Monthly churn" value={fmt.pct(m.churnRate)} inverseChange />
                <MetricCard label="Cancelled (period)" value={fmt.num(subs.totalCancelled)} />
              </div>
              <div className="metrics-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)", marginTop: 12 }}>
                <MetricCard label="£19 plan — active" value={fmt.num(rpActive)} />
                <MetricCard label="£19 plan — cancelled" value={fmt.num(rpCancelled)} />
                <MetricCard label="£19 plan — monthly churn" value={fmt.pct(rpChurnRate)} inverseChange />
              </div>
              <div className="charts-2col">
                <SectionCard title="Active subscriptions by plan">
                  {(subs.byPlan||[]).length > 0 ? (
                    <div>{(() => {
                      const merged = [];
                      let plan19Count = rpActive;
                      (subs.byPlan||[]).forEach((plan, i) => {
                        if (plan.plan_name === 'repeat prescription' || plan.plan_name === 'Ongoing prescription') return;
                        merged.push({ ...plan, idx: i });
                      });
                      const total19 = plan19Count + merged.reduce((s,p)=>s+p.count,0);
                      return [
                        <PlanBar key="plan19" name="£19 plan" count={plan19Count} total={total19} color={planColors[0]} />,
                        ...merged.map((plan, i) => <PlanBar key={plan.plan_name} name={plan.plan_name} count={plan.count} total={total19} color={planColors[i+1 % planColors.length]} />)
                      ];
                    })()}</div>
                  ) : <div style={{ fontSize: 13, color: COLORS.muted }}>No subscription data</div>}
                </SectionCard>
                <SectionCard title="Cancellations by plan" badge={range}>
                  {(subs.churnByPlan||[]).length > 0 ? (
                    <div>{(subs.churnByPlan||[]).map((plan, i) => <PlanBar key={plan.plan_name} name={plan.plan_name} count={plan.count} total={(subs.churnByPlan||[]).reduce((s,p)=>s+p.count,0)} color={COLORS.red} />)}</div>
                  ) : <div style={{ fontSize: 13, color: COLORS.muted }}>No cancellations in this period</div>}
                </SectionCard>
              </div>
              <SectionCard title="MRR trend">
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={mrrData} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: COLORS.muted }} />
                    <YAxis tick={{ fontSize: 10, fill: COLORS.muted }} tickFormatter={v => `£${v >= 1000 ? (v/1000).toFixed(0)+"k" : v}`} />
                    <Tooltip formatter={(v) => [fmt.gbp(v), "MRR"]} />
                    <Area type="monotone" dataKey="MRR" stroke={COLORS.green} fill="rgba(29,158,117,0.1)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </SectionCard>
            </>
          )}

          {/* ── MH FUNNEL ── */}
          {tab === "MH Funnel" && (
          <div style={{display:'flex',flexDirection:'column',gap:16}}>
            {/* £59 website funnel (GA4): the only section on this tab that follows the date range */}
            <SectionCard title="£59 website funnel (GA4)" badge={rangeLabel}>
              <Mh59WebFunnel steps={charts.mh59Funnel} rangeLabel={rangeLabel} siteChanges={mh59SiteChanges}
                onShowDays={(fromDay, toDay) => handleCustomApply(Math.floor(Date.parse(fromDay) / 1000), Math.floor(new Date(toDay + 'T23:59:59').getTime() / 1000), `${fromDay} → ${toDay}`)} />
            </SectionCard>
            {/* weekly trend: a fixed 26-week window with the Change Log's £59 website changes marked */}
            <SectionCard title="£59 funnel: weekly trend (GA4)" badge="last 26 complete weeks">
              <Mh59WeeklyTrend trend={mh59Trend} />
            </SectionCard>
            {mhFunnel.total59 !== undefined ? (<>
              {/* Key metrics row */}
              <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
                <MetricCard label="New (last 30 days)" value={mhFunnel.recent59 + mhFunnel.recent189} sub="£59 + £189 intakes" />
                <MetricCard label="Total active £19 plan" value={mhFunnel.totalActive19} sub="paid in last 35 days" color={COLORS.green} />
              </div>

              {/* £59 track */}
              <SectionCard title="£59 Initial Consultation Track">
                <div style={{display:'flex',gap:12,flexWrap:'wrap',marginBottom:8}}>
                  <MetricCard label="Total since Apr 2026" value={mhFunnel.total59} />
                  <MetricCard label="New (last 30 days)" value={mhFunnel.recent59} />
                  <MetricCard label="Eligible (13+ weeks)" value={mhFunnel.eligible59} sub="past the 13-week gate" />
                  <MetricCard label="Converted to £19 plan" value={mhFunnel.converted59} color={COLORS.green} />
                  <MetricCard label="Still to convert" value={mhFunnel.actionable59 ?? '—'} sub="eligible, not on £19" />
                  <MetricCard label="Conversion (KM @13wk)" value={mhFunnel.conversionRate59+'%'} color={parseFloat(mhFunnel.conversionRate59)>=10?COLORS.green:COLORS.orange} sub={(() => { const k=(mhFunnel.km59||[]).find(x=>x.weeks===13); return k? `95% CI ${k.ciLow}–${k.ciHigh}%` : 'Kaplan-Meier estimate'; })()} />
                </div>
                {Array.isArray(mhFunnel.km59) && mhFunnel.km59.length > 0 && (
                  <div style={{marginTop:10}}>
                    <div style={{fontSize:11,fontWeight:600,color:COLORS.muted,marginBottom:4}}>Cumulative conversion by weeks since first £59 — Kaplan-Meier (censoring-correct), full cohort of {mhFunnel.km59CohortSize} patients</div>
                    <div style={{overflowX:'auto'}}>
                      <table style={{borderCollapse:'collapse',fontSize:12,width:'100%'}}>
                        <thead><tr>{['Horizon','Converted %','95% CI','Still at risk','Conversions','Confidence'].map(h=>(
                          <th key={h} style={{border:'1px solid #E5E7EB',padding:'4px 8px',background:'#F3F4F6',textAlign:'left',fontWeight:600,whiteSpace:'nowrap'}}>{h}</th>))}</tr></thead>
                        <tbody>{mhFunnel.km59.map(k=>(
                          <tr key={k.weeks}>
                            <td style={{border:'1px solid #E5E7EB',padding:'4px 8px'}}>{k.weeks} weeks</td>
                            <td style={{border:'1px solid #E5E7EB',padding:'4px 8px',fontWeight:600}}>{k.cumulativePct}%</td>
                            <td style={{border:'1px solid #E5E7EB',padding:'4px 8px',color:COLORS.muted}}>{k.ciLow}–{k.ciHigh}%</td>
                            <td style={{border:'1px solid #E5E7EB',padding:'4px 8px'}}>{k.atRisk}</td>
                            <td style={{border:'1px solid #E5E7EB',padding:'4px 8px'}}>{k.events}</td>
                            <td style={{border:'1px solid #E5E7EB',padding:'4px 8px',color:k.reliability==='good'?COLORS.green:k.reliability==='indicative'?COLORS.orange:COLORS.red}}>
                              {k.reliability==='good'?'good':k.reliability==='indicative'?'indicative (n<30)':'weak (n<10)'}
                            </td>
                          </tr>))}</tbody>
                      </table>
                    </div>
                    <div style={{fontSize:11,color:COLORS.muted,marginTop:6}}>
                      ⏳ Only {mhFunnel.km59Converters} conversions observed so far, so every figure above is provisional — read the CI, not just the point estimate. Kaplan-Meier uses the whole cohort and credits each patient only for the time they have actually been observed, so these numbers stay comparable as cohorts age (a raw ratio would not). Time-to-conversion percentiles are deliberately not shown until there are 30+ conversions.
                    </div>
                  </div>
                )}
              </SectionCard>

              {/* £189 track */}
              <SectionCard title="£189 Multi-Appointment Track">
                <div style={{display:'flex',gap:12,flexWrap:'wrap',marginBottom:8}}>
                  <MetricCard label="Total £189 patients" value={mhFunnel.total189} />
                  <MetricCard label="New (last 30 days)" value={mhFunnel.recent189} />
                  <MetricCard label="Eligible (8+ weeks)" value={mhFunnel.eligible189} sub="past conversion window (unchanged)" />
                  <MetricCard label="Converted to £19 plan" value={mhFunnel.converted189} color={COLORS.green} />
                  <MetricCard label="Conversion rate" value={mhFunnel.conversionRate189+'%'} color={parseFloat(mhFunnel.conversionRate189)>=10?COLORS.green:COLORS.orange} sub={'based on '+mhFunnel.eligible189+' eligible'} />
                </div>
              </SectionCard>
            </>) : (
              <div style={{color:COLORS.muted,fontSize:13}}>MH funnel data unavailable</div>
            )}
          </div>
        )}
        {tab === "Traffic" && (
            <>
              <div className="metrics-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                <MetricCard label="Total sessions" value={fmt.num(m.totalSessions)} />
                <MetricCard label="New customers" value={fmt.num(m.newCustomers)} change={comparison?.newCustomers?.change} />
                <MetricCard label="Conversion rate" value={fmt.pct(m.conversionRate)} />
              </div>
              <div className="charts-2col">
                <SectionCard title="Sessions over time">
                  {sessionData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={sessionData} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: COLORS.muted }} />
                        <YAxis tick={{ fontSize: 10, fill: COLORS.muted }} />
                        <Tooltip formatter={(v) => [fmt.num(v), "Sessions"]} />
                        <Area type="monotone" dataKey="Sessions" stroke={COLORS.purple} fill="rgba(83,74,183,0.1)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : <div style={{ fontSize: 13, color: COLORS.muted, padding: "1rem 0" }}>GA4 not connected</div>}
                </SectionCard>
                <SectionCard title="Sessions by channel">
                  {channelData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={channelData} layout="vertical" margin={{ top: 4, right: 4, left: 60, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                        <XAxis type="number" tick={{ fontSize: 10, fill: COLORS.muted }} />
                        <YAxis dataKey="channel" type="category" tick={{ fontSize: 10, fill: COLORS.muted }} width={60} />
                        <Tooltip formatter={(v) => [fmt.num(v), "Sessions"]} />
                        <Bar dataKey="sessions" radius={[0,3,3,0]}>
                          {channelData.map((_, i) => <Cell key={i} fill={planColors[i % planColors.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div style={{ fontSize: 13, color: COLORS.muted, padding: "1rem 0" }}>No channel data yet</div>}
                </SectionCard>
              </div>

            </>
          )}

        {tab === 'Ads Intelligence' && (
          <div>
            {/* Sub-tab buttons */}
            <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:14,borderBottom:'1px solid #E5E7EB',paddingBottom:10}}>
              {['Recommendations','Keywords','Search Terms','Pacing','Ad Quality','Page Ideas','Impression Share','Cannibalism','Change Log','Settings','Action Plan'].map(t=>(
                <button key={t} onClick={()=>{setAdsTab(t);if(t==='Page Ideas')fetchPageOpps();if(t==='Ad Quality')fetchAdQuality();}} style={{padding:'5px 12px',borderRadius:6,border:'none',background:adsTab===t?COLORS.purple:'#F3F4F6',color:adsTab===t?'#fff':COLORS.text,cursor:'pointer',fontSize:12,fontWeight:adsTab===t?600:400}}>{t}</button>
              ))}
              <button onClick={()=>setShowChangeForm(true)} style={{marginLeft:'auto',padding:'5px 12px',borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer',fontSize:12}}>+ Log Change</button>
            </div>

            {/* Recommendations tab */}
            {adsTab === 'Recommendations' && (() => {
              const kws = adsIntel ? adsIntel.keywords || [] : [];
              const sts = adsIntel ? adsIntel.searchTerms || [] : [];
              const targetCpaMap = adsIntel ? adsIntel.targetCpaMap || {} : {};
              const getTargetCpa = (campaign) => targetCpaMap[campaign] || parseFloat(m.cac) || 42;
              const aqLookup = (kw, camp) => adQuality ? (adQuality.keywords||[]).find(q=>q.keyword.toLowerCase()===kw.toLowerCase()&&q.campaign===camp) : null;
              const ratingArrow = (v) => v==='ABOVE_AVERAGE'?{s:'↑',c:COLORS.green}:v==='BELOW_AVERAGE'?{s:'↓',c:COLORS.red}:{s:'→',c:'#F59E0B'};
              const demand = sts.filter(st=>st.recClass==='opportunity').sort((a,b)=>b.conversions-a.conversions).slice(0,10);
              const budgetOpps = adsIntel?.budgetOpportunities || [];
              const kill = kws.filter(k=>k.recClass==='waste').sort((a,b)=>b.spend-a.spend).slice(0,10);
              const totalWasted = kws.filter(k=>k.recClass==='waste').reduce((s,k)=>s+k.spend,0);
              const fix = kws.filter(k=>k.recClass==='fix_quality').sort((a,b)=>b.spend-a.spend).slice(0,10);
              const overTargetKws = kws.filter(k=>k.recClass==='over_target'||k.recClass==='extreme_cpa').sort((a,b)=>(b.spend/b.conversions)-(a.spend/a.conversions)).slice(0,10);
              const overTargetTerms = sts.filter(st=>st.recClass==='over_target'||st.recClass==='extreme_cpa').sort((a,b)=>(b.spend/Math.max(b.conversions,0.01))-(a.spend/Math.max(a.conversions,0.01))).slice(0,10);
              const watch = kws.filter(k=>k.recClass==='watch').sort((a,b)=>b.spend-a.spend).slice(0,10);
              const campAgg = {};
              kws.forEach(k=>{if(!campAgg[k.campaign])campAgg[k.campaign]={campaign:k.campaign,spend:0,conversions:0,clicks:0};campAgg[k.campaign].spend+=k.spend;campAgg[k.campaign].conversions+=k.conversions;campAgg[k.campaign].clicks+=k.clicks;});
              const campaigns = Object.values(campAgg).map(c=>({...c,cpa:c.conversions>0?c.spend/c.conversions:null,target:getTargetCpa(c.campaign)})).filter(c=>c.conversions>0).sort((a,b)=>b.conversions-a.conversions);
              const demandWindow = adsIntel ? ('last 90 days') : '';
              const Section = ({id,title,subtitle,count,color,window:win,children}) => (
                <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:8,marginBottom:10,overflow:'hidden'}}>
                  <div onClick={()=>setExpandedRecs(p=>({...p,[id]:!p[id]}))} style={{display:'flex',alignItems:'center',gap:10,padding:'12px 16px',cursor:'pointer'}}>
                    <span style={{fontSize:12,color:COLORS.muted,transform:expandedRecs[id]?'rotate(90deg)':'rotate(0deg)',transition:'transform 0.15s'}}>▶</span>
                    <span style={{fontWeight:600,fontSize:14}}>{title}</span>
                    {count>0&&<span style={{fontSize:12,background:color+'18',color:color,padding:'2px 8px',borderRadius:10,fontWeight:600}}>{count}</span>}
                    {win&&<span style={{marginLeft:'auto',fontSize:10,color:COLORS.muted,background:'#F3F4F6',padding:'2px 8px',borderRadius:4}}>{win}</span>}
                  </div>
                  {expandedRecs[id]&&<div style={{padding:'0 16px 14px',borderTop:'1px solid #F3F4F6'}}>{subtitle&&<div style={{fontSize:11,color:COLORS.muted,margin:'10px 0'}}>{subtitle}</div>}{children}</div>}
                </div>
              );
              const shorten = (s) => s.replace('Back-up fixed terms campaign','Back-up').replace('Mental health - £59','MH £59');
              return (
                <div>
                  <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:8,marginBottom:14,overflow:'hidden'}}>
                    <div onClick={()=>setShowGuide(g=>!g)} style={{display:'flex',alignItems:'center',gap:10,padding:'12px 16px',cursor:'pointer'}}>
                      <span style={{fontSize:12,color:COLORS.muted,transform:showGuide?'rotate(90deg)':'rotate(0deg)',transition:'transform 0.15s'}}>▶</span>
                      <span style={{fontWeight:600,fontSize:14}}>📖 How to use this</span>
                      <span style={{marginLeft:'auto',fontSize:11,color:COLORS.muted}}>weekly + monthly routine</span>
                    </div>
                    {showGuide&&(<div style={{padding:'0 16px 16px',borderTop:'1px solid #F3F4F6',fontSize:12,lineHeight:1.6,color:COLORS.text}}>
                      <div style={{marginTop:12,fontWeight:600,color:COLORS.purple}}>Weekly (~30 min, 3M range)</div>
                      <div style={{marginTop:4}}>
                        <div>· <b>Stop waste</b> — negative <span style={{color:COLORS.red}}>●</span> Irrelevant; leave <span style={{color:'#F59E0B'}}>●</span> Relevant (page/conversion issue)</div>
                        <div>· <b>Capture demand</b> — add converting non-location terms as keywords</div>
                        <div>· <b>Converting but pricey</b> — negative ⚠️ extreme (3x+); note the rest</div>
                        <div>· <b>Campaign bidding</b> — only adjust if clearly beating target; leave 2 weeks</div>
                      </div>
                      <div style={{marginTop:12,fontWeight:600,color:COLORS.purple}}>Monthly (~1 hr)</div>
                      <div style={{marginTop:4}}>
                        <div>· <b>Page Ideas</b> — validate clinically before building</div>
                        <div>· <b>Fix Quality</b> — use 💡 Suggest copy, test in Google Ads</div>
                        <div>· <b>Change log</b> — did last month's changes work?</div>
                      </div>
                      <div style={{marginTop:12,fontWeight:600,color:COLORS.purple}}>Rules of thumb</div>
                      <div style={{marginTop:4}}>
                        <div>· Don't check daily — thresholds are 90-day, daily swings are noise</div>
                        <div>· Give any change 2 weeks before judging</div>
                        <div>· MH data reliable; GP is CTA-click only until GCLID fixed</div>
                      </div>
                    </div>)}
                  </div>
                  <div style={{background:'#F0F9FF',border:'1px solid #BAE6FD',borderRadius:8,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#075985'}}>
                    ℹ️ MH conversions are fully tracked. GP conversions are partially tracked (GCLID pending) — weight MH actions with higher confidence. £ impact estimates are directional.
                  </div>

                  {/* Manual keyword adder */}
                  <SectionCard title="🔍 Add keyword manually">
                    <div style={{fontSize:12,color:COLORS.muted,marginBottom:10}}>Enter a keyword to assess its fit — Claude will suggest the right campaign, ad group, and match type.</div>
                    <div style={{display:'flex',gap:8,marginBottom:10}}>
                      <input value={manualKw.term} onChange={e=>setManualKw(s=>({...s,term:e.target.value,result:null}))} onKeyDown={e=>e.key==='Enter'&&assessManualKeyword()} placeholder="e.g. same day GP appointment" style={{flex:1,padding:'6px 10px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:13}} />
                      <button onClick={assessManualKeyword} disabled={manualKw.assessing||!manualKw.term.trim()} style={{padding:'6px 16px',borderRadius:6,background:COLORS.blue,color:'#fff',border:'none',cursor:'pointer',fontSize:13,fontWeight:600,opacity:manualKw.assessing?0.6:1}}>{manualKw.assessing?'Assessing\u2026':'Assess'}</button>
                    </div>
                    {manualKw.result&&(<div style={{background:manualKw.result.verdict==='good'?'#F0FDF4':manualKw.result.verdict==='caution'?'#FFFBEB':'#FEF2F2',border:'1px solid '+(manualKw.result.verdict==='good'?'#86EFAC':manualKw.result.verdict==='caution'?'#FCD34D':'#FCA5A5'),borderRadius:6,padding:'10px 12px',fontSize:12}}>
                      <div style={{fontWeight:600,marginBottom:6}}>{manualKw.result.verdict==='good'?'✅ Good fit':manualKw.result.verdict==='caution'?'⚠️ Add with caution':'\u274c Not recommended'}</div>
                      <div style={{marginBottom:4,color:'#374151'}}>{manualKw.result.reason}</div>
                      {manualKw.result.ok&&manualKw.result.verdict!=='reject'&&(<>
                        <div style={{marginTop:8,display:'flex',flexWrap:'wrap',gap:6,fontSize:11}}>
                          <span style={{background:'#E5E7EB',borderRadius:4,padding:'2px 8px'}}>📁 {manualKw.result.campaign}</span>
                          <span style={{background:'#E5E7EB',borderRadius:4,padding:'2px 8px'}}>🗂 {manualKw.result.adGroup}</span>
                          <span style={{background:'#E5E7EB',borderRadius:4,padding:'2px 8px'}}>🎯 {manualKw.result.matchType}</span>
                        </div>
                        <button onClick={()=>openKwModal(manualKw.term.trim(),manualKw.result.campaign,'keyword')} style={{marginTop:10,padding:'6px 16px',borderRadius:6,background:COLORS.green,color:'#fff',border:'none',cursor:'pointer',fontSize:12,fontWeight:600}}>➕ Add keyword</button>
                      </>)}
                    </div>)}
                  </SectionCard>

                  <Section id="budget" title="💰 Budget opportunities" count={budgetOpps.length} color="#0EA5A0" window="selected range" subtitle="Campaigns beating their target CPA but losing impressions to ad rank (not budget). Raising the campaign target CPA bids more aggressively to capture that lost volume — without sacrificing your CPA target.">
                    {budgetOpps.length===0?<div style={{fontSize:12,color:COLORS.muted}}>No campaigns with clear budget headroom right now.</div>:budgetOpps.map((b,i)=>(
                      <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid #F3F4F6',gap:10}}>
                        <div style={{flex:1}}>
                          <div style={{fontWeight:500,fontSize:13}}>{shorten(b.campaign)}</div>
                          <div style={{fontSize:11,color:COLORS.muted}}>CPA £{b.cpa} vs £{b.target} target · {b.rankLostShare}% rank-lost, only {b.budgetLostShare}% budget-lost · ~{b.estMonthlyConvGain} more conv/mo if captured</div>
                        </div>
                        <button onClick={()=>askRaiseTcpa({campaignName:b.campaign,current:b.target,next:b.suggestedTcpa,description:(<span>Raise <b>{shorten(b.campaign)}</b> target CPA from <b>£{b.target}</b> to <b>£{b.suggestedTcpa}</b>? This bids more aggressively to capture the <b>{b.rankLostShare}%</b> impression share currently lost to rank (CPA is £{b.cpa}, under the £{b.target} target). Estimated <b>+{b.estMonthlyConvGain}</b> conversions/month. This changes bidding for the whole campaign.</span>)})} style={{color:'#0E7490',fontSize:11,fontWeight:600,background:'#ECFEFF',border:'1px solid #67E8F9',borderRadius:4,padding:'4px 10px',cursor:'pointer',whiteSpace:'nowrap'}}>Raise tCPA → £{b.suggestedTcpa}</button>
                      </div>
                    ))}
                  </Section>

                  <Section id="demand" title="📈 Capture demand" count={demand.length} color="#059669" window={demandWindow} subtitle="Converting search terms not yet added as keywords, within 2x target CPA. Adding them as exact-match keywords gives you bid control and better Quality Score.">
                    {demand.length===0?<div style={{fontSize:12,color:COLORS.muted}}>No new converting terms right now.</div>:demand.map((st,i)=>(
                      <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid #F3F4F6'}}>
                        <div><div style={{fontWeight:500,fontSize:13}}>"{st.term}"</div><div style={{fontSize:11,color:COLORS.muted}}>{shorten(st.campaign)} · {st.conversions.toFixed(1)} conv · £{Math.round(st.spend/st.conversions)} CPA vs £{Math.round(getTargetCpa(st.campaign))} target</div></div>
                        <button onClick={()=>openKwModal(st.term,st.campaign,'keyword')} style={{color:'#065F46',fontSize:11,fontWeight:600,background:'#ECFDF5',border:'1px solid #6EE7B7',borderRadius:4,padding:'4px 10px',cursor:'pointer',whiteSpace:'nowrap'}}>➕ Add keyword</button>
                      </div>
                    ))}
                  </Section>

                  <Section id="waste" title={<><span style={{color:'#DC2626'}}>●</span> Stop waste</>} count={kill.length} color="#DC2626" window="selected range" subtitle={"Past the statistical click threshold with zero conversions. ~£"+Math.round(totalWasted)+" spent with no conversion."}>
                    {kill.length>0&&<div style={{display:'flex',justifyContent:'flex-end',marginBottom:8}}>
                      <button onClick={()=>askNegativeAll(kill.map(k=>({keyword:k.keyword,campaign:k.campaign,spend:k.spend})))} style={{fontSize:11,fontWeight:600,color:'#fff',background:'#DC2626',border:'none',borderRadius:6,padding:'5px 12px',cursor:'pointer'}}>🚫 Negative all {kill.length}</button>
                    </div>}
                    {kill.length===0?<div style={{fontSize:12,color:COLORS.muted}}>No keywords clearly wasting budget right now.</div>:kill.map((k,i)=>(
                      <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid #F3F4F6'}}>
                        <div style={{flex:1}}>
                          <div style={{fontWeight:500,fontSize:13}}>"{k.keyword}"</div>
                          <div style={{fontSize:11,color:COLORS.muted}}>{shorten(k.campaign)} · {k.clicks} clicks · £{k.cpc} CPC{k.qualityScore?' · QS '+k.qualityScore:''}</div>
                          {aiClassifications[k.keyword]&&<div style={{fontSize:10,marginTop:3,color:aiClassifications[k.keyword].classification==='irrelevant'?COLORS.red:aiClassifications[k.keyword].classification==='relevant_not_converting'?'#92400E':'#6B7280'}}>{aiClassifications[k.keyword].classification==='irrelevant'?'● Irrelevant':aiClassifications[k.keyword].classification==='relevant_not_converting'?'● Check landing page':'● Ambiguous'} — {aiClassifications[k.keyword].reason}</div>}
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:10}}>
                          <div style={{textAlign:'right'}}><div style={{fontWeight:600,color:COLORS.red,fontSize:13}}>£{k.spend}</div><div style={{fontSize:10,color:COLORS.muted}}>0 conv</div></div>
                          <button onClick={()=>openKwModal(k.keyword,k.campaign,'negative')} style={{color:'#991B1B',fontSize:11,fontWeight:600,background:'#FEF2F2',border:'1px solid #FCA5A5',borderRadius:4,padding:'4px 10px',cursor:'pointer',whiteSpace:'nowrap'}}>🚫 Negative</button>
                        </div>
                      </div>
                    ))}
                  </Section>

                  <Section id="fix" title="🔧 Fix quality" count={fix.length} color="#D97706" window="selected range" subtitle="Converting but low Quality Score with £100+ spend — Google is charging a premium.">
                    {fix.length===0?<div style={{fontSize:12,color:COLORS.muted}}>No high-spend low-QS keywords right now.</div>:fix.map((k,i)=>{
                      const aq=aqLookup(k.keyword,k.campaign);
                      const ctrA=ratingArrow(k.expectedCtr),adA=ratingArrow(k.adRelevance),lpA=ratingArrow(k.landingPageExp);
                      const estSaving=Math.round(k.spend*0.25);
                      return(<div key={i} style={{padding:'8px 0',borderBottom:'1px solid #F3F4F6'}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:10}}>
                          <div style={{flex:1}}>
                            <div style={{fontWeight:500,fontSize:13}}>"{k.keyword}"</div>
                            <div style={{fontSize:11,color:COLORS.muted,marginTop:2}}>{shorten(k.campaign)} · {k.conversions.toFixed(1)} conv · £{k.cpc} CPC</div>
                            <div style={{fontSize:11,marginTop:4,display:'flex',gap:12}}>
                              <span>CTR <span style={{color:ctrA.c,fontWeight:600}}>{ctrA.s}</span></span>
                              <span>Ad <span style={{color:adA.c,fontWeight:600}}>{adA.s}</span></span>
                              <span>LP <span style={{color:lpA.c,fontWeight:600}}>{lpA.s}</span></span>
                              {aq&&<span style={{color:COLORS.muted}}>Match: ad {aq.adScore}% / page {aq.pageScore}%</span>}
                            </div>
                            <div style={{fontSize:10,color:COLORS.muted,marginTop:4,fontStyle:'italic'}}>Directional only: lifting QS {k.qualityScore}→7 typically cuts CPC ~25% (~£{estSaving}/period). Not a guarantee.</div>
                          </div>
                          <div style={{textAlign:'right'}}><div style={{background:k.qualityScore<=2?COLORS.red:'#F59E0B',color:'#fff',borderRadius:4,padding:'2px 6px',fontSize:11,fontWeight:600,display:'inline-block'}}>QS {k.qualityScore}</div></div>
                        </div>
                        <div style={{marginTop:6}}>
                          {recCopySuggestions[k.keyword]?(<div style={{fontSize:11,background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:4,padding:8}}>
                            <div style={{fontWeight:600,color:COLORS.green,marginBottom:4}}>💡 Suggested headlines:</div>
                            {(recCopySuggestions[k.keyword].headlines||[]).map((h,hi)=>(<div key={hi} style={{padding:'1px 0'}}>"{h}" <span style={{color:h.length>30?COLORS.red:COLORS.muted,fontSize:10}}>{h.length}/30</span></div>))}
                            <div style={{marginTop:4}}><b>Desc:</b> {recCopySuggestions[k.keyword].description}</div>
                            {recCopySuggestions[k.keyword].landingPageNote&&<div style={{marginTop:4,color:COLORS.muted,fontStyle:'italic'}}>{recCopySuggestions[k.keyword].landingPageNote}</div>}
                          </div>):(<button onClick={async()=>{
                            setRecCopyLoading(p=>({...p,[k.keyword]:true}));
                            try{const agData=aq?(adQuality.adGroups||[]).find(a=>a.campaign===k.campaign&&a.name===aq.adGroup):null;const urlData=aq&&adQuality.urlContents?adQuality.urlContents[aq.url]:null;const r=await authFetch('/api/ads/suggest-copy',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({keyword:k.keyword,headlines:agData?.headlines,landingPageH1:urlData?.h1,adScore:aq?.adScore,pageScore:aq?.pageScore})});const d=await r.json();if(d.ok)setRecCopySuggestions(p=>({...p,[k.keyword]:d}));}catch(e){}
                            setRecCopyLoading(p=>({...p,[k.keyword]:false}));
                          }} style={{fontSize:11,padding:'3px 10px',borderRadius:4,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer'}}>{recCopyLoading[k.keyword]?'...':'💡 Suggest copy'}</button>)}
                        </div>
                      </div>);
                    })}
                  </Section>

                  <Section id="overtarget" title="💸 Converting but pricey" count={overTargetKws.length+overTargetTerms.length} color="#EA580C" window="selected range" subtitle="Converting, but above target CPA. ⚠️ = 3x+ target with real spend.">
                    {[...overTargetKws.map(k=>({...k,term:k.keyword,isKw:true})),...overTargetTerms.map(t=>({...t,isKw:false}))].sort((a,b)=>(b.spend/b.conversions)-(a.spend/a.conversions)).slice(0,20).map((k,i)=>(
                      <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid #F3F4F6'}}>
                        <div>
                          <div style={{fontWeight:500,fontSize:13}}>"{k.term}"{k.isKw&&k.qualityScore?<span style={{fontSize:10,color:COLORS.muted,marginLeft:6}}>QS {k.qualityScore}</span>:null}</div>
                          <div style={{fontSize:11,color:COLORS.muted}}>{shorten(k.campaign)} · {k.conversions.toFixed(1)} conv · {k.isKw?'keyword':'search term'}</div>
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:10}}>
                          <div style={{textAlign:'right'}}>
                            <div style={{fontWeight:600,color:k.recClass==='extreme_cpa'?COLORS.red:'#EA580C',fontSize:13}}>£{Math.round(k.spend/k.conversions)} CPA{k.recClass==='extreme_cpa'?' ⚠️':''}</div>
                            <div style={{fontSize:10,color:COLORS.muted}}>vs £{Math.round(getTargetCpa(k.campaign))} target</div>
                          </div>
                          {k.recClass==='extreme_cpa'&&!k.isAlreadyNegative&&<button onClick={()=>openKwModal(k.term,k.campaign,'negative')} style={{color:'#991B1B',fontSize:11,fontWeight:600,background:'#FEF2F2',border:'1px solid #FCA5A5',borderRadius:4,padding:'4px 10px',cursor:'pointer',whiteSpace:'nowrap'}}>Negative</button>}
                        </div>
                      </div>
                    ))}
                  </Section>

                  <Section id="bidding" title="⚙️ Campaign bidding" count={campaigns.length} color="#7C3AED" window="selected range" subtitle="All campaigns use automated bidding. The lever is the campaign target CPA. ⚠️ Changing tCPA affects every keyword in the campaign.">
                    {campaigns.map((c,i)=>{
                      const beating=c.cpa&&c.cpa<c.target;
                      return(<div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid #F3F4F6'}}>
                        <div>
                          <div style={{fontWeight:500,fontSize:13}}>{shorten(c.campaign)}</div>
                          <div style={{fontSize:11,color:COLORS.muted}}>{c.conversions.toFixed(0)} conv · £{Math.round(c.spend)} spend · £{Math.round(c.cpa)} CPA vs £{Math.round(c.target)} target {beating?<span style={{color:COLORS.green}}>· beating target</span>:<span style={{color:'#F59E0B'}}>· at/over target</span>}</div>
                        </div>
                        <button onClick={()=>{setTcpaModal({campaign:c.campaign,current:c.target});setTcpaValue(String(Math.round(c.target)));setTcpaResult(null);}} style={{color:'#5B21B6',fontSize:11,fontWeight:600,background:'#F5F3FF',border:'1px solid #C4B5FD',borderRadius:4,padding:'4px 10px',cursor:'pointer',whiteSpace:'nowrap'}}>Adjust tCPA</button>
                      </div>);
                    })}
                  </Section>

                  <Section id="watch" title="👀 Watch" count={watch.length} color="#6B7280" window="selected range" subtitle="Zero conversions but not yet past the click threshold — too little data to judge.">
                    {watch.length===0?<div style={{fontSize:12,color:COLORS.muted}}>Nothing on the watchlist.</div>:watch.map((k,i)=>(
                      <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid #F3F4F6'}}>
                        <div><div style={{fontWeight:500,fontSize:13}}>"{k.keyword}"</div><div style={{fontSize:11,color:COLORS.muted}}>{shorten(k.campaign)} · {k.clicks} clicks · £{k.cpc} CPC</div></div>
                        <div style={{textAlign:'right'}}><div style={{fontWeight:600,color:'#F59E0B',fontSize:13}}>£{k.spend}</div><div style={{fontSize:10,color:COLORS.muted}}>0 conv</div></div>
                      </div>
                    ))}
                  </Section>
                </div>
              );
            })()}

            {/* Keywords tab */}
            {adsTab === 'Keywords' && (
              <SectionCard title={`Keywords (${keywordRows.length} shown)`}>
                <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap',alignItems:'center'}}>
                  <input placeholder="Filter campaign..." value={kwFilter.campaign} onChange={e=>setKwFilter(f=>({...f,campaign:e.target.value}))} style={{padding:'4px 8px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:12,width:130}} />
                  <input placeholder="Filter keyword..." value={kwFilter.keyword} onChange={e=>setKwFilter(f=>({...f,keyword:e.target.value}))} style={{padding:'4px 8px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:12,width:160}} />
                  <select value={kwFilter.flag} onChange={e=>setKwFilter(f=>({...f,flag:e.target.value}))} style={{padding:'4px 8px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:12}}>
                    <option value="">All flags</option>
                    <option value="low_qs">Low QS</option>
                    <option value="high_spend_no_conv">High spend no conv</option>
                  </select>
                  <label style={{fontSize:12,display:'flex',alignItems:'center',gap:4}}><input type="checkbox" checked={hideZeroSpend} onChange={e=>setHideZeroSpend(e.target.checked)} /> Active only</label>
                  <button onClick={()=>downloadCSV('keywords.csv',[{label:'Campaign',key:'campaign'},{label:'Keyword',key:'keyword'},{label:'Match',key:'matchType'},{label:'Spend',key:'spend'},{label:'Clicks',key:'clicks'},{label:'Conversions',key:'conversions'},{label:'CPC',key:'cpc'},{label:'CTR',key:'ctr'},{label:'CR',key:'convRate'},{label:'QS',get:r=>r.qualityScore??''},{label:'AdMatch',get:r=>r.adScore??''},{label:'PageMatch',get:r=>r.pageScore??''},{label:'Flag',get:r=>r.flag??''}],keywordRows)} style={{marginLeft:'auto',fontSize:11,padding:'5px 12px',borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer'}}>⬇ CSV ({keywordRows.length})</button>
                </div>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                    <thead><tr style={{borderBottom:'2px solid #E5E7EB'}}>
                      {[['campaign','Campaign'],['keyword','Keyword'],['matchType','Match'],['spend','Spend'],['clicks','Clicks'],['conversions','Conv'],['cpc','CPC'],['ctr','CTR'],['convRate','CR'],['qualityScore','QS'],['adScore','Ad Match'],['pageScore','Page Match'],['flag','Flag']].map(([col,label])=>(
                        <th key={col} onClick={()=>setKwSort(s=>({col,dir:s.col===col&&s.dir==='desc'?'asc':'desc'}))} style={{padding:'5px 8px',textAlign:'left',cursor:'pointer',fontWeight:600,color:kwSort.col===col?COLORS.purple:COLORS.muted,whiteSpace:'nowrap'}}>{label}{kwSort.col===col?(kwSort.dir==='desc'?' ↓':' ↑'):''}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {keywordRows.map((k,i)=>(
                        <tr key={i} style={{borderBottom:'1px solid #F3F4F6',background:i%2===0?'transparent':'#FAFAFA'}}>
                          <td style={{padding:'5px 8px',color:COLORS.muted,fontSize:11}}>{k.campaign.replace('Back-up fixed terms campaign','Back-up').replace('Mental health - £59','MH £59')}</td>
                          <td style={{padding:'5px 8px',fontWeight:500}}>{k.keyword}</td>
                          <td style={{padding:'5px 8px',color:COLORS.muted}}>{k.matchType}</td>
                          <td style={{padding:'5px 8px',fontWeight:500}}>£{k.spend}</td>
                          <td style={{padding:'5px 8px'}}>{k.clicks}</td>
                          <td style={{padding:'5px 8px'}}>{k.conversions}</td>
                          <td style={{padding:'5px 8px'}}>£{k.cpc}</td>
                          <td style={{padding:'5px 8px'}}>{k.ctr}%</td>
                          <td style={{padding:'5px 8px'}}>{k.convRate ? k.convRate+'%' : '—'}</td>
                          <td style={{padding:'5px 8px'}}>{k.qualityScore!=null?<span style={{background:k.qualityScore<=3?COLORS.red:k.qualityScore<=6?'#F59E0B':COLORS.green,color:'#fff',borderRadius:4,padding:'1px 6px',fontWeight:600}}>{k.qualityScore}</span>:'—'}</td>
                          <td style={{padding:'5px 8px'}}>{k.adScore!=null?k.adScore+'%':'—'}</td>
                          <td style={{padding:'5px 8px'}}>{k.pageScore!=null?k.pageScore+'%':'—'}</td>
                          <td style={{padding:'5px 8px'}}>
                            {k.flag==='low_qs'&&<span style={{fontSize:10,color:'#F59E0B'}}>⚠ Low QS</span>}
                            {k.flag==='high_spend_no_conv'&&<span style={{fontSize:10,color:COLORS.red}}>⚠ No conv</span>}
                            {k.recClass==='fix_quality'&&<button onClick={async()=>{setCopyLoading(p=>({...p,[k.keyword]:true}));try{const aq2=(adQuality?.keywords||[]).find(q=>q.keyword.toLowerCase()===k.keyword.toLowerCase()&&q.campaign===k.campaign);const agData=aq2?(adQuality.adGroups||[]).find(a=>a.campaign===k.campaign&&a.name===aq2.adGroup):null;const urlData=aq2&&adQuality.urlContents?adQuality.urlContents[aq2.url]:null;const r=await authFetch('/api/ads/suggest-copy',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({keyword:k.keyword,headlines:agData?.headlines,landingPageH1:urlData?.h1,adScore:aq2?.adScore,pageScore:aq2?.pageScore})});const d=await r.json();if(d.ok)setCopySuggestions(p=>({...p,[k.keyword]:d}));}catch(e){}setCopyLoading(p=>({...p,[k.keyword]:false}));}} style={{fontSize:10,padding:'2px 6px',borderRadius:4,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer',marginLeft:4}}>{copyLoading[k.keyword]?'...':'💡 Suggest'}</button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {Object.keys(copySuggestions).length>0&&<div style={{marginTop:16,padding:12,background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:8}}>
                  {Object.entries(copySuggestions).map(([kw,d])=>(
                    <div key={kw} style={{marginBottom:12}}>
                      <div style={{fontWeight:600,fontSize:13,marginBottom:4}}>"{kw}"</div>
                      {(d.headlines||[]).map((h,i)=>(<div key={i} style={{fontSize:12}}>"{h}" <span style={{color:h.length>30?COLORS.red:COLORS.muted,fontSize:10}}>{h.length}/30</span></div>))}
                      {d.description&&<div style={{fontSize:12,marginTop:4}}><b>Desc:</b> {d.description}</div>}
                      {d.landingPageNote&&<div style={{fontSize:11,color:COLORS.muted,fontStyle:'italic',marginTop:2}}>{d.landingPageNote}</div>}
                    </div>
                  ))}
                </div>}
              </SectionCard>
            )}

            {/* Search Terms tab */}
            {adsTab === 'Search Terms' && (
              <SectionCard title="Search Terms">
                <div style={{marginBottom:12}}>
                  {adsIntel?.searchTermsSummary&&(
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,flexWrap:'wrap',marginBottom:12,padding:'8px 12px',background:'#F0F9FF',border:'1px solid #BAE6FD',borderRadius:6,fontSize:12,color:'#075985'}}>
                      <span>{adsIntel.searchTermsSummary.full
                        ? `Showing all ${fmt.num(adsIntel.searchTermsSummary.total)} search terms.`
                        : `Showing ${fmt.num(adsIntel.searchTermsSummary.sent)} actionable terms (opportunity / waste / extreme / over-target) of ${fmt.num(adsIntel.searchTermsSummary.total)} total.`}</span>
                      {!adsIntel.searchTermsSummary.full && adsIntel.searchTermsSummary.total > adsIntel.searchTermsSummary.sent && (
                        <button onClick={()=>fetchAdsIntel(range, true)} disabled={adsIntelLoading} style={{padding:'4px 12px',borderRadius:6,border:'1px solid #0EA5A0',background:'#fff',color:'#0EA5A0',cursor:'pointer',fontSize:12,fontWeight:600,whiteSpace:'nowrap'}}>{adsIntelLoading?'Loading…':`Load all ${fmt.num(adsIntel.searchTermsSummary.total)}`}</button>
                      )}
                    </div>
                  )}
                  {adsIntel?.campaignThresholds&&Object.keys(adsIntel.campaignThresholds).length>0&&(
                    <div style={{marginBottom:12,padding:'10px 12px',background:'#F8FAFC',borderRadius:6,border:'1px solid #E5E7EB'}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,flexWrap:'wrap'}}><div style={{fontSize:11,fontWeight:600,color:COLORS.muted}}>📊 Negative keyword thresholds (clicks needed with 0 conversions to be statistically confident)</div><span style={{fontSize:10,color:COLORS.muted,background:'#F3F4F6',padding:'2px 8px',borderRadius:4,whiteSpace:'nowrap'}}>minimum 90-day window · 1M and 3M show the same 90 days; 6M and 1Y show more</span></div>
                      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                        {Object.entries(adsIntel.campaignThresholds).map(([name,t])=>(
                          <div key={name} style={{fontSize:11,background:'#fff',border:'1px solid #E5E7EB',borderRadius:4,padding:'4px 10px'}}>{name.replace('Back-up fixed terms campaign','Back-up').replace('Mental health - £59','MH £59')}: <b>{t.clickThreshold} clicks</b> ({t.conversionRate}% CR)</div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginBottom:10}}>
                    <input placeholder="Filter term..." value={stFilter.term} onChange={e=>setStFilter(f=>({...f,term:e.target.value}))} style={{padding:'4px 8px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:12,width:160}} />
                    <select value={stFilter.campaign} onChange={e=>setStFilter(f=>({...f,campaign:e.target.value}))} style={{padding:'4px 8px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:12}}>
                      <option value="">All campaigns</option>
                      {[...new Set((adsIntel?.searchTerms||[]).map(s=>s.campaign).filter(Boolean))].sort().map(c=><option key={c} value={c}>{c}</option>)}
                    </select>
                    <select value={stFilter.action} onChange={e=>setStFilter(f=>({...f,action:e.target.value}))} style={{padding:'4px 8px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:12}}>
                      <option value="">Action</option>
                      <option value="add_as_keyword">Add as keyword</option>
                      <option value="negative_candidate">Negative candidate</option>
                    </select>
                    {(stFilter.term||stFilter.campaign||stFilter.action)&&<button onClick={()=>setStFilter({campaign:'',term:'',action:''})} style={{fontSize:11,color:COLORS.muted,background:'transparent',border:'none',cursor:'pointer'}}>✕ Clear</button>}
                  </div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:10}}>
                    <div onClick={()=>setStFilter(f=>({...f,action:f.action==='add_as_keyword'?'':'add_as_keyword'}))} style={{background:stFilter.action==='add_as_keyword'?'#065F46':'#ECFDF5',borderRadius:6,padding:'6px 12px',fontSize:12,color:stFilter.action==='add_as_keyword'?'#fff':'#065F46',cursor:'pointer',userSelect:'none'}}>
                      ● {(adsIntel?.searchTerms||[]).filter(s=>s.recClass==='opportunity').length} keyword opportunities
                    </div>
                    <div onClick={()=>setStFilter(f=>({...f,action:f.action==='negative_candidate'?'':'negative_candidate'}))} style={{background:stFilter.action==='negative_candidate'?'#991B1B':'#FEF2F2',borderRadius:6,padding:'6px 12px',fontSize:12,color:stFilter.action==='negative_candidate'?'#fff':'#991B1B',cursor:'pointer',userSelect:'none'}}>
                      ● {(adsIntel?.searchTerms||[]).filter(s=>!s.isAlreadyNegative&&s.intentClass!=='in_scope'&&(s.recClass==='waste'||s.recClass==='extreme_cpa')).length} negative candidates
                    </div>
                    <label style={{fontSize:12,display:'flex',alignItems:'center',gap:6,cursor:'pointer',padding:'6px 12px',background:'#F3F4F6',borderRadius:6}}>
                      <input type="checkbox" checked={showBorderline} onChange={e=>{const on=e.target.checked;setShowBorderline(on);if(on&&adsIntel?.searchTermsSummary&&!adsIntel.searchTermsSummary.full)fetchAdsIntel(range,true);}} />
                      Show borderline terms
                    </label>
                  </div>
                </div>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                    <thead><tr style={{borderBottom:'2px solid #E5E7EB'}}>
                      {[['term','Search Term'],['campaign','Campaign'],['spend','Spend'],['clicks','Clicks'],['conversions','Conv'],['cpc','CPC'],['cpa','CPA'],['action','Action']].map(([col,label])=>(
                        <th key={col} onClick={()=>col!=='action'&&setStSort(s=>({col,dir:s.col===col&&s.dir==='desc'?'asc':'desc'}))} style={{padding:'5px 8px',textAlign:'left',cursor:col!=='action'?'pointer':'default',fontWeight:600,color:stSort.col===col?COLORS.purple:COLORS.muted,whiteSpace:'nowrap'}}>{label}{stSort.col===col?(stSort.dir==='desc'?' ↓':' ↑'):''}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {searchTermRows.map((st,i)=>(
                        <tr key={i} style={{borderBottom:'1px solid #F3F4F6',background:st.recClass==='opportunity'?'#F0FDF4':(st.recClass==='waste'||st.recClass==='extreme_cpa')?'#FFF8F8':showBorderline&&st.conversions===0?'#FFFBEB':'transparent'}}>
                          <td style={{padding:'5px 8px',fontWeight:500}}>{st.term}</td>
                          <td style={{padding:'5px 8px',color:COLORS.muted,fontSize:11}}>{st.campaign.replace('Back-up fixed terms campaign','Back-up').replace('Mental health - £59','MH £59')}</td>
                          <td style={{padding:'5px 8px',fontWeight:500}}>£{st.spend}</td>
                          <td style={{padding:'5px 8px'}}>{st.clicks}</td>
                          <td style={{padding:'5px 8px'}}>{st.conversions}</td>
                          <td style={{padding:'5px 8px'}}>£{st.cpc}</td>
                          <td style={{padding:'5px 8px',fontWeight:500,color:st.cpa?(st.cpa>(adsIntel?.targetCpaMap?.[st.campaign]||42)?COLORS.red:COLORS.green):COLORS.muted}}>{st.cpa?'£'+st.cpa:'—'}</td>
                          <td style={{padding:'5px 8px'}}>
                            {st.recClass==='opportunity'&&<button onClick={()=>openKwModal(st.term,st.campaign,'keyword')} style={{color:'#065F46',fontSize:11,fontWeight:600,background:'#ECFDF5',border:'1px solid #6EE7B7',borderRadius:4,padding:'2px 8px',cursor:'pointer'}}>➕ Add as keyword</button>}
                            {(st.recClass==='waste')&&(<div style={{display:'flex',flexDirection:'column',gap:2}}>
                              {st.confidence&&<div style={{fontSize:10,color:st.confidence>=90?COLORS.red:st.confidence>=70?'#F59E0B':'#6B7280'}}>{st.confidence>=90?'● High confidence':st.confidence>=70?'● Medium confidence':'● Low confidence'} · {st.confidence}% this won't convert</div>}
                              {aiClassifications[st.term]&&<div style={{fontSize:10,color:aiClassifications[st.term].classification==='irrelevant'?COLORS.red:aiClassifications[st.term].classification==='relevant_not_converting'?'#92400E':'#6B7280'}}>{aiClassifications[st.term].classification==='irrelevant'?'● Irrelevant':aiClassifications[st.term].classification==='relevant_not_converting'?'● Check landing page':'● Ambiguous'} — {aiClassifications[st.term].reason}</div>}
                              {st.intentClass==='in_scope'
                                ? <div style={{fontSize:10,color:COLORS.green,fontWeight:600}}>✓ In-scope service — not a negative</div>
                                : <button onClick={()=>openKwModal(st.term,st.campaign,'negative')} style={{color:COLORS.red,fontSize:11,fontWeight:600,background:'#FEF2F2',border:'1px solid #FCA5A5',borderRadius:4,padding:'2px 8px',cursor:'pointer',width:'fit-content'}}>➖ Add as negative</button>}
                            </div>)}
                            {st.recClass==='extreme_cpa'&&!st.isAlreadyNegative&&(<div style={{display:'flex',flexDirection:'column',gap:2}}>
                              <div style={{fontSize:10,color:COLORS.red,marginBottom:1}}>⚠️ £{st.conversions>0?Math.round(st.spend/st.conversions):0} CPA — converts but extreme (likely a fluke)</div>
                              {st.intentClass==='in_scope'
                                ? <div style={{fontSize:10,color:COLORS.green,fontWeight:600}}>✓ In-scope service — not a negative</div>
                                : <button onClick={()=>openKwModal(st.term,st.campaign,'negative')} style={{color:COLORS.red,fontSize:11,fontWeight:600,background:'#FEF2F2',border:'1px solid #FCA5A5',borderRadius:4,padding:'2px 8px',cursor:'pointer',width:'fit-content'}}>➖ Add as negative</button>}
                            </div>)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{marginTop:10,display:'flex',justifyContent:'flex-end'}}>
                  <button onClick={()=>downloadCSV('search-terms.csv',[{label:'Term',key:'term'},{label:'Campaign',key:'campaign'},{label:'Spend',key:'spend'},{label:'Clicks',key:'clicks'},{label:'Conversions',key:'conversions'},{label:'CPC',key:'cpc'},{label:'CPA',get:r=>r.cpa??''},{label:'Class',key:'recClass'},{label:'Confidence',get:r=>r.confidence??''}],searchTermRows)} style={{fontSize:11,padding:'5px 12px',borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer'}}>⬇ Download CSV ({searchTermRows.length})</button>
                </div>

                {/* N-gram analysis */}
                <div style={{marginTop:20,borderTop:'1px solid #E5E7EB',paddingTop:16}}>
                  <div style={{fontWeight:600,fontSize:14,marginBottom:4}}>🔤 N-gram Analysis</div>
                  <div style={{fontSize:11,color:COLORS.muted,marginBottom:10}}>Every search term broken into individual words, aggregated. Words with £50+ spend, &lt;0.5 conversions across 5+ terms are flagged as <b>systemic negative candidates</b> — one phrase negative kills the whole pattern. {!adsIntel?.searchTermsSummary?.full && <span>Analysing actionable terms only — <button onClick={()=>fetchAdsIntel(range,true)} style={{background:'transparent',border:'none',color:'#0EA5A0',cursor:'pointer',fontSize:11,padding:0,textDecoration:'underline'}}>load all terms</button> for the full picture.</span>}</div>
                  {ngramResult&&<div style={{fontSize:12,marginBottom:10,color:ngramResult.ok?COLORS.green:COLORS.red}}>{ngramResult.ok?'✓ ':'✕ '}"{ngramResult.word}": {ngramResult.ok?ngramResult.message:ngramResult.error}</div>}
                  <div style={{overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                      <thead><tr style={{borderBottom:'2px solid #E5E7EB'}}>
                        {['Word','Terms','Clicks','Spend','Conv','',].map((h,i)=><th key={i} style={{padding:'5px 8px',textAlign:'left',fontWeight:600,color:COLORS.muted}}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {ngramData.slice(0,40).map((w,i)=>(
                          <tr key={i} style={{borderBottom:'1px solid #F3F4F6',background:w.systemic?'#FFF8F8':'transparent'}}>
                            <td style={{padding:'5px 8px',fontWeight:500}}>{w.word} {w.systemic&&<span style={{fontSize:9,background:COLORS.red,color:'#fff',borderRadius:4,padding:'1px 5px',marginLeft:4}}>systemic</span>}</td>
                            <td style={{padding:'5px 8px'}}>{w.terms}</td>
                            <td style={{padding:'5px 8px'}}>{w.clicks}</td>
                            <td style={{padding:'5px 8px',fontWeight:500}}>£{w.spend}</td>
                            <td style={{padding:'5px 8px',color:w.conversions>0?COLORS.green:COLORS.muted}}>{w.conversions}</td>
                            <td style={{padding:'5px 8px'}}>{w.systemic&&<button onClick={()=>negativeWord(w.word,w.domCampaign)} style={{color:COLORS.red,fontSize:11,fontWeight:600,background:'#FEF2F2',border:'1px solid #FCA5A5',borderRadius:4,padding:'2px 8px',cursor:'pointer',whiteSpace:'nowrap'}}>➖ Negative this word</button>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </SectionCard>
            )}

            {/* Pacing tab */}
            {adsTab === 'Pacing' && (
              <SectionCard title="Budget Pacing">
                <div style={{display:'flex',gap:8,marginBottom:12}}>
                  <input placeholder="Filter campaign..." value={pacingFilter} onChange={e=>setPacingFilter(e.target.value)} style={{padding:'4px 8px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:12,width:160}} />
                </div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                  <thead><tr style={{borderBottom:'2px solid #E5E7EB'}}>
                    {[['name','Name'],['totalSpend','Spend'],['monthlyBudget','Monthly Budget'],['projectedMonthly','Projected'],['pacingPct','Pacing']].map(([col,label])=>(
                      <th key={col} onClick={()=>setPacingSort(s=>({col,dir:s.col===col&&s.dir==='desc'?'asc':'desc'}))} style={{padding:'8px',textAlign:'left',cursor:'pointer',fontWeight:600,color:pacingSort.col===col?COLORS.purple:COLORS.muted}}>{label}{pacingSort.col===col?(pacingSort.dir==='desc'?' ↓':' ↑'):''}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {sortData((adsIntel?.pacingData||[]).filter(p=>!pacingFilter||p.name.toLowerCase().includes(pacingFilter.toLowerCase())),pacingSort.col,pacingSort.dir).map((p,i)=>(
                      <tr key={i} style={{borderBottom:'1px solid #F3F4F6'}}>
                        <td style={{padding:'8px',fontWeight:500}}>{p.name.replace('Back-up fixed terms campaign','Back-up').replace('Mental health - £59','MH £59')}</td>
                        <td style={{padding:'8px'}}>£{Math.round(p.totalSpend).toLocaleString()}</td>
                        <td style={{padding:'8px'}}>£{Math.round(p.monthlyBudget).toLocaleString()}</td>
                        <td style={{padding:'8px'}}>£{Math.round(p.projectedMonthly).toLocaleString()}</td>
                        <td style={{padding:'8px'}}>
                          <div style={{display:'flex',alignItems:'center',gap:8}}>
                            <div style={{flex:1,height:6,background:'#E5E7EB',borderRadius:3,overflow:'hidden',minWidth:60}}>
                              <div style={{height:'100%',width:Math.min(p.pacingPct,100)+'%',background:p.flag==='overpacing'?COLORS.red:p.flag==='underpacing'?'#F59E0B':COLORS.green,borderRadius:3}}></div>
                            </div>
                            <span style={{fontWeight:600,color:p.flag==='overpacing'?COLORS.red:p.flag==='underpacing'?'#F59E0B':COLORS.green,minWidth:36}}>{p.pacingPct}%</span>
                          </div>
                          {p.flag==='underpacing'&&<div style={{fontSize:10,color:'#F59E0B',marginTop:2}}>⚠ Underpacing — budget being underutilised</div>}
                          {p.flag==='overpacing'&&<div style={{fontSize:10,color:COLORS.red,marginTop:2}}>⚠ Overpacing — may run out of budget</div>}
                          {p.impressionShare!=null&&<div style={{fontSize:10,color:COLORS.muted,marginTop:2}}>Impression share: {p.impressionShare}% | Budget lost: {p.budgetLostShare}% | Rank lost: {p.rankLostShare}%</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </SectionCard>
            )}

            {/* Ad Quality tab */}
            {adsTab === 'Ad Quality' && (
              <div>
                {adQualityLoading&&<div style={{padding:20,textAlign:'center',color:COLORS.muted}}>Loading ad quality data...</div>}
                {adQuality&&!adQualityLoading&&(<>
                  <div style={{display:'flex',gap:12,flexWrap:'wrap',marginBottom:14}}>
                    {(adQuality.campaigns||[]).map((c,i)=>{
                      const adAvg=Math.round((adQuality.keywords||[]).filter(k=>k.campaign===c.name).reduce((s,k)=>s+k.adScore,0)/Math.max(1,(adQuality.keywords||[]).filter(k=>k.campaign===c.name).length));
                      const pageAvg=Math.round((adQuality.keywords||[]).filter(k=>k.campaign===c.name).reduce((s,k)=>s+k.pageScore,0)/Math.max(1,(adQuality.keywords||[]).filter(k=>k.campaign===c.name).length));
                      const toFix=(adQuality.keywords||[]).filter(k=>k.campaign===c.name&&k.overallScore<50&&(k.spend>10||k.qs)).length;
                      return(<SectionCard key={i} title={c.name.replace('Back-up fixed terms campaign','Back-up').replace('Mental health - £59','MH £59')}>
                        <div style={{display:'flex',gap:16,flexWrap:'wrap',marginBottom:8}}>
                          <div style={{fontSize:13}}><b>{c.overallScore||0}%</b> overall</div>
                          <div style={{fontSize:13,color:COLORS.muted}}>📝 Ad: {adAvg}%</div>
                          <div style={{fontSize:13,color:COLORS.muted}}>🌐 Page: {pageAvg}%</div>
                          {toFix>0&&<div style={{fontSize:13,color:COLORS.red}}>{toFix} keywords need fixing</div>}
                        </div>
                      </SectionCard>);
                    })}
                  </div>
                  {/* Mobile Performance (PageSpeed) */}
                  {(()=>{
                    const urls=[...new Set((adQuality.adGroups||[]).map(ag=>ag.url).filter(Boolean))];
                    if(urls.length===0) return null;
                    const scoreColor=(s)=>s==null?COLORS.muted:s<50?COLORS.red:s<90?'#F59E0B':COLORS.green;
                    const ratingColor=(r)=>r==='good'?COLORS.green:r==='needs-improvement'?'#F59E0B':r==='poor'?COLORS.red:COLORS.muted;
                    return(<div style={{marginBottom:16}}>
                      <div style={{fontWeight:600,fontSize:14,marginBottom:4}}>📱 Mobile Performance (Google PageSpeed)</div>
                      <div style={{fontSize:11,color:COLORS.muted,marginBottom:10}}>Mobile Lighthouse score and Core Web Vitals per landing page (cached 7 days).</div>
                      <div style={{display:'flex',flexDirection:'column',gap:10}}>
                        {urls.map(url=>{
                          const ps=pageSpeed[url];
                          const ags=(adQuality.adGroups||[]).filter(a=>a.url===url);
                          const usedBy=[...new Set(ags.map(a=>(a.campaign||'').replace('Back-up fixed terms campaign','Back-up').replace('Mental health - £59','MH £59')))].filter(Boolean).join(', ');
                          return(<div key={url} style={{border:'1px solid #E5E7EB',borderRadius:8,padding:12,display:'flex',gap:14,alignItems:'flex-start',flexWrap:'wrap'}}>
                            {ps?.data?.screenshot&&<img src={ps.data.screenshot} alt="mobile screenshot" loading="lazy" style={{width:280,maxWidth:'100%',borderRadius:6,border:'1px solid #E5E7EB'}} />}
                            <div style={{flex:1,minWidth:240}}>
                              <div style={{fontSize:12,wordBreak:'break-all',marginBottom:2}}><a href={url} target="_blank" rel="noreferrer" style={{color:COLORS.blue,textDecoration:'none'}}>{url}</a></div>
                              {usedBy&&<div style={{fontSize:11,color:COLORS.muted,marginBottom:8}}>{usedBy}</div>}
                              {!ps||ps.loading?<div style={{fontSize:12,color:COLORS.muted}}>Measuring mobile performance…</div>
                               :ps.error?<div style={{fontSize:12,color:COLORS.red}}>PageSpeed unavailable: {ps.error}</div>
                               :(<>
                                 <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                                   <div style={{width:46,height:46,borderRadius:'50%',border:'3px solid '+scoreColor(ps.data.score),display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:15,color:scoreColor(ps.data.score)}}>{ps.data.score??'—'}</div>
                                   <div style={{fontSize:12,color:COLORS.muted}}>mobile score{ps.data.cached?'':' · just measured'}</div>
                                 </div>
                                 <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                                   {['LCP','CLS','TBT','FCP'].map(m=>(
                                     <span key={m} title={ps.data.ratings?.[m]||''} style={{fontSize:11,padding:'3px 8px',borderRadius:12,background:ratingColor(ps.data.ratings?.[m])+'22',color:ratingColor(ps.data.ratings?.[m]),fontWeight:600}}>{m} {ps.data.displays?.[m]||'—'}</span>
                                   ))}
                                 </div>
                               </>)}
                            </div>
                          </div>);
                        })}
                      </div>
                    </div>);
                  })()}
                  {/* Ad Group Overlap Comparison */}
                  {(()=>{
                    const kws = adQuality.keywords || [];
                    const byKw = {};
                    kws.forEach(k => {
                      const key = k.keyword + '|||' + k.campaign;
                      if(!byKw[key]) byKw[key] = [];
                      byKw[key].push(k);
                    });
                    const overlapping = Object.entries(byKw)
                      .filter(([,arr]) => arr.length > 1)
                      .map(([key,arr]) => ({
                        keyword: arr[0].keyword,
                        campaign: arr[0].campaign,
                        adGroups: arr.sort((a,b) => (b.qs||0)-(a.qs||0))
                      }))
                      .sort((a,b) => {
                        const aSpread = Math.max(...a.adGroups.map(x=>x.qs||0)) - Math.min(...a.adGroups.map(x=>x.qs||0));
                        const bSpread = Math.max(...b.adGroups.map(x=>x.qs||0)) - Math.min(...b.adGroups.map(x=>x.qs||0));
                        return bSpread - aSpread;
                      });
                    if(overlapping.length === 0) return null;
                    return (
                      <div style={{marginBottom:16}}>
                        <div style={{fontWeight:600,fontSize:14,marginBottom:10}}>⚡ Ad Group Overlap — {overlapping.length} keywords in multiple ad groups</div>
                        <div style={{fontSize:12,color:COLORS.muted,marginBottom:10}}>Same keyword appearing in multiple ad groups — Google will pick the winner per auction, splitting signal and potentially inflating CPCs. The highest QS ad group is the recommended one to consolidate into.</div>
                        {overlapping.slice(0,15).map((ov,i) => {
                          const winner = ov.adGroups[0];
                          const hasSpread = (winner.qs||0) > (ov.adGroups[ov.adGroups.length-1].qs||0);
                          return (
                            <div key={i} style={{background: hasSpread ? '#FFFBEB' : '#F9FAFB', border:'1px solid '+(hasSpread?'#FCD34D':'#E5E7EB'), borderRadius:8, padding:'10px 14px', marginBottom:8}}>
                              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                                <div style={{fontWeight:600,fontSize:13}}>"{ov.keyword}"</div>
                                <div style={{fontSize:11,color:COLORS.muted}}>{ov.campaign.replace('Back-up fixed terms campaign','Back-up').replace('Mental health - £59','MH £59')}</div>
                              </div>
                              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                                {ov.adGroups.map((ag,j) => (
                                  <div key={j} style={{
                                    background: j===0 ? (hasSpread ? '#FEF3C7' : '#F0FDF4') : '#fff',
                                    border:'1px solid '+(j===0 ? (hasSpread ? '#FCD34D' : '#86EFAC') : '#E5E7EB'),
                                    borderRadius:6, padding:'6px 10px', fontSize:12, minWidth:140
                                  }}>
                                    <div style={{fontWeight:600,marginBottom:2}}>{ag.adGroup} {j===0 && hasSpread ? '👑' : j===0 ? '✓' : ''}</div>
                                    <div style={{display:'flex',gap:8,color:COLORS.muted}}>
                                      <span>QS <b style={{color:ag.qs>=7?COLORS.green:ag.qs>=4?'#F59E0B':COLORS.red}}>{ag.qs||'—'}</b></span>
                                      <span>Ad {ag.adScore}%</span>
                                      <span>Page {ag.pageScore}%</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {hasSpread && <div style={{fontSize:11,color:'#92400E',marginTop:6}}>⚠️ QS gap of {(winner.qs||0) - (ov.adGroups[ov.adGroups.length-1].qs||0)} points — consider pausing in lower-QS ad groups and consolidating into <b>{winner.adGroup}</b></div>}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {(adQuality.adGroups||[]).map((ag,i)=>{
                    const agKws=(adQuality.keywords||[]).filter(k=>k.adGroup===ag.name&&k.campaign===ag.campaign);
                    const poorKws=agKws.filter(k=>k.overallScore<50&&(k.spend>10||k.qs));
                    if(poorKws.length===0) return null;
                    return(<div key={i} style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:8,marginBottom:10,overflow:'hidden'}}>
                      <div onClick={()=>setExpandedAdGroups(p=>({...p,[ag.name+ag.campaign]:!p[ag.name+ag.campaign]}))} style={{display:'flex',alignItems:'center',gap:10,padding:'12px 16px',cursor:'pointer'}}>
                        <span style={{fontSize:12,color:COLORS.muted,transform:expandedAdGroups[ag.name+ag.campaign]?'rotate(90deg)':'rotate(0deg)',transition:'transform 0.15s'}}>▶</span>
                        <span style={{fontWeight:600,fontSize:14}}>{ag.campaign.replace('Back-up fixed terms campaign','Back-up').replace('Mental health - £59','MH £59')} — {ag.name}</span>
                        <span style={{fontSize:12,color:COLORS.red,marginLeft:'auto'}}>{poorKws.length} to fix</span>
                      </div>
                      {expandedAdGroups[ag.name+ag.campaign]&&(<div style={{padding:'0 16px 14px',borderTop:'1px solid #F3F4F6'}}>
                        <div style={{marginBottom:12}}>
                          <div style={{fontSize:11,fontWeight:600,color:COLORS.muted,marginBottom:6}}>📝 AD HEADLINES</div>
                          <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                            {(typeof ag.headlines==='string'?ag.headlines.split('|').map(h=>h.trim()):ag.headlines||[]).map((h,hi)=>(<span key={hi} style={{fontSize:11,background:'#F3F4F6',borderRadius:4,padding:'2px 8px'}}>"{h}"</span>))}
                          </div>
                          <div style={{fontSize:11,color:COLORS.muted,marginTop:6}}>🌐 LANDING PAGE</div>
                          <div style={{fontSize:11,marginTop:2}}>{ag.url} <span style={{color:COLORS.muted}}>— {ag.h1||'(H1 not found)'}</span></div>
                        </div>
                        <div style={{fontSize:11,fontWeight:600,color:COLORS.muted,marginBottom:8}}>⚠️ KEYWORDS WITH POOR MESSAGE MATCH</div>
                        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                          <thead><tr style={{borderBottom:'1px solid #E5E7EB'}}>
                            <th style={{padding:'4px 8px',textAlign:'left',fontWeight:600,color:COLORS.muted}}>Keyword</th>
                            <th style={{padding:'4px 8px',textAlign:'left',fontWeight:600,color:COLORS.muted}}>QS</th>
                            <th style={{padding:'4px 8px',textAlign:'left',fontWeight:600,color:COLORS.muted}}>Ad Match</th>
                            <th style={{padding:'4px 8px',textAlign:'left',fontWeight:600,color:COLORS.muted}}>Page Match</th>
                            <th style={{padding:'4px 8px',textAlign:'left',fontWeight:600,color:COLORS.muted}}>Spend</th>
                            <th style={{padding:'4px 8px',textAlign:'left',fontWeight:600,color:COLORS.muted}}>Fix</th>
                            <th style={{padding:'4px 8px',textAlign:'left',fontWeight:600,color:COLORS.muted}}>Suggest</th>
                          </tr></thead>
                          <tbody>
                            {poorKws.map((k,ki)=>(
                              <tr key={ki} style={{borderBottom:'1px solid #F3F4F6'}}>
                                <td style={{padding:'4px 8px',fontWeight:500}}>{k.keyword}</td>
                                <td style={{padding:'4px 8px'}}>{k.qs||'—'}</td>
                                <td style={{padding:'4px 8px'}}>{k.adScore!=null?k.adScore+'%':'—'}</td>
                                <td style={{padding:'4px 8px'}}>{k.pageScore!=null?k.pageScore+'%':'—'}</td>
                                <td style={{padding:'4px 8px'}}>£{k.spend||0}</td>
                                <td style={{padding:'4px 8px',fontSize:10,color:COLORS.muted}}>{k.adScore<50&&k.pageScore<50?'📝🌐 Both':k.adScore<50?'📝 Ad':k.pageScore<50?'🌐 Page':'—'}</td>
                                <td style={{padding:'4px 8px'}}>
                                  {copySuggestions[k.keyword]?(<div style={{fontSize:10,background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:4,padding:4}}>
                                    {(copySuggestions[k.keyword].headlines||[]).slice(0,3).map((h,hi)=>(<div key={hi}>"{h}"</div>))}
                                  </div>):(<button onClick={async()=>{setCopyLoading(p=>({...p,[k.keyword]:true}));try{const r=await authFetch('/api/ads/suggest-copy',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({keyword:k.keyword,headlines:typeof ag.headlines==='string'?ag.headlines.split('|').map(h=>h.trim()):ag.headlines,landingPageH1:ag.h1,adScore:k.adScore,pageScore:k.pageScore})});const d=await r.json();if(d.ok)setCopySuggestions(p=>({...p,[k.keyword]:d}));}catch(e){}setCopyLoading(p=>({...p,[k.keyword]:false}));}} style={{fontSize:10,padding:'2px 6px',borderRadius:4,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer'}}>{copyLoading[k.keyword]?'...':'💡 Suggest'}</button>)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>)}
                    </div>);
                  })}
                </>)}
              </div>
            )}

            {/* Page Ideas tab */}
            {adsTab === 'Page Ideas' && (() => {
              if(pageOppsLoading) return <SectionCard title="Page Ideas"><div style={{padding:20,textAlign:'center',color:COLORS.muted}}>Analysing search terms with AI...</div></SectionCard>;
              if(!pageOpps) return <SectionCard title="Page Ideas"><div style={{padding:20,textAlign:'center',color:COLORS.muted}}>Open this tab to analyse landing-page opportunities.</div></SectionCard>;
              const sl=pageOpps.shortlist||[];
              const v=pageOpps.verdicts||{};
              const withVerdict=sl.map(s=>({...s,...(v[s.term]||{category:'fine',reason:'',pageAngle:''})}));
              const opps=withVerdict.filter(x=>x.category==='page_opportunity');
              const locs=withVerdict.filter(x=>x.category==='location');
              const comps=withVerdict.filter(x=>x.category==='competitor');
              const stop=new Set(['online','gp','doctor','appointment','the','for','a','to','uk','private','book','booking','my','with','near','me']);
              const clusterMap={};
              opps.forEach(o=>{const words=o.term.toLowerCase().split(/\s+/).filter(w=>w.length>3&&!stop.has(w));const key=words[0]||o.term;if(!clusterMap[key])clusterMap[key]=[];clusterMap[key].push(o);});
              const clusters=Object.entries(clusterMap).map(([k,items])=>({key:k,items,spend:items.reduce((s,x)=>s+x.spend,0)})).filter(c=>c.items.length>=3&&c.spend>=100).sort((a,b)=>b.spend-a.spend);
              const clusteredTerms=new Set(clusters.flatMap(c=>c.items.map(x=>x.term)));
              const singleOpps=opps.filter(o=>!clusteredTerms.has(o.term)).sort((a,b)=>b.spend-a.spend);
              return(<div>
                <div style={{background:'#F0F9FF',border:'1px solid #BAE6FD',borderRadius:8,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#075985'}}>Relevant searches getting clicks but converting poorly, analysed by AI. Page opportunities = a dedicated or improved landing page could help.</div>
                {clusters.length>0&&(<SectionCard title={<><span style={{color:'#059669'}}>●</span>{" Page opportunities by theme ("+clusters.length+")"}</>}>
                  <div style={{fontSize:11,color:COLORS.muted,marginBottom:10}}>Clusters of 3+ related terms with £100+ combined spend — the strongest case for a dedicated page.</div>
                  {clusters.map((c,i)=>(<div key={i} style={{border:'1px solid #BBF7D0',background:'#F0FDF4',borderRadius:8,padding:12,marginBottom:10}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}><div style={{fontWeight:600,fontSize:14,textTransform:'capitalize'}}>{c.key} ({c.items.length} terms)</div><div style={{fontWeight:600,color:'#059669'}}>£{Math.round(c.spend)} combined</div></div>
                    <div style={{fontSize:12,color:COLORS.muted,marginBottom:8}}>Suggested page: {c.items[0].pageAngle||'Dedicated '+c.key+' page'}</div>
                    {c.items.map((it,j)=>(<div key={j} style={{fontSize:12,padding:'3px 0',borderTop:j>0?'1px solid #DCFCE7':'none'}}>"{it.term}" <span style={{color:COLORS.muted}}>— {it.clicks} clicks, {it.conversions} conv, £{Math.round(it.spend)}</span></div>))}
                  </div>))}
                </SectionCard>)}
                {singleOpps.length>0&&(<SectionCard title={<><span style={{color:'#059669'}}>●</span>{" Individual page opportunities ("+singleOpps.length+")"}</>}>
                  {singleOpps.map((o,i)=>(<div key={i} style={{padding:'8px 0',borderBottom:'1px solid #F3F4F6'}}>
                    <div style={{fontWeight:500,fontSize:13}}>"{o.term}"</div>
                    <div style={{fontSize:11,color:COLORS.muted}}>{o.campaign.replace('Back-up fixed terms campaign','Back-up').replace('Mental health - £59','MH £59')} — {o.clicks} clicks, {o.conversions} conv, £{Math.round(o.spend)}{o.pageScore!=null?', page match '+o.pageScore+'%':''}</div>
                    {o.pageAngle&&<div style={{fontSize:12,color:'#059669',marginTop:3}}>Suggested page: {o.pageAngle}</div>}
                    {o.reason&&<div style={{fontSize:11,color:COLORS.muted,fontStyle:'italic',marginTop:2}}>{o.reason}</div>}
                  </div>))}
                </SectionCard>)}
                {locs.length>0&&(<SectionCard title={"📍 Location searches ("+locs.length+") — online-only, a page will not fully serve these"}>
                  {locs.map((o,i)=>(<div key={i} style={{fontSize:12,padding:'4px 0',borderBottom:'1px solid #F3F4F6'}}>"{o.term}" <span style={{color:COLORS.muted}}>— {o.clicks} clicks, £{Math.round(o.spend)}</span></div>))}
                </SectionCard>)}
                {comps.length>0&&(<SectionCard title={<><span style={{color:COLORS.red}}>●</span>{" Competitor searches ("+comps.length+") — consider negativing"}</>}>
                  {comps.map((o,i)=>(<div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:12,padding:'4px 0',borderBottom:'1px solid #F3F4F6'}}>
                    <span>"{o.term}" <span style={{color:COLORS.muted}}>— {o.clicks} clicks, £{Math.round(o.spend)}</span></span>
                    <button onClick={()=>openKwModal(o.term,o.campaign,'negative')} style={{color:'#991B1B',fontSize:11,fontWeight:600,background:'#FEF2F2',border:'1px solid #FCA5A5',borderRadius:4,padding:'2px 8px',cursor:'pointer'}}>Negative</button>
                  </div>))}
                </SectionCard>)}
              </div>);
            })()}

            {/* Impression Share (competitive pressure) tab — replaces the old API-broken auction-insights tab */}
            {adsTab === 'Impression Share' && (
              <SectionCard title="Impression Share (competitive pressure)">
                <div style={{fontSize:11,color:COLORS.muted,marginBottom:12,lineHeight:1.6}}>
                  <b>Impression share</b> = the % of available auctions your ads showed in.{' '}
                  <b style={{color:COLORS.red}}>Lost to rank</b> = % missed because competitors out-rank you on Ad Rank (bid / Quality Score) — flagged red over 20%.{' '}
                  <b style={{color:'#B45309'}}>Lost to budget</b> = % missed because the campaign ran out of budget — flagged over 0%.{' '}
                  Low impression share with low rank-lost and low budget-lost just means limited search volume (not a problem to fix).
                </div>
                {(adsIntel?.competitivePressure||[]).length===0?<div style={{fontSize:12,color:COLORS.muted}}>No impression-share data available for the current period.</div>:(
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                    <thead><tr style={{borderBottom:'2px solid #E5E7EB'}}>
                      <th style={{padding:'5px 8px',textAlign:'left',fontWeight:600,color:COLORS.muted}}>Campaign</th>
                      <th style={{padding:'5px 8px',textAlign:'left',fontWeight:600,color:COLORS.muted}}>Impression share</th>
                      <th style={{padding:'5px 8px',textAlign:'left',fontWeight:600,color:COLORS.muted}}>Lost to rank</th>
                      <th style={{padding:'5px 8px',textAlign:'left',fontWeight:600,color:COLORS.muted}}>Lost to budget</th>
                      <th style={{padding:'5px 8px',textAlign:'left',fontWeight:600,color:COLORS.muted}}>Top</th>
                      <th style={{padding:'5px 8px',textAlign:'left',fontWeight:600,color:COLORS.muted}}>Abs. top</th>
                    </tr></thead>
                    <tbody>
                      {(adsIntel?.competitivePressure||[]).map((c)=>{
                        const rankBad=(c.rankLostShare||0)>20, budgetBad=(c.budgetLostShare||0)>0;
                        return (
                        <tr key={c.campaign} style={{borderBottom:'1px solid #F3F4F6'}}>
                          <td style={{padding:'5px 8px',fontWeight:500}}>{shortenCampaign(c.campaign)}</td>
                          <td style={{padding:'5px 8px'}}>
                            <div style={{display:'flex',alignItems:'center',gap:6}}>
                              <div style={{flex:'0 0 70px',height:8,background:'#F3F4F6',borderRadius:4,overflow:'hidden'}}>
                                <div style={{width:Math.min(100,Math.max(0,c.impressionShare||0))+'%',height:'100%',background:COLORS.green}} />
                              </div>
                              <span>{c.impressionShare!=null?c.impressionShare+'%':'—'}</span>
                            </div>
                          </td>
                          <td style={{padding:'5px 8px',color:rankBad?COLORS.red:COLORS.text,fontWeight:rankBad?600:400}}>{c.rankLostShare!=null?c.rankLostShare+'%':'—'}{rankBad?' ⚠️':''}</td>
                          <td style={{padding:'5px 8px',color:budgetBad?'#B45309':COLORS.muted,fontWeight:budgetBad?600:400}}>{c.budgetLostShare!=null?c.budgetLostShare+'%':'—'}{budgetBad?' ⚠️':''}</td>
                          <td style={{padding:'5px 8px'}}>{c.topShare!=null?c.topShare+'%':'—'}</td>
                          <td style={{padding:'5px 8px'}}>{c.absoluteTopShare!=null?c.absoluteTopShare+'%':'—'}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </SectionCard>
            )}

            {/* Cannibalism tab */}
            {adsTab === 'Cannibalism' && (
              <SectionCard title="Keyword Cannibalism">
                {(adsIntel?.cannibalism||[]).length===0?<div style={{fontSize:12,color:COLORS.muted}}>No cannibalism detected in this range.</div>:(
                  <div>
                    <div style={{fontSize:12,color:COLORS.muted,marginBottom:12}}>Keywords competing for the same searches across campaigns. This can split budget and confuse Google's bidding.</div>
                    {(adsIntel?.cannibalism||[]).map((group,i)=>{
                      const objs=(group.campaigns||[]).filter(c=>typeof c==='object').map(c=>({...c,cpa:c.conversions>0?c.spend/c.conversions:(c.spend>0?Infinity:0)}));
                      const sorted=[...objs].sort((a,b)=>(b.cpa-a.cpa)||(b.spend-a.spend)); // worst CPA (the loser) first; tie-break on spend
                      const losing=sorted[0], winning=sorted[sorted.length-1];
                      const canPause=losing&&winning&&losing.campaign!==winning.campaign&&losing.spend>0;
                      const kwMatch=canPause?(adsIntel?.keywords||[]).filter(k=>k.campaign===losing.campaign&&(k.keyword||'').toLowerCase()===(group.keyword||'').toLowerCase()).sort((a,b)=>(b.spend||0)-(a.spend||0))[0]:null;
                      return(<div key={i} style={{border:'1px solid #FDE68A',background:'#FFFBEB',borderRadius:8,padding:12,marginBottom:10}}>
                        <div style={{fontWeight:600,fontSize:13,marginBottom:8}}>⚠️ "{group.keyword}" matched in {group.campaigns?.length||0} campaigns</div>
                        {(group.campaigns||[]).map((c,j)=>{
                          const obj=typeof c==='object'?c:null;
                          const name=shortenCampaign(typeof c==='string'?c:c.campaign||'');
                          const isLosing=canPause&&obj&&obj.campaign===losing.campaign;
                          return(<div key={j} style={{fontSize:12,padding:'4px 0',color:COLORS.muted,display:'flex',justifyContent:'space-between',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                            <span>{name}{isLosing&&<span style={{marginLeft:6,fontSize:9,fontWeight:600,background:'#FEE2E2',color:'#991B1B',borderRadius:4,padding:'1px 5px'}}>worse CPA</span>}</span>
                            <div style={{display:'flex',alignItems:'center',gap:10}}>
                              {obj&&<span style={{whiteSpace:'nowrap'}}>£{obj.spend} · {obj.conversions} conv</span>}
                              {isLosing&&<button onClick={()=>askPauseKeyword({keyword:kwMatch?.keyword||group.keyword,campaignName:losing.campaign,adGroupName:kwMatch?.adGroup,matchType:kwMatch?.matchType,description:(<span>Pause <b>"{group.keyword}"</b> in <b>{shortenCampaign(losing.campaign)}</b>{kwMatch?.adGroup?` / ${kwMatch.adGroup}`:''}? It's spending <b>£{losing.spend}</b> with <b>{losing.conversions}</b> conversions vs {shortenCampaign(winning.campaign)}'s £{winning.spend} / {winning.conversions} conversions. This stops it serving in {shortenCampaign(losing.campaign)} only.</span>)})} style={{color:'#991B1B',fontSize:11,fontWeight:600,background:'#FEF2F2',border:'1px solid #FCA5A5',borderRadius:4,padding:'3px 9px',cursor:'pointer',whiteSpace:'nowrap'}}>🚫 Pause in {shortenCampaign(losing.campaign)}</button>}
                            </div>
                          </div>);
                        })}
                      </div>);
                    })}
                  </div>
                )}
              </SectionCard>
            )}

            {/* Change Log tab */}
            {adsTab === 'Change Log' && (
              <div>
                <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',marginBottom:12}}>
                  <button onClick={syncAdsHistory} disabled={syncingHistory} style={{padding:'6px 14px',borderRadius:6,border:'1px solid '+COLORS.purple,background:'#fff',color:COLORS.purple,cursor:syncingHistory?'default':'pointer',fontSize:12,fontWeight:600,opacity:syncingHistory?0.6:1}}>{syncingHistory?'Syncing…':'↻ Sync Google Ads history'}</button>
                  <span style={{fontSize:11,color:COLORS.muted}}>Auto-imports the last 30 days of account changes (also runs hourly).</span>
                  {syncHistoryResult&&(syncHistoryResult.ok
                    ? <span style={{fontSize:12,color:COLORS.green}}>✓ Imported {syncHistoryResult.imported} new change{syncHistoryResult.imported===1?'':'s'}</span>
                    : <span style={{fontSize:12,color:COLORS.red}}>✕ {syncHistoryResult.error}</span>)}
                </div>
                {changeWarning&&(<div style={{background:'#FFFBEB',border:'1px solid #FCD34D',borderRadius:8,padding:'10px 14px',marginBottom:12,fontSize:12,color:'#92400E',display:'flex',justifyContent:'space-between',alignItems:'center',gap:10}}>
                  <span>⚠️ {changeWarning}</span>
                  <button onClick={()=>setChangeWarning(null)} style={{background:'transparent',border:'none',cursor:'pointer',color:'#92400E',fontSize:12}}>✕</button>
                </div>)}
                {showChangeForm&&(<SectionCard title="Log a change">
                  <div style={{display:'flex',flexDirection:'column',gap:10}}>
                    {/* Website changes are judged on funnel rates, which need about six weeks either side, hence a 42-day review */}
                    <select value={changeForm.change_type} onChange={e=>{const t=e.target.value; setChangeForm(f=>({...f,change_type:t,observation_days:[14,42].includes(f.observation_days)?(['Website','Landing Page'].includes(t)?42:14):f.observation_days}));}} style={{padding:'6px 10px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:13}}>
                      <option>Google Ads</option><option>Website</option><option>Landing Page</option><option>Budget</option><option>Other</option>
                    </select>
                    <input placeholder="Campaign or page" list="site-change-pages" value={changeForm.campaign_or_page} onChange={e=>setChangeForm(f=>({...f,campaign_or_page:e.target.value}))} style={{padding:'6px 10px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:13}} />
                    <datalist id="site-change-pages">{(mh59SiteChanges?.pages||[]).map(p=><option key={p.path} value={p.path}>{p.label}</option>)}</datalist>
                    {['Website','Landing Page'].includes(changeForm.change_type)&&<div style={{fontSize:11,color:COLORS.muted,marginTop:-4}}>Use the page's address, e.g. /mental-health-assessment-2/. Changes to a £59 page show as ◆ on the MH Funnel tab.</div>}
                    <textarea placeholder="What did you change and why?" value={changeForm.notes} onChange={e=>setChangeForm(f=>({...f,notes:e.target.value}))} style={{padding:'6px 10px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:13,minHeight:80,resize:'vertical'}} />
                    <div style={{display:'flex',gap:8,alignItems:'center'}}>
                      <span style={{fontSize:12,color:COLORS.muted}}>Went live on</span>
                      <input type="date" value={changeForm.changed_on||new Date().toLocaleDateString('en-CA')} max={new Date().toLocaleDateString('en-CA')} onChange={e=>setChangeForm(f=>({...f,changed_on:e.target.value}))} style={{padding:'6px 10px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:13}} />
                    </div>
                    <div style={{display:'flex',gap:8,alignItems:'center'}}>
                      <span style={{fontSize:12,color:COLORS.muted}}>Review in</span>
                      <input type="number" value={changeForm.observation_days} onChange={e=>setChangeForm(f=>({...f,observation_days:parseInt(e.target.value)}))} style={{padding:'6px 10px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:13,width:60}} />
                      <span style={{fontSize:12,color:COLORS.muted}}>days</span>
                    </div>
                    <div style={{display:'flex',gap:8}}>
                      <button onClick={submitChange} style={{padding:'8px 16px',borderRadius:6,border:'none',background:COLORS.purple,color:'#fff',cursor:'pointer',fontWeight:600}}>Save</button>
                      <button onClick={()=>setShowChangeForm(false)} style={{padding:'8px 16px',borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer'}}>Cancel</button>
                    </div>
                  </div>
                </SectionCard>)}
                <SectionCard title={"Change Log ("+changes.length+" entries)"}>
                  {changes.length>0&&<div style={{display:'flex',justifyContent:'flex-end',marginBottom:8}}><button onClick={()=>downloadCSV('change-log.csv',[{label:'Type',key:'change_type'},{label:'Campaign/Page',key:'campaign_or_page'},{label:'Notes',key:'notes'},{label:'Changed',get:r=>new Date(r.changed_at*1000).toLocaleDateString('en-GB')},{label:'Review',get:r=>new Date(r.review_date*1000).toLocaleDateString('en-GB')},{label:'Verdict',get:r=>changeEvals[r.id]?.verdict||''}],changes)} style={{fontSize:11,padding:'5px 12px',borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer'}}>⬇ Download CSV</button></div>}
                  {changes.length===0?<div style={{fontSize:12,color:COLORS.muted}}>No changes logged yet.</div>:changes.map((ch,i)=>{
                    const reviewDate=new Date(ch.review_date*1000);
                    const isAuto=ch.change_type==='Google Ads (auto)';
                    const overdue=reviewDate<new Date()&&!isAuto; // auto records aren't "reviews"
                    const ev=changeEvals[ch.id];
                    return(<div key={i} style={{padding:'10px 0',borderBottom:'1px solid #F3F4F6'}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:10}}>
                        <div style={{flex:1}}>
                          <div style={{fontWeight:500,fontSize:13,display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                            {isAuto&&<span style={{fontSize:9,fontWeight:600,background:'#EDE9FE',color:'#5B21B6',borderRadius:4,padding:'1px 6px',textTransform:'uppercase',letterSpacing:'0.04em'}}>auto</span>}
                            <span>{isAuto?'Google Ads':ch.change_type}{ch.campaign_or_page?' — '+ch.campaign_or_page:''}</span>
                          </div>
                          <div style={{fontSize:12,color:COLORS.muted,marginTop:2}}>{ch.notes}</div>
                          <div style={{fontSize:11,color:COLORS.muted,marginTop:4}}>{new Date(ch.changed_at*1000).toLocaleDateString('en-GB')}{isAuto?'':` · review ${reviewDate.toLocaleDateString('en-GB')}`} {overdue?<span style={{color:COLORS.red,fontWeight:600}}>⚠ overdue</span>:null}</div>
                          {ev&&(
                            <div style={{marginTop:6,fontSize:11,padding:'6px 10px',borderRadius:6,background:ev.verdict==='working'?'#F0FDF4':ev.verdict==='worse'?'#FEF2F2':'#F9FAFB',border:'1px solid '+(ev.verdict==='working'?'#86EFAC':ev.verdict==='worse'?'#FCA5A5':'#E5E7EB'),color:ev.verdict==='working'?'#065F46':ev.verdict==='worse'?'#991B1B':'#6B7280'}}>
                              {ev.verdict==='working'?'✓ Working':ev.verdict==='worse'?'✗ Worse':'– No clear change'} — 14d before vs after:
                              {' '}CPA {ev.before_cpa!=null?'£'+ev.before_cpa:'n/a'} → {ev.after_cpa!=null?'£'+ev.after_cpa:'n/a'},
                              {' '}conv {ev.before_conv} → {ev.after_conv}
                            </div>
                          )}
                        </div>
                        <button onClick={()=>deleteChange(ch.id)} style={{fontSize:10,color:COLORS.muted,background:'transparent',border:'none',cursor:'pointer',padding:4}}>✕</button>
                      </div>
                    </div>);
                  })}
                </SectionCard>
              </div>
            )}

            {/* Settings tab */}
            {adsTab === 'Settings' && (() => {
              const settings=adsIntel?.adsSettings||{};
              const campaigns=Object.keys(settings);
              const targetCpaMap=adsIntel?.targetCpaMap||{};
              const getTargetCpa=(campaign)=>targetCpaMap[campaign]||parseFloat(m.cac)||42;
              return(<div>
                <div style={{background:'#F0F9FF',border:'1px solid #BAE6FD',borderRadius:8,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#075985'}}>
                  These control when a keyword/search term is flagged as waste. A term is waste if it has 0 conversions AND (clicks >= click threshold) OR (spend >= spend multiple x target CPA AND clicks >= click floor).
                </div>
                <SectionCard title="Action Plan — wrong-intent filter (global)">
                  <div style={{fontSize:12,color:COLORS.muted,marginBottom:10}}>Which search terms reach the Action Plan as negative / wrong-intent candidates: a term must have at least this many clicks, and at least this much spend unless it has zero conversions. Applies across all campaigns.</div>
                  <div style={{display:'flex',gap:20,flexWrap:'wrap',alignItems:'flex-end'}}>
                    <div>
                      <div style={{fontSize:11,color:COLORS.muted,marginBottom:4}}>Click threshold</div>
                      <input type="number" value={negDraft.neg_filter_click_threshold??negSettings.neg_filter_click_threshold} onChange={e=>setNegDraft(p=>({...p,neg_filter_click_threshold:e.target.value}))} style={{padding:'6px 10px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:13,width:80}} />
                    </div>
                    <div>
                      <div style={{fontSize:11,color:COLORS.muted,marginBottom:4}}>Spend threshold (£)</div>
                      <input type="number" step="0.5" value={negDraft.neg_filter_spend_threshold??negSettings.neg_filter_spend_threshold} onChange={e=>setNegDraft(p=>({...p,neg_filter_spend_threshold:e.target.value}))} style={{padding:'6px 10px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:13,width:80}} />
                    </div>
                    <button onClick={async()=>{const ct=negDraft.neg_filter_click_threshold??negSettings.neg_filter_click_threshold;const st=negDraft.neg_filter_spend_threshold??negSettings.neg_filter_spend_threshold;const r=await authFetch('/api/ads/global-settings',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({neg_filter_click_threshold:ct,neg_filter_spend_threshold:st})});const d=await r.json();setNegSettingsResult(d);if(d.ok){setNegSettings(d.settings||{neg_filter_click_threshold:Number(ct),neg_filter_spend_threshold:Number(st)});setNegDraft({});}}} style={{padding:'8px 16px',borderRadius:6,border:'none',background:COLORS.purple,color:'#fff',cursor:'pointer',fontWeight:600,fontSize:13}}>Save</button>
                  </div>
                  {negSettingsResult&&<div style={{fontSize:12,color:negSettingsResult.ok?COLORS.green:COLORS.red,marginTop:10}}>{negSettingsResult.ok?'✓ Saved':'✕ '+(negSettingsResult.error||'failed')}</div>}
                </SectionCard>
                {campaigns.map((c,i)=>{
                  const s=settings[c];
                  const draft=adsSettingsDraft[c]||{};
                  const ct=draft.clickThreshold??s.clickThreshold;
                  const sm=draft.spendMultiple??s.spendMultiple;
                  const cf=draft.clickFloor??s.clickFloor;
                  return(<SectionCard key={i} title={c.replace('Back-up fixed terms campaign','Back-up').replace('Mental health - £59','MH £59')}>
                    <div style={{display:'flex',gap:20,flexWrap:'wrap',alignItems:'flex-end'}}>
                      <div>
                        <div style={{fontSize:11,color:COLORS.muted,marginBottom:4}}>Click threshold</div>
                        <input type="number" value={ct} onChange={e=>setAdsSettingsDraft(p=>({...p,[c]:{...p[c],clickThreshold:e.target.value}}))} style={{padding:'6px 10px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:13,width:80}} />
                        <div style={{fontSize:10,color:COLORS.muted,marginTop:2}}>{ct}/30d</div>
                      </div>
                      <div>
                        <div style={{fontSize:11,color:COLORS.muted,marginBottom:4}}>Spend multiple (x target CPA)</div>
                        <input type="number" step="0.5" value={sm} onChange={e=>setAdsSettingsDraft(p=>({...p,[c]:{...p[c],spendMultiple:e.target.value}}))} style={{padding:'6px 10px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:13,width:80}} />
                        <div style={{fontSize:10,color:COLORS.muted,marginTop:2}}>£{Math.round(getTargetCpa(c)*sm)} trigger</div>
                      </div>
                      <div>
                        <div style={{fontSize:11,color:COLORS.muted,marginBottom:4}}>Click floor</div>
                        <input type="number" value={cf} onChange={e=>setAdsSettingsDraft(p=>({...p,[c]:{...p[c],clickFloor:e.target.value}}))} style={{padding:'6px 10px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:13,width:80}} />
                      </div>
                      <button onClick={async()=>{const r=await authFetch('/api/ads/settings',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({campaign:c,clickThreshold:ct,spendMultiple:sm,clickFloor:cf})});const d=await r.json();setSettingsResult({campaign:c,...d});if(d.ok)fetchAdsIntel(range);}} style={{padding:'8px 16px',borderRadius:6,border:'none',background:COLORS.purple,color:'#fff',cursor:'pointer',fontWeight:600,fontSize:13}}>Save</button>
                    </div>
                    {settingsResult&&settingsResult.campaign===c&&<div style={{fontSize:12,color:settingsResult.ok?COLORS.green:COLORS.red,marginTop:10}}>{settingsResult.ok?'✓ Saved':'✕ '+settingsResult.error}</div>}
                  </SectionCard>);
                })}
              </div>);
            })()}

            {/* Action Plan tab */}
            {adsTab === 'Action Plan' && (
              <div>
                {/* Business Context Panel */}
                <div style={{background:'#F8FAFC',border:'1px solid #E5E7EB',borderRadius:8,padding:16,marginBottom:16}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                    <div style={{fontWeight:600,fontSize:14}}>🏢 Business Context</div>
                    <button onClick={()=>{setEditingContext(true);setContextDraft(businessContext||{});}} style={{fontSize:12,padding:'4px 10px',borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer'}}>Edit</button>
                  </div>
                  {editingContext ? (
                    <div style={{display:'flex',flexDirection:'column',gap:10}}>
                      <div style={{fontSize:11,color:COLORS.muted,marginBottom:2}}>Anything Claude should know that the data can't tell it — upcoming changes, strategic priorities, context about the business.</div>
                      <textarea value={contextDraft.other_context||''} onChange={e=>setContextDraft(p=>({...p,other_context:e.target.value}))} placeholder="e.g. We're launching a new MH landing page next week. GP price increasing to £59 in July. Focusing on MH growth this quarter..." style={{width:'100%',padding:'8px 10px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:13,minHeight:100,resize:'vertical',boxSizing:'border-box'}} />
                      <div style={{fontSize:11,color:COLORS.muted,marginTop:6,marginBottom:2}}>In-scope service terms (negative-keyword protection)</div>
                      <textarea value={contextDraft.in_scope_terms||''} onChange={e=>setContextDraft(p=>({...p,in_scope_terms:e.target.value}))} placeholder="One term per line. Terms containing these words will never be suggested as negatives (e.g. 'daridorexant', 'weight loss', 'travel vaccination')." style={{width:'100%',padding:'8px 10px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:13,minHeight:70,resize:'vertical',boxSizing:'border-box'}} />
                      <div style={{display:'flex',gap:8}}>
                        <button onClick={saveBusinessContext} style={{padding:'8px 16px',borderRadius:6,border:'none',background:COLORS.purple,color:'#fff',cursor:'pointer',fontWeight:600}}>Save</button>
                        <button onClick={()=>setEditingContext(false)} style={{padding:'8px 16px',borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer'}}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{fontSize:12,color:COLORS.muted}}>
                      {businessContext?.other_context ? <span style={{color:COLORS.text}}>{businessContext.other_context}</span> : <span>Nothing added yet — click Edit to give Claude context the data can't provide.</span>}
                      {businessContext?.in_scope_terms && <div style={{marginTop:6}}><span style={{fontWeight:600,color:COLORS.text}}>In-scope (never negatived):</span> <span style={{color:COLORS.text}}>{businessContext.in_scope_terms.split(/[\n,]+/).map(s=>s.trim()).filter(Boolean).join(', ')}</span></div>}
                      {contextSaved&&<div style={{color:COLORS.green,marginTop:4}}>✓ Saved</div>}
                    </div>
                  )}
                </div>

                {/* Open tasks panel — tracked recommendations from prior reports */}
                {(actionPlanTasks.open.length > 0 || actionPlanTasks.completed.length > 0) && (
                  <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:8,padding:16,marginBottom:16}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
                      <div style={{fontWeight:600,fontSize:14}}>📋 Open tasks</div>
                      {actionPlanTasks.open.length>0 && <span style={{fontSize:11,fontWeight:700,color:'#fff',background:COLORS.purple,borderRadius:10,padding:'1px 8px'}}>{actionPlanTasks.open.length}</span>}
                      <span style={{fontSize:11,color:COLORS.muted,marginLeft:'auto'}}>Tick when done · off-platform items must be confirmed manually</span>
                    </div>
                    {actionPlanTasks.open.length === 0
                      ? <div style={{fontSize:12,color:COLORS.muted,padding:'4px 0'}}>No open tasks — all caught up. 🎉</div>
                      : actionPlanTasks.open.map(t => {
                          const meta = TASK_CAT[t.category] || TASK_CAT.other;
                          return (
                            <div key={t.id} style={{display:'flex',alignItems:'flex-start',gap:10,padding:'9px 11px',borderRadius:6,marginBottom:6,background:meta.off?'#FAF5FF':'#F8FAFC',border:'1px solid '+(meta.off?'#E9D5FF':'#E5E7EB')}}>
                              <input type="checkbox" checked={false} onChange={()=>toggleTaskStatus(t.id,'done')} title="Mark done" style={{marginTop:2,width:16,height:16,cursor:'pointer',flexShrink:0,accentColor:COLORS.purple}} />
                              <div style={{flex:1}}>
                                <div style={{fontSize:13,color:COLORS.text,lineHeight:1.5}}>{t.task_text}</div>
                                <div style={{display:'flex',gap:6,alignItems:'center',marginTop:5,flexWrap:'wrap'}}>
                                  <span style={{fontSize:10,fontWeight:700,color:'#fff',background:meta.color,borderRadius:4,padding:'1px 6px'}}>{meta.label}</span>
                                  {meta.off
                                    ? <span style={{fontSize:10,color:'#7C3AED',fontWeight:600}}>✋ off-platform</span>
                                    : <span style={{fontSize:10,color:COLORS.muted}}>{t.detect_hint ? 'auto-tracked' : 'in-platform'}</span>}
                                </div>
                              </div>
                              <button onClick={()=>toggleTaskStatus(t.id,'dismissed')} title="Dismiss (not doing this)" style={{border:'none',background:'transparent',color:COLORS.muted,cursor:'pointer',fontSize:15,lineHeight:1,flexShrink:0,padding:'0 2px'}}>✕</button>
                            </div>
                          );
                        })}
                    {actionPlanTasks.completed.length > 0 && (
                      <div style={{marginTop:10,borderTop:'1px solid #F3F4F6',paddingTop:10}}>
                        <div style={{fontSize:11,color:COLORS.muted,marginBottom:6}}>Recently completed</div>
                        {actionPlanTasks.completed.map(t => {
                          const meta = TASK_CAT[t.category] || TASK_CAT.other;
                          const isDone = t.status === 'done';
                          return (
                            <div key={t.id} style={{display:'flex',alignItems:'center',gap:8,padding:'5px 8px',opacity:0.75}}>
                              <input type="checkbox" checked={isDone} onChange={()=>toggleTaskStatus(t.id,'open')} title="Re-open" style={{width:14,height:14,cursor:'pointer',flexShrink:0,accentColor:COLORS.green}} />
                              <span style={{fontSize:12,color:COLORS.muted,flex:1,textDecoration:isDone?'line-through':'none'}}>{t.task_text}</span>
                              <span style={{fontSize:9,fontWeight:600,color:meta.color}}>{meta.label}</span>
                              {isDone && t.completed_by==='auto' && <span style={{fontSize:9,fontWeight:700,color:'#0369A1',background:'#E0F2FE',borderRadius:4,padding:'1px 5px'}}>AUTO</span>}
                              {!isDone && <span style={{fontSize:9,color:COLORS.muted,fontStyle:'italic'}}>dismissed</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Generate button */}
                {!actionPlan && !actionPlanLoading && (
                  <div style={{textAlign:'center',padding:'40px 20px'}}>
                    {actionPlanError && <div style={{background:'#FEF2F2',border:'1px solid #FCA5A5',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:13,color:'#991B1B'}}>⚠️ Last attempt failed: {actionPlanError}</div>}
                    <div style={{fontSize:14,color:COLORS.muted,marginBottom:16}}>Generate a marketing director-level action plan using all your live data — keywords, QS, pacing, MH CPA, change log, and business context.</div>
                    <button onClick={generateActionPlan} style={{padding:'12px 32px',borderRadius:8,border:'none',background:COLORS.purple,color:'#fff',cursor:'pointer',fontSize:15,fontWeight:600}}>Generate Action Plan</button>
                    <div style={{fontSize:11,color:COLORS.muted,marginTop:8}}>Takes 20-30 seconds · Uses Claude Opus</div>
                  </div>
                )}

                {/* Loading state */}
                {actionPlanLoading && (
                  <div style={{textAlign:'center',padding:'60px 20px'}}>
                    <div style={{fontSize:24,marginBottom:16}}>🧠</div>
                    <div style={{fontSize:14,fontWeight:600,marginBottom:8}}>Analysing your account...</div>
                    <div style={{fontSize:12,color:COLORS.muted}}>Reviewing 217 keywords, search terms, QS scores, pacing, MH CPA data, and recent changes. This takes 20-30 seconds.</div>
                  </div>
                )}

                {/* Report (current or a selected historical one) */}
                {(actionPlan || actionPlanHistory.length>0) && !actionPlanLoading && (() => {
                  const latestReport = actionPlanHistory[0] || null;
                  const viewedReport = selectedReportId != null ? (actionPlanHistory.find(r=>r.id===selectedReportId)||null) : null;
                  const reportText = viewedReport ? viewedReport.report : actionPlan;
                  if(!reportText) return null;
                  const reportQuestion = viewedReport ? viewedReport.question : actionPlanQuestion;
                  const isViewingHistorical = !!viewedReport && !!latestReport && viewedReport.id !== latestReport.id;
                  const viewedAnswer = reportQuestion ? (actionPlanAnswers.find(a=>a.question===reportQuestion)||null) : null;
                  const genAt = viewedReport ? viewedReport.generated_at : (latestReport ? latestReport.generated_at : Math.floor(Date.now()/1000));
                  const genRange = (viewedReport && viewedReport.range) || (latestReport && latestReport.range) || range;
                  const dShort = (ts)=>new Date(ts*1000).toLocaleDateString('en-GB',{day:'numeric',month:'short'});
                  const dLong = (ts)=>new Date(ts*1000).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'});
                  return (
                  <div>
                    {/* Previous reports selector */}
                    {actionPlanHistory.length>1 && (
                      <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center',marginBottom:12}}>
                        <span style={{fontSize:11,color:COLORS.muted,marginRight:2}}>📅 Reports:</span>
                        <button onClick={()=>setSelectedReportId(null)} style={{padding:'4px 10px',borderRadius:6,border:'1px solid '+(selectedReportId==null?COLORS.purple:'#E5E7EB'),background:selectedReportId==null?COLORS.purple:'#fff',color:selectedReportId==null?'#fff':'#374151',cursor:'pointer',fontSize:11,fontWeight:600}}>Latest</button>
                        {actionPlanHistory.slice(1).map(r=>(
                          <button key={r.id} onClick={()=>setSelectedReportId(r.id)} title={dLong(r.generated_at)} style={{padding:'4px 10px',borderRadius:6,border:'1px solid '+(selectedReportId===r.id?COLORS.purple:'#E5E7EB'),background:selectedReportId===r.id?COLORS.purple:'#fff',color:selectedReportId===r.id?'#fff':'#374151',cursor:'pointer',fontSize:11,fontWeight:selectedReportId===r.id?600:400}}>{dShort(r.generated_at)}</button>
                        ))}
                      </div>
                    )}
                    {/* Historical banner */}
                    {isViewingHistorical && (
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10,flexWrap:'wrap',background:'#FFFBEB',border:'1px solid #FCD34D',borderRadius:8,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#92400E'}}>
                        <span>📜 Viewing report from {dLong(viewedReport.generated_at)} — this is not the current report</span>
                        <button onClick={()=>setSelectedReportId(null)} style={{padding:'4px 12px',borderRadius:6,border:'1px solid #D97706',background:'#fff',color:'#92400E',cursor:'pointer',fontSize:11,fontWeight:600,whiteSpace:'nowrap'}}>View latest</button>
                      </div>
                    )}
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
                      <div style={{fontSize:11,color:COLORS.muted}}>Generated {dLong(genAt)} · {String(genRange).toUpperCase()} data</div>
                      <button onClick={generateActionPlan} style={{fontSize:12,padding:'6px 14px',borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer'}}>↻ Regenerate</button>
                    </div>

                    {/* Tabbed report renderer */}
                    {(() => {
                      // Parse report into sections
                      const sections = {};
                      let currentKey = 'Summary';
                      sections[currentKey] = [];
                      reportText.split('\n').forEach(line => {
                        if(line.startsWith('## ')) {
                          const title = line.replace('## ','').trim();
                          const key = title.includes('Since last time') || title.includes('Since Last Time') ? 'Since last time'
                            : title.includes('Virtual GP') ? 'Virtual GP'
                            : title.includes('Mental Health') || title.includes('Mental health') ? 'Mental Health'
                            : title.includes('Back-up') || title.includes('Back-Up') ? 'Back-up'
                            : title.includes('Cross') ? 'Cross-Campaign'
                            : title.includes('Top 3') || title.includes('Actions') ? 'Actions'
                            : title.includes("Don't Touch") || title.includes('Dont Touch') ? "Don't Touch"
                            : title.includes('Question') ? 'Question'
                            : title.includes('Executive') ? 'Summary'
                            : title;
                          currentKey = key;
                          if(!sections[currentKey]) sections[currentKey] = [];
                        } else {
                          if(!sections[currentKey]) sections[currentKey] = [];
                          sections[currentKey].push(line);
                        }
                      });

                      const tabList = ['Since last time','Summary','Virtual GP','Mental Health','Back-up','Cross-Campaign','Actions',"Don't Touch",'Question','Feedback'].filter(t => t==='Feedback' || sections[t]?.some(l=>l.trim()));
                      // The selected tab may not exist in this report (e.g. after generating/switching
                      // to a report that lacks the section). Fall back to the first available tab so the
                      // user never lands on an empty pane with no button to navigate away.
                      const activeTab = tabList.includes(reportTab) ? reportTab : (tabList[0] || 'Summary');

                      const renderLines = (lines, tabKey) => {
                        if(!lines) return null;
                        return lines.map((line, i) => {
                          if(line.trim()==='') return <div key={i} style={{height:6}}/>;
                          if(line.startsWith('# ') && !line.startsWith('## ') && !line.startsWith('### ')) return <div key={i} style={{fontWeight:700,fontSize:16,color:COLORS.text,marginBottom:4}}>{line.replace('# ','')}</div>;
                          if(line.trim()==='---') return <hr key={i} style={{border:'none',borderTop:'1px solid #E5E7EB',margin:'8px 0'}}/>;
                          if(line.startsWith('### ')) return <div key={i} style={{fontWeight:700,fontSize:13,color:COLORS.text,marginTop:14,marginBottom:4}}>{line.replace('### ','')}</div>;
                          // Numbered items — cards for all tabs
                          if(line.match(/^\d+\./)) {
                            const num = line.match(/^(\d+)\.\s*/)[1];
                            const rest = line.replace(/^\d+\.\s*/, '');
                            const parts = rest.split(/(\*\*[^*]+\*\*)/g);
                            const bg = tabKey==="Don't Touch" ? '#FFFBEB' : tabKey==='Actions' ? '#F5F3FF' : '#F8FAFC';
                            const border = tabKey==="Don't Touch" ? '#FCD34D' : tabKey==='Actions' ? '#C4B5FD' : '#E5E7EB';
                            const numBg = tabKey==="Don't Touch" ? '#D97706' : tabKey==='Actions' ? COLORS.purple : '#6B7280';
                            return <div key={i} style={{display:'flex',gap:12,background:bg,border:'1px solid '+border,borderRadius:8,padding:'12px 14px',marginBottom:8,marginTop:4}}>
                              <div style={{width:24,height:24,borderRadius:'50%',background:numBg,color:'#fff',fontSize:11,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:1}}>{num}</div>
                              <div style={{fontSize:13,lineHeight:1.65,flex:1}}>{parts.map((p,j)=>p.startsWith('**')?<strong key={j} style={{fontWeight:700}}>{p.replace(/\*\*/g,'')}</strong>:<span key={j}>{p}</span>)}</div>
                            </div>;
                          }
                          // Bullet points
                          if(line.startsWith('- ') || line.startsWith('• ')) {
                            const text = line.replace(/^[-•] /,'');
                            const parts = text.split(/(\*\*[^*]+\*\*)/g);
                            return <div key={i} style={{display:'flex',gap:10,padding:'7px 10px',fontSize:13,lineHeight:1.65,background:'#FAFAFA',borderRadius:6,marginBottom:4}}>
                              <span style={{color:COLORS.purple,fontWeight:900,flexShrink:0,fontSize:18,lineHeight:'1.1'}}>•</span>
                              <div style={{flex:1}}>{parts.map((p,j)=>p.startsWith('**')?<strong key={j} style={{fontWeight:700}}>{p.replace(/\*\*/g,'')}</strong>:<span key={j}>{p}</span>)}</div>
                            </div>;
                          }
                          // Bold-only lines — section subheaders with left accent
                          if(line.startsWith('**') && line.endsWith('**')) return <div key={i} style={{fontWeight:700,fontSize:13,marginTop:16,marginBottom:6,color:'#111827',paddingLeft:10,borderLeft:'3px solid '+COLORS.purple,lineHeight:1.4}}>{line.replace(/\*\*/g,'')}</div>;
                          // Normal paragraph with inline bold
                          const parts = line.split(/(\*\*[^*]+\*\*)/g);
                          return <p key={i} style={{margin:'4px 0 8px',fontSize:13,lineHeight:1.75,color:'#374151'}}>{parts.map((p,j)=>p.startsWith('**')?<strong key={j} style={{fontWeight:700,color:'#111827'}}>{p.replace(/\*\*/g,'')}</strong>:<span key={j}>{p}</span>)}</p>;
                        });
                      };

                      const tabColors = {
                        'Since last time': '#0EA5E9',
                        'Summary': '#7C3AED',
                        'Virtual GP': '#2563EB',
                        'Mental Health': '#059669',
                        'Back-up': '#D97706',
                        'Cross-Campaign': '#0891B2',
                        'Actions': '#DC2626',
                        "Don't Touch": '#92400E',
                        'Question': '#7C3AED',
                        'Feedback': '#6B7280',
                      };

                      const tabIcons = {
                        'Since last time': '📋',
                        'Summary': '📊',
                        'Virtual GP': '🏥',
                        'Mental Health': '🧠',
                        'Back-up': '🔄',
                        'Cross-Campaign': '🔗',
                        'Actions': '⚡',
                        "Don't Touch": '⛔',
                        'Question': '💬',
                        'Feedback': '✏️',
                      };

                      return <div>
                        {/* Tab buttons */}
                        <div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:16,borderBottom:'2px solid #E5E7EB',paddingBottom:10}}>
                          {tabList.map(t=>(
                            <button key={t} onClick={()=>setReportTab(t)} style={{padding:'6px 12px',borderRadius:6,border:'none',background:activeTab===t?tabColors[t]||COLORS.purple:'#F3F4F6',color:activeTab===t?'#fff':'#374151',cursor:'pointer',fontSize:12,fontWeight:activeTab===t?600:400,transition:'all 0.15s'}}>
                              {tabIcons[t]||''} {t}
                            </button>
                          ))}
                        </div>

                        {/* Tab content */}
                        {activeTab !== 'Question' && activeTab !== 'Feedback' && (
                          <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:8,padding:20,minHeight:200}}>
                            {activeTab === 'Actions' && (
                              <div style={{background:'#FEF2F2',border:'1px solid #FCA5A5',borderRadius:6,padding:'8px 14px',marginBottom:14,fontSize:12,color:'#991B1B',fontWeight:500}}>
                                ⚡ These are your highest-leverage actions this week, ranked by £ impact.
                              </div>
                            )}
                            {activeTab === 'Actions' && actionPlanActions.length > 0 && (
                              <div style={{border:'1px solid #C4B5FD',borderRadius:8,padding:12,marginBottom:16,background:'#FAF5FF'}}>
                                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                                  <div style={{fontWeight:600,fontSize:13}}>⚡ One-click actions</div>
                                  <span style={{fontSize:11,color:COLORS.muted,marginLeft:'auto'}}>Each opens a confirmation · no bulk apply</span>
                                </div>
                                {actionPlanActions.map(a => {
                                  const p = a.params || {};
                                  const applied = a.status === 'applied';
                                  const dismissed = a.status === 'dismissed';
                                  const isRed = a.action_type === 'pause_keyword' || a.action_type === 'add_negative' || (a.action_type === 'update_budget' && p.current_daily_budget != null && Number(p.new_daily_budget) < Number(p.current_daily_budget));
                                  const label = ({ add_keyword:'Add keyword', pause_keyword:'Pause keyword', add_negative:'Add negative', raise_tcpa:'Raise tCPA', reactivate_keyword:'Reactivate', update_final_url:'Repoint URL', update_budget:'Budget', update_keyword_bid:'Set bid' })[a.action_type] || a.action_type;
                                  const summary = a.action_type === 'raise_tcpa' ? `${shortenCampaign(p.campaign)} → £${p.value}`
                                    : a.action_type === 'update_budget' ? `${shortenCampaign(p.campaign)} → £${p.new_daily_budget}/day${p.current_daily_budget != null ? ` (from £${p.current_daily_budget})` : ''}`
                                    : a.action_type === 'update_final_url' ? `${shortenCampaign(p.campaign)}${p.ad_group ? ' / ' + p.ad_group : ''} → ${p.new_url || '(URL missing)'}`
                                    : a.action_type === 'update_keyword_bid' ? `"${p.keyword}" → £${p.new_bid_gbp} bid · ${shortenCampaign(p.campaign)}${p.ad_group ? ' / ' + p.ad_group : ''}`
                                    : `"${p.keyword}" · ${shortenCampaign(p.campaign)}${p.ad_group ? ' / ' + p.ad_group : ''}`;
                                  const hasNums = a.spend != null && a.clicks != null && a.conv != null && a.cpa != null;
                                  return (
                                    <div key={a.id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',borderRadius:6,marginBottom:6,background:'#fff',border:'1px solid #E5E7EB',opacity:(applied||dismissed)?0.6:1}}>
                                      <span style={{fontSize:10,fontWeight:700,color:'#fff',background:isRed?'#DC2626':COLORS.purple,borderRadius:4,padding:'1px 6px',flexShrink:0}}>{label}</span>
                                      <div style={{flex:1,minWidth:0}}>
                                        <div style={{fontSize:13,color:COLORS.text,textDecoration:applied?'line-through':'none',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{summary}</div>
                                        {hasNums && <div style={{fontSize:11,color:COLORS.muted}}>£{Math.round(a.spend)} spend · {a.clicks} clk · {a.conv} conv · £{Math.round(a.cpa)} CPA</div>}
                                      </div>
                                      {applied ? <span style={{fontSize:11,color:COLORS.green,fontWeight:600,flexShrink:0}}>✓ Applied</span>
                                       : dismissed ? <span style={{fontSize:11,color:COLORS.muted,flexShrink:0}}>Dismissed</span>
                                       : (<div style={{display:'flex',gap:6,flexShrink:0}}>
                                           <button onClick={()=>applyAction(a)} style={{padding:'5px 12px',borderRadius:6,border:'none',background:isRed?'#DC2626':COLORS.purple,color:'#fff',cursor:'pointer',fontSize:12,fontWeight:600}}>Apply</button>
                                           <button onClick={()=>dismissAction(a.id)} title="Dismiss" style={{padding:'5px 8px',borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',color:COLORS.muted,cursor:'pointer',fontSize:12}}>✕</button>
                                         </div>)}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {activeTab === "Don't Touch" && (
                              <div style={{background:'#FFFBEB',border:'1px solid #FCD34D',borderRadius:6,padding:'8px 14px',marginBottom:14,fontSize:12,color:'#92400E',fontWeight:500}}>
                                ⛔ These things look like problems but should not be acted on yet.
                              </div>
                            )}
                            {renderLines(sections[activeTab], activeTab)}
                          </div>
                        )}

                        {/* Question tab */}
                        {activeTab === 'Question' && (
                          <div style={{background:'#F5F3FF',border:'1px solid #C4B5FD',borderRadius:8,padding:20}}>
                            <div style={{fontWeight:600,fontSize:14,color:COLORS.purple,marginBottom:10}}>💬 Claude's question {isViewingHistorical?'(from this report)':'for you'}</div>
                            {sections['Question'] && sections['Question'].some(l=>l.trim()) ? (
                              <div style={{fontSize:13,lineHeight:1.7,marginBottom:16,color:COLORS.text}}>{renderLines(sections['Question'],'Question')}</div>
                            ) : reportQuestion ? (
                              <div style={{fontSize:13,lineHeight:1.7,marginBottom:16,color:COLORS.text}}>{reportQuestion}</div>
                            ) : <div style={{fontSize:12,color:COLORS.muted,marginBottom:16}}>No question recorded for this report.</div>}
                            {viewedAnswer ? (
                              <div style={{background:'#fff',border:'1px solid #C4B5FD',borderRadius:6,padding:'10px 12px'}}>
                                <div style={{fontSize:11,color:COLORS.muted,marginBottom:4}}>Your answer{viewedAnswer.answered_at?` · ${dLong(viewedAnswer.answered_at)}`:''}:</div>
                                <div style={{fontSize:13,color:COLORS.text,lineHeight:1.6}}>{viewedAnswer.answer}</div>
                              </div>
                            ) : isViewingHistorical ? (
                              <div style={{fontSize:12,color:COLORS.muted}}>No answer was recorded for this report.</div>
                            ) : actionPlanAnswerSaved ? (
                              <div style={{fontSize:12,color:COLORS.green}}>✓ Answer saved — included in next report</div>
                            ) : (
                              <div style={{display:'flex',gap:8}}>
                                <input value={actionPlanAnswer} onChange={e=>setActionPlanAnswer(e.target.value)} placeholder="Your answer..." style={{flex:1,padding:'8px 10px',borderRadius:6,border:'1px solid #C4B5FD',fontSize:13}} onKeyDown={e=>e.key==='Enter'&&saveActionPlanAnswer()} />
                                <button onClick={saveActionPlanAnswer} disabled={!actionPlanAnswer.trim()} style={{padding:'8px 16px',borderRadius:6,border:'none',background:COLORS.purple,color:'#fff',cursor:'pointer',fontWeight:600,fontSize:13}}>Save</button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Feedback tab */}
                        {activeTab === 'Feedback' && (
                          <div style={{background:'#F9FAFB',border:'1px solid #E5E7EB',borderRadius:8,padding:20}}>
                            <div style={{fontWeight:600,fontSize:14,marginBottom:6}}>✏️ Feedback for Claude</div>
                            <div style={{fontSize:12,color:COLORS.muted,marginBottom:14}}>Tell Claude anything that would improve future reports — corrections, context, things it got wrong, strategic changes, upcoming plans. This gets stored and included in every future report.</div>
                            {apFeedbackSaved && <div style={{fontSize:12,color:COLORS.green,marginBottom:10}}>✓ Feedback saved — Claude will use this in future reports</div>}
                            <textarea value={apFeedback} onChange={e=>setApFeedback(e.target.value)} placeholder="e.g. The propranolol recommendation was wrong — we paused it and conversions dropped, so we re-enabled it. The Back-up campaign is being wound down next month. MH landing page redesign launching 20 June..." style={{width:'100%',padding:'10px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:13,minHeight:120,resize:'vertical',boxSizing:'border-box',marginBottom:10}} />
                            <button onClick={saveApFeedback} disabled={!apFeedback.trim()} style={{padding:'8px 20px',borderRadius:6,border:'none',background:'#374151',color:'#fff',cursor:'pointer',fontWeight:600,fontSize:13,opacity:apFeedback.trim()?1:0.5}}>Save feedback</button>
                          </div>
                        )}
                      </div>;
                    })()}
                  </div>
                  );
                })()}
              </div>
            )}

            {/* tCPA modal */}
            {/* Approval-gated action modal (reusable) */}
            {confirmModal&&<ConfirmActionModal {...confirmModal} onClose={()=>setConfirmModal(null)} />}

            {tcpaModal&&(<div onClick={()=>setTcpaModal(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
              <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:12,padding:24,maxWidth:420,width:'90%'}}>
                <div style={{fontSize:16,fontWeight:600,marginBottom:4}}>⚙️ Adjust target CPA</div>
                <div style={{fontSize:13,color:COLORS.muted,marginBottom:16}}>{tcpaModal.campaign.replace('Back-up fixed terms campaign','Back-up').replace('Mental health - £59','MH £59')} · current £{Math.round(tcpaModal.current)}</div>
                <div style={{background:'#FEF2F2',border:'1px solid #FCA5A5',borderRadius:6,padding:'8px 12px',fontSize:12,color:'#991B1B',marginBottom:16}}>⚠️ This changes bidding for the entire campaign, not a single keyword. Raising it increases spend and volume; lowering it reduces both.</div>
                {tcpaResult?(<div>
                  <div style={{fontSize:13,color:tcpaResult.ok?COLORS.green:COLORS.red,marginBottom:16}}>{tcpaResult.ok?'✓ '+tcpaResult.message:'✕ '+tcpaResult.error}</div>
                  <button onClick={()=>setTcpaModal(null)} style={{padding:'8px 16px',borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer'}}>Close</button>
                </div>):(<div>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:16}}>
                    <span style={{fontSize:14}}>New target CPA: £</span>
                    <input type="number" value={tcpaValue} onChange={e=>setTcpaValue(e.target.value)} style={{padding:'6px 10px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:14,width:80}} />
                  </div>
                  <div style={{display:'flex',gap:8}}>
                    <button onClick={async()=>{const r=await authFetch('/api/ads/update-tcpa',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({campaignName:tcpaModal.campaign,newTcpa:tcpaValue})});const d=await r.json();setTcpaResult(d);if(d.ok){await authFetch('/api/changes',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({change_type:'Google Ads',campaign_or_page:tcpaModal.campaign,notes:'Changed target CPA from £'+Math.round(tcpaModal.current)+' to £'+tcpaValue,observation_days:14})});fetchChanges();}}} style={{padding:'8px 16px',borderRadius:6,border:'none',background:COLORS.purple,color:'#fff',cursor:'pointer',fontWeight:600}}>Confirm change</button>
                    <button onClick={()=>setTcpaModal(null)} style={{padding:'8px 16px',borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer'}}>Cancel</button>
                  </div>
                </div>)}
              </div>
            </div>)}

            {/* Add/Negative keyword modal */}
            {kwModal&&(<div onClick={()=>{setKwModal(null);setKwActionResult(null);}} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
              <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:12,padding:24,maxWidth:480,width:'90%'}}>
                <div style={{fontSize:16,fontWeight:600,marginBottom:4}}>{kwModal.mode==='keyword'?'➕ Add as keyword':'➖ Add as negative'}</div>
                <div style={{fontSize:12,color:COLORS.muted,marginBottom:16}}>Campaign: {kwModal.campaign}</div>
                <div style={{background:'#F9FAFB',borderRadius:6,padding:'8px 12px',marginBottom:16,fontSize:13,fontWeight:500}}>"{kwModal.term}"</div>
                {kwModal.mode==='keyword'?(
                  <div style={{marginBottom:16}}>
                    <div style={{fontSize:12,color:COLORS.muted,marginBottom:8}}>Select ad group:</div>
                    <select value={selectedAdGroup} onChange={e=>setSelectedAdGroup(e.target.value)} style={{width:'100%',padding:'6px 8px',borderRadius:6,border:'1px solid #E5E7EB',fontSize:13}}>
                      {adGroups.map(ag=><option key={ag.adGroupId} value={ag.adGroupId}>{ag.adGroupName}</option>)}
                    </select>
                    <div style={{fontSize:12,color:COLORS.muted,marginTop:12,marginBottom:8}}>Match type:</div>
                    <div style={{display:'flex',gap:8}}>
                      {['EXACT','PHRASE','BROAD'].map(mt=>(
                        <button key={mt} onClick={()=>setMatchType(mt)} style={{padding:'6px 12px',borderRadius:6,border:'1px solid #E5E7EB',background:matchType===mt?COLORS.purple:'#fff',color:matchType===mt?'#fff':COLORS.text,cursor:'pointer',fontSize:12,fontWeight:matchType===mt?600:400}}>{mt}</button>
                      ))}
                    </div>
                    {readiness&&(
                      <div style={{marginTop:12,fontSize:12,padding:'8px 10px',borderRadius:6,background:readiness.status==='ready'?'#F0FDF4':readiness.status==='caution'?'#FFFBEB':'#FEF2F2',border:'1px solid '+(readiness.status==='ready'?'#86EFAC':readiness.status==='caution'?'#FCD34D':'#FCA5A5'),color:readiness.status==='ready'?'#065F46':readiness.status==='caution'?'#92400E':'#991B1B'}}>
                        <div style={{fontWeight:600}}>{readiness.status==='ready'?'✅ Campaign ready for new keywords':readiness.status==='caution'?'⚠️ Add with caution':'⛔ Campaign not ready — adding keywords now may disrupt optimisation'}</div>
                        {(readiness.warnings||[]).map((w,wi)=>(<div key={wi} style={{fontSize:11,marginTop:3}}>· {w}</div>))}
                      </div>
                    )}
                  </div>
                ):(
                  <div style={{fontSize:12,color:COLORS.muted,marginBottom:16}}>Will be added as an exact match negative keyword at campaign level.</div>
                )}
                {kwActionResult&&<div style={{fontSize:13,color:kwActionResult.ok?COLORS.green:COLORS.red,marginBottom:12}}>{kwActionResult.ok?'✓ '+kwActionResult.message:'✕ '+kwActionResult.error}</div>}
                <div style={{display:'flex',gap:8}}>
                  <button onClick={confirmKwAction} style={{flex:1,padding:'8px 0',borderRadius:6,background:kwModal.mode==='keyword'?COLORS.green:COLORS.red,color:'#fff',border:'none',cursor:'pointer',fontSize:13,fontWeight:600}}>{kwModal.mode==='keyword'?'Add keyword':'Add negative'}</button>
                  <button onClick={()=>{setKwModal(null);setKwActionResult(null);}} style={{padding:'8px 16px',borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer'}}>Cancel</button>
                </div>
              </div>
            </div>)}

          </div>
        )}
        </>
      )}
    </div>
  );
}
